<template>
    <BaseTemplate app-name="" page-title="">
        <template #content>
            <!-- Banner de Inatividade - Canto Inferior Direito -->
            <div v-if="showInactivityBanner" class="fixed bottom-4 right-4 z-[9999] bg-yellow-100 dark:bg-yellow-800 border border-yellow-300 dark:border-yellow-600 rounded-lg shadow-xl p-4 max-w-sm w-full transform transition-all duration-300">
                <div class="flex items-center justify-between">
                    <div class="flex items-center space-x-3">
                        <div class="flex-shrink-0">
                            <svg class="h-6 w-6 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                            </svg>
                        </div>
                        <div>
                            <h3 class="text-sm font-semibold text-yellow-800 dark:text-yellow-200">Modo Inativo</h3>
                            <p class="text-xs text-yellow-700 dark:text-yellow-300">
                                Atualização em <span class="font-bold text-yellow-600 dark:text-yellow-400">{{ autoRefreshCountdown }}s</span>
                            </p>
                        </div>
                    </div>
                    <div class="flex items-center space-x-2">
                        <div class="flex-shrink-0">
                            <div class="w-8 h-8 rounded-full bg-yellow-100 dark:bg-yellow-800 flex items-center justify-center">
                                <span class="text-sm font-bold text-yellow-800 dark:text-yellow-200">{{ autoRefreshCountdown }}</span>
                            </div>
                        </div>
                        <button @click="stopAutoRefresh" class="text-yellow-400 hover:text-yellow-600 dark:hover:text-yellow-300 transition-colors duration-200">
                            <svg class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Indicador de Atualização Automática - Canto Inferior Direito -->
            <div v-if="isAutoRefreshing && !showInactivityBanner" class="fixed bottom-4 right-4 z-[9999] bg-blue-100 dark:bg-blue-800 border border-blue-300 dark:border-blue-600 rounded-lg shadow-lg p-3 max-w-xs">
                <div class="flex items-center space-x-3">
                    <div class="flex-shrink-0">
                        <svg class="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                    </div>
                    <div>
                        <p class="text-sm font-medium text-blue-800 dark:text-blue-200">Atualizando automaticamente</p>
                        <p class="text-xs text-blue-600 dark:text-blue-300">Próxima atualização em {{ nextRefreshCountdown }}s</p>
                    </div>
                </div>
            </div>

            <!-- Indicador de Atualização Automática -->
            <div v-if="isAutoRefreshing && !showInactivityBanner" class="fixed top-4 right-4 z-50 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg shadow-lg p-3">
                <div class="flex items-center space-x-2">
                    <svg class="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span class="text-sm text-blue-800 dark:text-blue-200">Atualizando automaticamente...</span>
                </div>
            </div>

            <div class="min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
                <div class="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
                    <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div class="flex items-center gap-2">
                            <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Agenda:</label>
                            <select v-model.number="selectedAgenda" class="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                                <option v-for="agenda in agendas" :key="agenda.id" :value="agenda.id">{{ agenda.name }}</option>
                            </select>
                            <button @click="openSearchModal()" class="px-3 py-2 clinic-btn text-white rounded-lg transition-colors flex items-center gap-2"><SearchIcon class="w-4 h-4" /><span>Pesquisar agendamento</span></button>
                        </div>
                        <div class="flex items-center gap-2">
                            <input v-model="selectedDate" type="date" class="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                            <button @click="previousDay" class="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors" aria-label="Dia anterior"><ChevronLeftIcon class="w-5 h-5 text-gray-600 dark:text-gray-400" /></button>
                            <button @click="nextDay" class="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors" aria-label="Próximo dia"><ChevronRightIcon class="w-5 h-5 text-gray-600 dark:text-gray-400" /></button>
                            <button @click="goToToday" class="ml-2 px-3 py-2 clinic-btn text-white rounded-lg transition-colors">Hoje</button>
                        </div>
                    </div>
                </div>

                <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
                    <div class="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-col md:flex-row md:items-center md:justify-between">
                        <div>
                            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">{{ formatSelectedDate(selectedDate) }}</h3>
                            <p class="text-sm text-gray-600 dark:text-gray-400">{{ currentAgenda?.name }}</p>
                        </div>
                        <div class="mt-2 md:mt-0 flex items-center gap-2">
                            <button v-if="canAddAppointment && !isPastDay" @click="openCreateModal()" class="px-3 py-2 clinic-btn text-white rounded-lg transition-colors flex items-center gap-2"><PlusIcon class="w-4 h-4" /><span>Novo Agendamento</span></button>
                            <button v-if="canAddSpecialSlot && !isPastDay" @click="openSpecialSlotModal()" class="px-3 py-2 clinic-btn text-white rounded-lg transition-colors flex items-center gap-2"><ClockIcon class="w-4 h-4" /><span>Gerar Horário Especial</span></button>
                            <button v-if="canImportSisreg && !isPastDay" @click="openImportModal()" class="px-3 py-2 clinic-btn text-white rounded-lg transition-colors flex items-center gap-2"><UploadIcon class="w-4 h-4" /><span>Importar Agenda</span></button>
                            <select v-model="filterStatus" class="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                                <option value="all">Todos os status</option>
                                <option value="confirmed">Confirmados</option>
                                <option value="pending">Pendentes</option>
                                <option value="cancelled">Cancelados</option>
                                <option value="completed">Realizados</option>
                                <option value="laudado">Laudados</option>
                                <option value="no_show">Faltas</option>
                                <option value="recalled">Reconvocados</option>
                                <option value="not_performed">Não Realizados</option>
                            </select>
                        </div>
                    </div>

                    <div class="hidden md:grid grid-cols-12 gap-4 p-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 font-medium text-gray-700 dark:text-gray-300">
                        <div class="col-span-1">Horário</div>
                        <div class="col-span-1">OS</div>
                        <div class="col-span-3">Paciente</div>
                        <div class="col-span-3">Exame</div>
                        <div class="col-span-2">Status</div>
                        <div class="col-span-1">Observações</div>
                        <div class="col-span-1 text-right">Ações</div>
                    </div>

                    <div v-if="isLoading" class="text-center py-20">
                         <svg class="animate-spin h-8 w-8 text-blue-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                        <p class="mt-4 text-gray-600 dark:text-gray-400">Carregando agendamentos...</p>
                    </div>
                    
                    <div v-else class="max-h-[calc(100vh-280px)] overflow-y-auto">
                        <div v-if="filteredAppointments.length > 0">
                             <div class="hidden md:block">
                                <div v-for="appointment in filteredAppointments" :key="appointment.id" :class="['grid grid-cols-12 gap-4 p-4 border-b border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors', appointment.status === 'cancelled' ? 'opacity-60 bg-red-50 dark:bg-red-900/20' : '', appointment.status === 'confirmed' ? 'bg-green-50 dark:bg-green-900/20' : '', appointment.status === 'rescheduled' ? 'bg-orange-50 dark:bg-orange-900/20' : '']" @contextmenu.prevent="showAppointmentContextMenu($event, appointment)">
                                    <div class="col-span-1 font-medium text-gray-900 dark:text-white">{{ appointment.time.slice(0, 5) }}</div>
                                    <div class="col-span-1 text-blue-700 dark:text-blue-300 font-mono text-sm">{{ appointment.accession_number || '-' }}</div>
                                    <div class="col-span-3">
                                        <div class="font-medium text-gray-900 dark:text-white">{{ appointment.patient_name }}</div>
                                        <div class="text-sm text-gray-500 dark:text-gray-200">{{ appointment.patient_id }}</div>
                                        <div v-if="appointment.status === 'rescheduled'" class="text-xs text-orange-600 dark:text-orange-400 font-medium">
                                            Reagendado para {{ formatDate(appointment.rescheduled_to?.date) }} {{ appointment.rescheduled_to?.time?.slice(0,5) }}
                                        </div>
                                        <div v-if="appointment.status === 'recalled'" class="text-xs text-purple-600 dark:text-purple-400 font-medium">
                                            {{ getRecallInfo(appointment) }}
                                        </div>
                                    </div>
                                    <div class="col-span-3 flex items-center gap-2">
                                        <component :is="getExamIcon(appointment.service_name)" class="w-4 h-4 text-gray-500 dark:text-gray-200 flex-shrink-0" />
                                        <span class="text-gray-900 dark:text-white">{{ appointment.service_name }}</span>
                                    </div>
                                    <div class="col-span-2">
                                        <span :class="['px-2 py-1 rounded text-xs font-medium', 
                                            appointment.status === 'confirmed' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : '', 
                                            appointment.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' : '', 
                                            appointment.status === 'cancelled' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' : '', 
                                            appointment.status === 'completed' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' : '',
                                            appointment.status === 'rescheduled' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' : '',
                                            appointment.status === 'no_show' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' : '',
                                            appointment.status === 'laudado' ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200' : '',
                                            appointment.status === 'recalled' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' : '',
                                            appointment.status === 'not_performed' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' : '',
                                            appointment.status === 'special_slot' ? 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200' : '',
                                            appointment.status === 'available' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' : '']">{{ getStatusLabel(appointment.status) }}</span>
                                    </div>
                                    <div class="col-span-1 truncate text-sm text-gray-500 dark:text-gray-200">{{ appointment.notes || '-' }}</div>
                                    <div class="col-span-1 flex justify-end items-center gap-1">
                                        <button @click="selectAppointment(appointment)" class="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors" aria-label="Ver detalhes"><EyeIcon class="w-5 h-5 text-gray-500 dark:text-gray-400" /></button>
                                        <button @click="showAppointmentContextMenu($event, appointment)" class="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors" aria-label="Mais opções"><MoreVerticalIcon class="w-5 h-5 text-gray-500 dark:text-gray-400" /></button>
                                    </div>
                                </div>
                            </div>
                             <div class="md:hidden">
                                 <div v-for="appointment in filteredAppointments" :key="appointment.id" :class="['p-4 border-b border-gray-200 dark:border-gray-600', appointment.status === 'cancelled' ? 'opacity-60' : '']" @click="selectAppointment(appointment)">
                                    <div class="flex justify-between items-start mb-2">
                                        <div class="font-medium text-gray-900 dark:text-white">
                                            {{ appointment.time.slice(0,5) }}
                                            <span v-if="appointment.accession_number" class="ml-2 text-blue-700 dark:text-blue-300 font-mono text-xs">({{ appointment.accession_number }})</span>
                                            - {{ appointment.patient_name }}
                                        </div>
                                        <span :class="['px-2 py-1 rounded text-xs font-medium', appointment.status === 'confirmed' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : '', appointment.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' : '', appointment.status === 'cancelled' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' : '', appointment.status === 'completed' ? 'bg-emerald-100 text-blue-800 dark:bg-emerald-900 dark:text-blue-200' : '', appointment.status === 'no_show' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' : '', appointment.status === 'laudado' ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200' : '', appointment.status === 'recalled' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' : '', appointment.status === 'not_performed' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' : '', appointment.status === 'special_slot' ? 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200' : '', appointment.status === 'available' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' : '']">{{ getStatusLabel(appointment.status) }}</span>
                                    </div>
                                    <div v-if="appointment.accession_number" class="text-xs text-blue-600 dark:text-blue-400 font-medium mb-1">
                                        Accession: {{ appointment.accession_number }}
                                    </div>
                                    <div class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300"><component :is="getExamIcon(appointment.service_name)" class="w-4 h-4 text-gray-500 dark:text-gray-400" /><span>{{ appointment.service_name }}</span></div>
                                    <div v-if="appointment.notes" class="mt-1 text-xs text-gray-500 dark:text-gray-400">{{ appointment.notes }}</div>
                                </div>
                            </div>
                        </div>
                        <div v-else class="text-center py-12">
                             <CalendarIcon class="w-12 h-12 mx-auto text-gray-400 dark:text-gray-600" />
                            <h3 class="mt-4 text-lg font-medium text-gray-900 dark:text-white">Nenhum agendamento encontrado</h3>
                            <p class="mt-2 text-gray-500 dark:text-gray-400">Não há agendamentos para esta data ou filtro selecionado.</p>
                            <button v-if="canAddAppointment && !isPastDay" @click="openCreateModal()" class="mt-4 px-4 py-2 clinic-btn text-white rounded-lg transition-colors">Adicionar Agendamento</button>
                        </div>
                    </div>
                </div>

                <div v-if="contextMenu.show" :style="{ top: contextMenu.y + 'px', left: contextMenu.x + 'px' }" class="fixed z-50 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 py-2 min-w-48 max-h-[80vh] overflow-y-auto">
                    <button v-for="action in filteredContextMenuActions" :key="action.label" @click="executeAction(action)" class="w-full px-3 py-1.5 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-700 dark:text-gray-300 text-xs">
                        <component :is="action.icon" class="w-3 h-3" />{{ action.label }}
                    </button>
                    <!-- Botões de impressão - ocultos para horários especiais disponíveis e status pendente -->
                    <template v-if="!(contextMenu.data?.status === 'available' && contextMenu.data?.special_slot_id) && contextMenu.data?.status !== 'pending'">
                        <button @click="async () => { await printPatientSheet(contextMenu.data); hideContextMenu(); }" class="w-full px-3 py-1.5 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-700 dark:text-gray-300 text-xs">
                            <Print class="w-3 h-3" />Imprimir ficha do paciente
                        </button>
                        <button @click="async () => { await printPreparation(contextMenu.data); hideContextMenu(); }" class="w-full px-3 py-1.5 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-700 dark:text-gray-300 text-xs">
                            <Print class="w-3 h-3" />Imprimir preparo
                        </button>
                        <button @click="async () => { await printAttendanceDeclaration(contextMenu.data); hideContextMenu(); }" class="w-full px-3 py-1.5 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-700 dark:text-gray-300 text-xs">
                            <Print class="w-3 h-3" />Imprimir declaração de comparecimento
                        </button>
                    </template>

                </div>

                <div v-if="selectedAppointment" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40 p-4" @click="selectedAppointment = null">
                    <div class="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full max-h-[80vh] overflow-y-auto" @click.stop>
                        <div class="flex justify-between items-start mb-4">
                            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Detalhes do Agendamento</h3>
                            <button @click="selectedAppointment = null" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"><XIcon class="w-5 h-5" /></button>
                        </div>
                        <div class="space-y-4">
                            <div class="flex justify-between items-center"><div class="text-sm font-medium text-gray-600 dark:text-gray-400">Data e Hora</div><div class="text-gray-900 dark:text-white font-medium">{{ formatDate(selectedAppointment.date) }} às {{ selectedAppointment.time.slice(0,5) }}</div></div>
                            <div class="border-t border-gray-200 dark:border-gray-700 pt-4">
                                <div class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Paciente</div>
                                <div class="text-gray-900 dark:text-white font-medium">{{ selectedAppointment.patient_name }}</div>
                                <div class="text-sm text-gray-500 dark:text-gray-400">{{ selectedAppointment.patient_id }}</div>
                                <div v-if="selectedAppointment.accession_number" class="text-sm text-blue-600 dark:text-blue-400 font-medium mt-1">
                                    Accession: {{ selectedAppointment.accession_number }}
                                </div>
                                <div v-if="selectedAppointment.phone" class="text-sm text-gray-500 dark:text-gray-400 mt-1">Tel: {{ selectedAppointment.phone }}</div>
                            </div>
                            <div class="border-t border-gray-200 dark:border-gray-700 pt-4"><div class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Exame</div><div class="flex items-center gap-2"><component :is="getExamIcon(selectedAppointment.service_name)" class="w-5 h-5 text-gray-500 dark:text-gray-400" /><span class="text-gray-900 dark:text-white">{{ selectedAppointment.service_name }}</span></div></div>
                            <div class="border-t border-gray-200 dark:border-gray-700 pt-4"><div class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Status</div><span :class="['px-2 py-1 rounded text-xs font-medium', selectedAppointment.status === 'confirmed' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : '', selectedAppointment.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' : '', selectedAppointment.status === 'cancelled' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' : '', selectedAppointment.status === 'completed' ? 'bg-emerald-100 text-blue-800 dark:bg-emerald-900 dark:text-blue-200' : '', selectedAppointment.status === 'no_show' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' : '', selectedAppointment.status === 'laudado' ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200' : '', selectedAppointment.status === 'recalled' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' : '', selectedAppointment.status === 'not_performed' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' : '', selectedAppointment.status === 'special_slot' ? 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200' : '', selectedAppointment.status === 'available' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' : '']">{{ getStatusLabel(selectedAppointment.status) }}</span></div>
                            
                            <!-- Chave de Confirmação SISREG -->
                            <div v-if="selectedAppointment.sisreg_confirmation_key" class="border-t border-gray-200 dark:border-gray-700 pt-4">
                                <div class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Chave de Confirmação SISREG</div>
                                <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                                    <div class="font-mono text-blue-900 dark:text-blue-100 text-sm">{{ selectedAppointment.sisreg_confirmation_key }}</div>
                                </div>
                            </div>
                            
                            <div v-if="selectedAppointment.notes" class="border-t border-gray-200 dark:border-gray-700 pt-4"><div class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Observações</div><p class="text-gray-900 dark:text-white">{{ selectedAppointment.notes }}</p></div>
                            
                            <!-- Motivo da Não Realização -->
                            <div v-if="selectedAppointment.not_performed_reason" class="border-t border-gray-200 dark:border-gray-700 pt-4">
                                <div class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Motivo da Não Realização</div>
                                <div class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                                    <p class="text-red-900 dark:text-red-100">{{ selectedAppointment.not_performed_reason }}</p>
                                    <div class="flex flex-col gap-1 mt-2">
                                        <p v-if="selectedAppointment.not_performed_at" class="text-xs text-red-600 dark:text-red-300">
                                            Registrado em {{ formatDateTime(selectedAppointment.not_performed_at) }}
                                        </p>
                                        <p v-if="selectedAppointment.not_performed_by" class="text-xs text-red-600 dark:text-red-300">
                                            Por: {{ selectedAppointment.not_performed_by }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            
                            <!-- Status de Worklist e Anamnese -->
                            <div v-if="selectedAppointment.worklist_generated" class="border-t border-gray-200 dark:border-gray-700 pt-4">
                                <div class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Worklist</div>
                                <div class="flex items-center gap-2">
                                    <CheckIcon class="w-4 h-4 text-green-500" />
                                    <span class="text-sm text-green-600 dark:text-green-400">Gerada em {{ formatDateTime(selectedAppointment.worklist_generated_at) }}</span>
                                </div>
                            </div>
                            
                            <div v-if="selectedAppointment.anamnesis_completed" class="border-t border-gray-200 dark:border-gray-700 pt-4">
                                <div class="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Anamnese</div>
                                <div class="flex items-center gap-2">
                                    <CheckIcon class="w-4 h-4 text-green-500" />
                                    <span class="text-sm text-green-600 dark:text-green-400">Finalizada em {{ formatDateTime(selectedAppointment.anamnesis_completed_at) }}</span>
                                </div>
                            </div>
                            
                            <div class="border-t border-gray-200 dark:border-gray-700 pt-4 flex justify-end gap-2">
                                <button v-if="selectedAppointment.status === 'pending'" @click="openConfirmationModal(selectedAppointment)" class="px-3 py-2 bg-green-600 text-white hover:bg-green-700 rounded-lg text-sm font-medium">Confirmar</button>
                                <button v-if="selectedAppointment.status === 'pending'" @click="updateAppointmentStatus(selectedAppointment, 'cancelled')" class="px-3 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg text-sm font-medium">Cancelar</button>
                                <button v-if="selectedAppointment.status === 'confirmed' || selectedAppointment.status === 'no_show'" @click="openRescheduleModal(selectedAppointment)" class="px-3 py-2 bg-orange-500 text-white hover:bg-orange-600 rounded-lg text-sm font-medium">Reagendar</button>
                                <button @click="selectedAppointment = null" class="px-3 py-2 bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-white rounded-lg text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-600">Fechar</button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Modal de Confirmação de Agendamento -->
                <div v-if="isConfirmationModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div class="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col" @click.stop>
                        <div class="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
                            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Confirmar Agendamento</h3>
                            <button @click="closeConfirmationModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"><XIcon class="w-5 h-5" /></button>
                        </div>
                        <form @submit.prevent="handleConfirmAppointment" class="p-6 space-y-4 overflow-y-auto">
                            <div v-if="confirmationError" class="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded text-red-700 dark:text-red-300 text-sm">
                                {{ confirmationError }}
                            </div>
                            <!-- Informações do Agendamento -->
                            <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
                                <h4 class="font-medium text-gray-900 dark:text-white mb-2">Dados do Agendamento</h4>
                                <div class="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span class="text-gray-800 dark:text-gray-400">Paciente:</span> 
                                        <span class="text-gray-900 ml-2 dark:text-gray-100">{{ confirmationAppointment?.patient_name }}</span>
                                    </div>
                                    <div>
                                        <span class="text-gray-800 dark:text-gray-400">CPF/CNS:</span>
                                        <span class="text-gray-900 ml-2 dark:text-gray-100">{{ confirmationAppointment?.patient_cpf_cns || 'Não informado' }}</span>
                                    </div>
                                    <div v-if="confirmationAppointment?.patient_race">
                                        <span class="text-gray-800 dark:text-gray-400">Raça:</span>
                                        <span class="text-gray-900 ml-2 dark:text-gray-100">{{ getRaceLabel(confirmationAppointment.patient_race) }}</span>
                                    </div>
                                    <div v-if="confirmationAppointment?.accession_number">
                                        <span class="text-gray-800 dark:text-gray-400">Accession:</span>
                                        <span class="text-blue-600 ml-2 dark:text-blue-400 font-medium">{{ confirmationAppointment.accession_number }}</span>
                                    </div>
                                    <div>
                                        <span class="text-gray-800 dark:text-gray-400">Exame:</span>
                                        <span class="text-gray-900 ml-2 dark:text-gray-100">{{ confirmationAppointment?.service_name }}</span>
                                    </div>
                                    <div>
                                        <span class="text-gray-800 dark:text-gray-400">Data:</span>
                                        <span class="text-gray-900 ml-2 dark:text-gray-100">{{ formatDate(confirmationAppointment?.date) }}</span>
                                    </div>
                                    <div>
                                        <span class="text-gray-800 dark:text-gray-400">Horário:</span>
                                        <span class="text-gray-900 ml-2 dark:text-gray-100">{{ confirmationAppointment?.time?.slice(0,5) }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Campos Obrigatórios -->
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Chave de Confirmação SISREG*</label>
                                    <input v-model="confirmationData.sisreg_confirmation_key" type="text" required placeholder="Ex: 123456789" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Médico*</label>
                                    <select v-model="confirmationData.doctor_name" required class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500">
                                        <option value="">Selecione um médico</option>
                                        <option v-for="doctor in doctors" :key="doctor.id" :value="doctor.name">{{ doctor.name }}</option>
                                    </select>
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Número da Guia*</label>
                                    <input v-model="confirmationData.guide_number" type="text" required placeholder="Ex: 987654321" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Data de Emissão da Guia*</label>
                                    <input v-model="confirmationData.guide_issue_date" type="date" required class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Data da Autorização*</label>
                                    <input v-model="confirmationData.authorization_date" type="date" required class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CID 10*</label>
                                    <input v-model="confirmationData.cid10" type="text" required class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Raça*</label>
                                    <select v-model="confirmationData.race" required class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500">
                                        <option value="">Selecione</option>
                                        <option value="01">Branco</option>
                                        <option value="02">Preto</option>
                                        <option value="03">Pardo</option>
                                        <option value="04">Amarelo</option>
                                        <option value="05">Indígena</option>
                                    </select>
                                </div>
                                <div class="md:col-span-2">
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Procedência*</label>
                                    <select v-model="confirmationData.procedencia" required class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500">
                                        <option value="">Selecione</option>
                                        <option value="Ambulatório">Ambulatório</option>
                                        <option value="Emergência">Emergência</option>
                                        <option value="Internação">Internação</option>
                                        <option value="Externo">Externo</option>
                                        <option value="Outro">Outro</option>
                                    </select>
                                </div>
                            </div>

                            <!-- Aviso sobre Anamnese -->
                            <div v-if="hasRequiredAnamnesis" class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                                <div class="flex items-start">
                                    <div class="flex-shrink-0">
                                        <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                                        </svg>
                                    </div>
                                    <div class="ml-3">
                                        <h3 class="text-sm font-medium text-yellow-800 dark:text-yellow-200">Anamnese Obrigatória</h3>
                                        <div class="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                                            <p>Este serviço possui anamnese obrigatória que deve ser finalizada antes da confirmação do agendamento.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>
                        
                        <div class="p-4 mt-auto border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                            <button type="button" @click="closeConfirmationModal" class="px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-white rounded-lg text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-500">Cancelar</button>
                            <button type="button" @click="handleConfirmAppointment" :disabled="isConfirming || !isConfirmationFormValid" class="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                                <template v-if="isConfirming">
                                    <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span>Confirmando...</span>
                                </template>
                                <template v-else>
                                    <CheckIcon class="w-4 h-4" />
                                    <span>Confirmar Agendamento</span>
                                </template>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Modal de Confirmação de Exclusão -->
                <div v-if="isDeleteConfirmationModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div class="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full" @click.stop>
                        <div class="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
                            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Confirmar Exclusão</h3>
                            <button @click="closeDeleteConfirmationModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"><XIcon class="w-5 h-5" /></button>
                        </div>
                        
                        <div class="p-6">
                            <div class="flex items-start">
                                <div class="flex-shrink-0">
                                    <svg class="h-6 w-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                </div>
                                <div class="ml-3">
                                    <h3 class="text-sm font-medium text-gray-900 dark:text-white">Excluir Agendamento</h3>
                                    <div class="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                        <p>Tem certeza que deseja excluir o agendamento do paciente <strong>{{ deleteConfirmationAppointment?.patient_name }}</strong>?</p>
                                        <div class="mt-3 bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                                            <div class="text-xs space-y-1">
                                                <div><span class="font-medium">Data:</span> {{ formatDate(deleteConfirmationAppointment?.date) }}</div>
                                                <div><span class="font-medium">Horário:</span> {{ deleteConfirmationAppointment?.time?.slice(0,5) }}</div>
                                                <div><span class="font-medium">Exame:</span> {{ deleteConfirmationAppointment?.service_name }}</div>
                                            </div>
                                        </div>
                                        <p class="mt-3 text-red-600 dark:text-red-400 font-medium">Esta ação não pode ser desfeita.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div class="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                            <button type="button" @click="closeDeleteConfirmationModal" class="px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-white rounded-lg text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-500">Cancelar</button>
                            <button type="button" @click="deleteAppointment(deleteConfirmationAppointment.id)" class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium flex items-center gap-2">
                                <TrashIcon class="w-4 h-4" />
                                Excluir Agendamento
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Modal de Confirmação de Exclusão de Horário Especial -->
                <div v-if="isDeleteSpecialSlotModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div class="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full" @click.stop>
                        <div class="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
                            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Confirmar Exclusão</h3>
                            <button @click="closeDeleteSpecialSlotModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"><XIcon class="w-5 h-5" /></button>
                        </div>
                        
                        <div class="p-6">
                            <div class="flex items-start">
                                <div class="flex-shrink-0">
                                    <svg class="h-6 w-6 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                </div>
                                <div class="ml-3">
                                    <h3 class="text-sm font-medium text-gray-900 dark:text-white">Excluir Horário Especial</h3>
                                    <div class="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                        <p>Tem certeza que deseja excluir este horário especial?</p>
                                        <div class="mt-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                                            <div class="text-xs space-y-1">
                                                <div><span class="font-medium">Data:</span> {{ formatDate(deleteSpecialSlotData?.date) }}</div>
                                                <div><span class="font-medium">Horário:</span> {{ deleteSpecialSlotData?.time?.slice(0,5) }}</div>
                                                <div><span class="font-medium">Status:</span> 
                                                    <span class="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                                        {{ getStatusLabel(deleteSpecialSlotData?.status) }}
                                                    </span>
                                                </div>
                                                <div v-if="deleteSpecialSlotData?.notes"><span class="font-medium">Observações:</span> {{ deleteSpecialSlotData.notes }}</div>
                                            </div>
                                        </div>
                                        <p class="mt-3 text-orange-600 dark:text-orange-400 font-medium">Esta ação não pode ser desfeita.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div class="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                            <button type="button" @click="closeDeleteSpecialSlotModal" class="px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-white rounded-lg text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-500">Cancelar</button>
                            <button type="button" @click="deleteSpecialSlot(deleteSpecialSlotData.special_slot_id)" class="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-medium flex items-center gap-2">
                                <TrashIcon class="w-4 h-4" />
                                Excluir Horário Especial
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Modal de Anamnese -->
                <div v-if="isAnamnesisModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div class="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col" @click.stop>
                        <div class="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
                            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Anamnese Obrigatória</h3>
                            <button @click="closeAnamnesisModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"><XIcon class="w-5 h-5" /></button>
                        </div>
                        
                        <div class="p-6 overflow-y-auto flex-1">
                            <div v-if="requiredAnamneses.length > 0" class="space-y-4">
                                <div v-for="anamnesis in requiredAnamneses" :key="anamnesis.id" class="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                    <h4 class="font-medium text-gray-900 dark:text-white mb-2">{{ anamnesis.titulo }}</h4>
                                    <p class="text-sm text-gray-600 dark:text-gray-400 mb-3">{{ anamnesis.descricao }}</p>
                                    <div class="flex justify-between items-center">
                                        <span class="text-xs text-gray-500 dark:text-gray-500">{{ anamnesis.questions_count }} pergunta(s)</span>
                                        <button @click="openAnamnesisForm(anamnesis)" class="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-sm">Preencher Anamnese</button>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="text-center py-8 text-gray-500 dark:text-gray-400">
                                <p>Nenhuma anamnese obrigatória encontrada para este serviço.</p>
                            </div>
                        </div>
                        
                        <div class="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                            <button type="button" @click="closeAnamnesisModal" class="px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-white rounded-lg text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-500">Fechar</button>
                        </div>
                    </div>
                </div>

                <div v-if="isCreateModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40 p-4">
                    <div class="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col" @click.stop>
                        <div class="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
                            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
                                {{ isCreatingFromSpecialSlot ? 'Adicionar Agendamento ao Horário Especial' : 'Novo Agendamento' }} para {{ formatSelectedDate(selectedDate) }}
                            </h3>
                            <button @click="closeCreateModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"><XIcon class="w-5 h-5" /></button>
                        </div>
                        
                        <form @submit.prevent="handleSaveAppointment" class="p-6 space-y-4 overflow-y-auto">
                            <div class="grid grid-cols-1 md:grid-cols-1 gap-4">
                                <div>
                                    <div class="flex items-center justify-between mb-1">
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Paciente*</label>
                                        <button 
                                            type="button"
                                            @click="openCreatePatientModal"
                                            class="inline-flex items-center gap-1 text-xs px-3 py-1.5 clinic-btn text-white rounded-lg transition-all duration-200 shadow-sm hover:shadow-md font-medium"
                                            placeholder="Cadastrar Paciente"
                                        >
                                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                                            </svg>
                                            
                                        </button>
                                    </div>
                                    
                                    <!-- Campo de pesquisa de pacientes -->
                                    <div class="mb-2 relative">
                                        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                                            </svg>
                                        </div>
                                        <input
                                            v-model="patientSearchTerm"
                                            @input="debouncedPatientSearch"
                                            @keyup.enter="performImmediateSearch"
                                            type="text"
                                            placeholder="Pesquisar paciente por nome, CPF, CNS, GEN-000123, telefone..."
                                            class="block w-full pl-10 pr-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    
                                    <select
                                        v-model.number="newAppointment.patient_id"
                                        required
                                        :disabled="!patientSearchTerm.trim()"
                                        :class="[
                                            'mt-1 block w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                                            !patientSearchTerm.trim()
                                                ? 'bg-gray-100 dark:bg-gray-600 border-gray-300 dark:border-gray-500 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                                                : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white',
                                            blinkingFields.has('patient_id') ? 'blink-border' : ''
                                        ]"
                                        >
                                        <option :value="null" disabled>
                                            {{ !patientSearchTerm.trim() ? 'Digite para pesquisar pacientes' : 'Selecione' }}
                                        </option>
                                        <option v-for="u in users" :key="u.id" :value="u.id">
                                            {{ u.label }} - {{ u.document }} - {{ u.birthdate }}
                                        </option>
                                    </select>
                                    
                                    <!-- Indicador de carregamento e contagem de resultados -->
                                    <div v-if="isLoadingPatients" class="mt-1 text-xs text-blue-600 dark:text-blue-400">
                                        Pesquisando...
                                    </div>
                                    <div v-else-if="users.length > 0" class="mt-1 text-xs text-gray-600 dark:text-gray-400">
                                        {{ users.length }} paciente(s) encontrado(s)
                                    </div>
                                    <div v-else-if="patientSearchTerm && !isLoadingPatients" class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                        Nenhum paciente encontrado
                                    </div>
                                    <div v-else-if="!patientSearchTerm && !isLoadingPatients" class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                        Digite no campo acima para pesquisar e selecionar pacientes
                                    </div>
                                </div>
                            </div>
                            
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Serviço*</label>
                                    <select 
                                        v-model="newAppointment.service_id" 
                                        required 
                                        @change="onServiceChange" 
                                        :disabled="filteredServices.length === 0"
                                        :class="[
                                            'mt-1 block w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                                            filteredServices.length === 0 
                                                ? 'bg-gray-100 dark:bg-gray-600 border-gray-300 dark:border-gray-500 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                                                : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white',
                                            blinkingFields.has('service_id') ? 'blink-border' : ''
                                        ]">
                                        <option :value="null" disabled>
                                            {{ filteredServices.length === 0 ? 'Nenhum serviço disponível para esta agenda' : 'Selecione um serviço' }}
                                        </option>
                                        <option v-for="s in filteredServices" :key="s.id" :value="s.id">{{ s.label }}</option>
                                    </select>
                                    <div v-if="filteredServices.length > 0" class="mt-1 text-xs text-green-600 dark:text-green-400">
                                        {{ filteredServices.length }} serviço(s) disponível(is) para esta agenda
                                    </div>
                                    <div v-else-if="currentScheduleServices.length === 0 && selectedAgendaId" class="mt-1 text-xs text-red-600 dark:text-red-400">
                                        ⚠️ Esta agenda não possui serviços vinculados. Vincule serviços antes de agendar.
                                    </div>
                                    <div v-else-if="!selectedAgendaId" class="mt-1 text-xs text-gray-600 dark:text-gray-400">
                                        Selecione uma agenda primeiro
                                    </div>
                                    <div v-else class="mt-1 text-xs text-yellow-600 dark:text-yellow-400">
                                        Carregando serviços da agenda...
                                    </div>
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Horário*</label>
                                    <select 
                                        v-model="newAppointment.time" 
                                        required 
                                        :disabled="isCreatingFromSpecialSlot || !newAppointment.service_id"
                                        :class="[
                                            'mt-1 block w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                                            isCreatingFromSpecialSlot || !newAppointment.service_id
                                                ? 'bg-gray-100 dark:bg-gray-600 border-gray-300 dark:border-gray-500 text-gray-500 dark:text-gray-400 cursor-not-allowed' 
                                                : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white',
                                            blinkingFields.has('time') ? 'blink-border' : ''
                                        ]"
                                    >
                                        <option value="" disabled>
                                            {{ !newAppointment.service_id ? 'Selecione um serviço primeiro' : 'Selecione' }}
                                        </option>
                                        <option v-for="t in availableTimes" :key="t" :value="t">{{ t }}</option>
                                    </select>
                                    <p v-if="isCreatingFromSpecialSlot" class="mt-1 text-xs text-blue-600 dark:text-blue-400">
                                        Horário fixo do horário especial
                                    </p>
                                    <p v-else-if="!newAppointment.service_id" class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                        Selecione um serviço para escolher o horário
                                    </p>
                                </div>
                            </div>
                            
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div v-if="newAppointment.service_id && filteredInsurances.length > 0">
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Plano</label>
                                    <select v-model="newAppointment.insurance_id" class="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                                        <option :value="null">Nenhum</option>
                                        <option v-for="i in filteredInsurances" :key="i.id" :value="i.id">{{ i.label }}</option>
                                    </select>
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Procedência*</label>
                                    <select v-model="newAppointment.procedencia" required :class="[
                                    'mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                                    blinkingFields.has('procedencia') ? 'blink-border' : ''
                                ]">
                                        <option value="" disabled>Selecione</option>
                                        <option v-for="p in procedenciaOptions" :key="p" :value="p">{{ p }}</option>
                                    </select>
                                </div>
                            </div>
                            
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Médico Solicitante</label>
                                    <select v-model="newAppointment.requesting_doctor_id" class="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                                        <option :value="null">Nenhum</option>
                                        <option v-for="d in doctors" :key="d.id" :value="d.id">{{ d.name }}</option>
                                    </select>
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Médico Interno</label>
                                    <select v-model="newAppointment.internal_doctor_id" class="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                                        <option :value="null">Nenhum</option>
                                        <option v-for="d in doctors" :key="d.id" :value="d.id">{{ d.name }}</option>
                                    </select>
                                </div>
                            </div>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Motivo da Marcação*</label>
                                    <select v-model="newAppointment.motivo_marcacao" required :class="[
                                    'mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                                    blinkingFields.has('motivo_marcacao') ? 'blink-border' : ''
                                ]">
                                        <option value="" disabled>Selecione</option>
                                        <option v-for="m in motivoMarcacaoOptions" :key="m" :value="m">{{ m }}</option>
                                    </select>
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">CID 10</label>
                                    <input type="text" v-model="newAppointment.cid10" placeholder="Ex: F41.1" class="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                                </div>
                            </div>

                            <div>
                                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Observações</label>
                                <textarea v-model="newAppointment.notes" rows="3" placeholder="Informações adicionais sobre o agendamento..." class="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"></textarea>
                            </div>
                        </form>
                        
                        <div class="p-4 mt-auto border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                            <button type="button" @click="closeCreateModal" class="px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-600 dark:bg-gray-500 text-white rounded-lg text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-500">Cancelar</button>
                            <button type="button" @click="handleSaveAppointment" class="px-4 py-2 clinic-btn text-white rounded-lg text-sm font-medium">
                                {{ isCreatingFromSpecialSlot ? 'Vincular ao Horário Especial' : 'Salvar Agendamento' }}
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Modal de Cadastro de Paciente -->
                <div v-if="isCreatePatientModalOpen" class="fixed inset-0 bg-black mt-16 bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div class="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col" @click.stop>
                        <div class="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
                            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Cadastrar Novo Paciente</h3>
                            <button @click="closeCreatePatientModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"><XIcon class="w-5 h-5" /></button>
                        </div>
                        
                        <form @submit.prevent="handleCreatePatient" class="p-6 pt-8 space-y-6 overflow-y-auto">
                            <!-- Informações Pessoais -->
                            <div class="border-b border-gray-200 dark:border-gray-700 pb-4">
                                <h4 class="text-md font-medium text-gray-900 dark:text-white mb-4">Informações Pessoais</h4>
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Nome Completo*</label>
                                        <input 
                                            v-model="newPatient.name" 
                                            type="text" 
                                            required 
                                            @input="formatPatientName" 
                                            placeholder="Digite o nome completo do paciente" 
                                            :class="[
                                                'mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500',
                                                blinkingPatientFields.has('name') ? 'blink-border' : ''
                                            ]"
                                        />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Nome Social</label>
                                        <input v-model="newPatient.social_name" type="text" placeholder="Digite o nome social do paciente (caso possua)" class="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Data de Nascimento*</label>
                                        <input 
                                            v-model="newPatient.birthdate" 
                                            type="date" 
                                            required 
                                            :class="[
                                                'mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500',
                                                blinkingPatientFields.has('birthdate') ? 'blink-border' : ''
                                            ]"
                                        />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Gênero*</label>
                                        <select 
                                            v-model="newPatient.gender" 
                                            required 
                                            :class="[
                                                'mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500',
                                                blinkingPatientFields.has('gender') ? 'blink-border' : ''
                                            ]"
                                        >
                                            <option value="">Selecione</option>
                                            <option value="M">Masculino</option>
                                            <option value="F">Feminino</option>
                                            <option value="O">Outro</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Raça/Etnia</label>
                                        <select 
                                            v-model="newPatient.race" 
                                            class="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="">Selecione</option>
                                            <option value="01">Branca</option>
                                        <option value="02">Preta</option>
                                        <option value="03">Parda</option>
                                        <option value="04">Amarela</option>
                                        <option value="05">Indígena</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Tipo de Documento*</label>
                                        <select v-model="newPatient.document_type" required @change="onDocumentTypeChange" class="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500">
                                            <option value="CPF">CPF</option>
                                            <option value="CNS">CNS</option>
                                            <option value="GEN">Genérico</option>
                                        </select>
                                    </div>
                                    <div v-if="newPatient.document_type === 'CPF'">
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">CPF</label>
                                        <input 
                                            v-model="newPatient.cpf" 
                                            type="text" 
                                            placeholder="000.000.000-00"
                                            required
                                            @input="newPatient.cpf = formatarCpf($event.target.value)"
                                            @blur="validateCpfField"
                                            :class="[
                                                'mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500',
                                                blinkingPatientFields.has('cpf') ? 'blink-border' : ''
                                            ]"
                                        />
                                    </div>
                                    <div v-if="newPatient.document_type === 'CNS'">
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">CNS</label>
                                        <input 
                                            v-model="newPatient.cns" 
                                            type="text" 
                                            class="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" 
                                            placeholder="000000000000000"
                                            maxlength="18"
                                            @input="newPatient.cns = formatarCns($event.target.value)"
                                        />
                                    </div>
                                    <div v-if="newPatient.document_type === 'GEN'">
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Identificador Genérico</label>
                                        <input 
                                            type="text" 
                                            disabled
                                            class="mt-1 block w-full px-3 py-2 bg-gray-100 dark:bg-gray-600 border border-gray-300 dark:border-gray-600 rounded-md text-gray-500 dark:text-gray-400 focus:ring-2 focus:ring-blue-500" 
                                            placeholder="Será gerado automaticamente"
                                        />
                                    </div>
                                </div>
                            </div>

                            <!-- Contato -->
                            <div class="border-b border-gray-200 dark:border-gray-700 pb-4">
                                <h4 class="text-md font-medium text-gray-900 dark:text-white mb-4">Contato</h4>
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Telefone*</label>
                                        <input 
                                            v-model="newPatient.phone" 
                                            type="tel" 
                                            required 
                                            placeholder="(00) 00000-0000"
                                            maxlength="15"
                                            @input="newPatient.phone = formatarTelefone($event.target.value)"
                                            @blur="validatePhoneField"
                                            :class="[
                                                'mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500',
                                                blinkingPatientFields.has('phone') ? 'blink-border' : ''
                                            ]"
                                        />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
                                        <input v-model="newPatient.email" type="email" placeholder="exemplo@email.com" class="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Contato de Emergência</label>
                                        <input v-model="newPatient.emergency_contact" type="text" placeholder="Nome e telefone do contato" class="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                                    </div>
                                </div>
                            </div>

                            <!-- Endereço -->
                            <div class="border-b border-gray-200 dark:border-gray-700 pb-4">
                                <h4 class="text-md font-medium text-gray-900 dark:text-white mb-4">Endereço</h4>
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">CEP</label>
                                        <div class="flex gap-2">
                                            <input 
                                                v-model="newPatient.cep" 
                                                type="text" 
                                                class="flex-1 px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" 
                                                placeholder="00000-000"
                                                maxlength="9"
                                                @input="newPatient.cep = formatarCep($event.target.value); cepError = ''"
                                                @blur="consultarCep"
                                            />
                                            <button 
                                                type="button"
                                                @click="consultarCep"
                                                :disabled="isConsultingCep"
                                                class="px-3 py-2 bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white rounded-md transition-colors flex items-center gap-1 text-sm font-medium"
                                            >
                                                <template v-if="isConsultingCep">
                                                    <svg class="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                </template>
                                                <template v-else>
                                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                                                    </svg>
                                                </template>
                                                Buscar
                                            </button>
                                        </div>
                                        <p v-if="cepError" class="text-xs text-red-600 dark:text-red-400 mt-1">{{ cepError }}</p>
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Tipo de Logradouro</label>
                                        <select 
                                            v-model="selectedLogradouroTipo" 
                                            @change="onLogradouroTipoChange"
                                            class="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="">Selecione o tipo</option>
                                            <option value="1">Rua</option>
                                            <option value="2">Avenida</option>
                                            <option value="3">Praça</option>
                                            <option value="4">Travessa</option>
                                            <option value="5">Alameda</option>
                                            <option value="6">Estrada</option>
                                            <option value="7">Rodovia</option>
                                            <option value="8">Viela</option>
                                            <option value="9">Beco</option>
                                            <option value="10">Largo</option>
                                            <option value="11">Parque</option>
                                            <option value="12">Jardim</option>
                                            <option value="13">Condomínio</option>
                                            <option value="14">Loteamento</option>
                                            <option value="15">Conjunto</option>
                                            <option value="16">Quadra</option>
                                            <option value="17">Lote</option>
                                            <option value="18">Chácara</option>
                                            <option value="19">Fazenda</option>
                                            <option value="20">Sítio</option>
                                            <option value="21">Colônia</option>
                                            <option value="22">Zona</option>
                                            <option value="23">Setor</option>
                                            <option value="24">Área</option>
                                            <option value="25">Sítio</option>
                                            <option value="26">Caminho</option>
                                            <option value="27">Passagem</option>
                                            <option value="28">Pátio</option>
                                            <option value="29">Recanto</option>
                                            <option value="30">Residencial</option>
                                            <option value="31">Vila</option>
                                            <option value="32">Reserva</option>
                                            <option value="33">Trevo</option>
                                            <option value="34">Acesso</option>
                                            <option value="35">Anel</option>
                                            <option value="36">Marginal</option>
                                            <option value="37">Via</option>
                                            <option value="38">Viaduto</option>
                                            <option value="39">Túnel</option>
                                            <option value="40">Ponte</option>
                                            <option value="41">Elevado</option>
                                            <option value="42">Passarela</option>
                                            <option value="43">Retorno</option>
                                            <option value="44">Rotatória</option>
                                            <option value="45">Desvio</option>
                                            <option value="46">Variante</option>
                                            <option value="47">Interligação</option>
                                            <option value="48">Ramal</option>
                                            <option value="49">Servidão</option>
                                            <option value="50">Vereda</option>
                                            <option value="51">Ladeira</option>
                                            <option value="52">Morro</option>
                                            <option value="53">Vale</option>
                                            <option value="54">Serra</option>
                                            <option value="55">Colina</option>
                                            <option value="56">Monte</option>
                                            <option value="57">Alto</option>
                                            <option value="58">Acesso</option>
                                            <option value="59">Cais</option>
                                            <option value="60">Caminho</option>
                                            <option value="61">Canal</option>
                                            <option value="62">Colônia</option>
                                            <option value="63">Condomínio</option>
                                            <option value="64">Conjunto</option>
                                            <option value="65">Conjunto Habitacional</option>
                                            <option value="66">Distrito</option>
                                            <option value="67">Esplanada</option>
                                            <option value="68">Estância</option>
                                            <option value="69">Favela</option>
                                            <option value="70">Feira</option>
                                            <option value="71">Galeria</option>
                                            <option value="72">Granja</option>
                                            <option value="73">Ilha</option>
                                            <option value="74">Lago</option>
                                            <option value="75">Lagoa</option>
                                            <option value="76">Loteamento</option>
                                            <option value="77">Mercado</option>
                                            <option value="78">Núcleo</option>
                                            <option value="79">Parque Residencial</option>
                                            <option value="80">Passeio</option>
                                            <option value="81">Praia</option>
                                            <option value="82">Prolongamento</option>
                                            <option value="83">Quadra</option>
                                            <option value="84">Recanto</option>
                                            <option value="85">Residencial</option>
                                            <option value="86">Rua de Ligação</option>
                                            <option value="87">Rua Particular</option>
                                            <option value="88">Sítio</option>
                                            <option value="89">Trecho</option>
                                            <option value="90">Vale</option>
                                            <option value="91">Vereda</option>
                                            <option value="92">Via Coletora</option>
                                            <option value="93">Via de Acesso</option>
                                            <option value="94">Via de Pedestre</option>
                                            <option value="95">Via Expressa</option>
                                            <option value="96">Via Local</option>
                                            <option value="97">Via Principal</option>
                                            <option value="98">Viela</option>
                                            <option value="99">Vila</option>
                                            <option value="100">Zona</option>
                                            <option value="101">Área Especial</option>
                                            <option value="102">Área de Lazer</option>
                                            <option value="103">Área Verde</option>
                                            <option value="104">Balneário</option>
                                            <option value="105">Campo</option>
                                            <option value="106">Ciclovia</option>
                                            <option value="107">Comunidade</option>
                                            <option value="108">Estacionamento</option>
                                            <option value="109">Forte</option>
                                            <option value="110">Gleba</option>
                                            <option value="111">Horto</option>
                                            <option value="112">Jardim</option>
                                            <option value="113">Largo</option>
                                            <option value="114">Lote</option>
                                            <option value="115">Morro</option>
                                            <option value="116">Núcleo Rural</option>
                                            <option value="117">Parque Municipal</option>
                                            <option value="118">Passeio Público</option>
                                            <option value="119">Pátio</option>
                                            <option value="120">Praça</option>
                                            <option value="121">Ramal</option>
                                            <option value="122">Residencial</option>
                                            <option value="123">Retorno</option>
                                            <option value="124">Rotatória</option>
                                            <option value="125">Servidão</option>
                                            <option value="126">Setor</option>
                                            <option value="127">Travessa</option>
                                            <option value="128">Trecho</option>
                                            <option value="129">Trevo</option>
                                            <option value="130">Vale</option>
                                            <option value="131">Vereda</option>
                                            <option value="132">Via Costeira</option>
                                            <option value="133">Via de Acesso</option>
                                            <option value="134">Via Ecológica</option>
                                            <option value="135">Via Lateral</option>
                                            <option value="136">Via Marginal</option>
                                            <option value="137">Via Parque</option>
                                            <option value="138">Via Rápida</option>
                                            <option value="139">Viaduto</option>
                                            <option value="140">Viela</option>
                                            <option value="141">Vila</option>
                                            <option value="142">Zona Rural</option>
                                            <option value="143">Zona Urbana</option>
                                            <option value="144">Condomínio Fechado</option>
                                            <option value="145">Loteamento Fechado</option>
                                            <option value="146">Condomínio Residencial</option>
                                            <option value="147">Condomínio Horizontal</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Rua</label>
                                        <input 
                                            v-model="newPatient.street" 
                                            @input="newPatient.logradouro = newPatient.street"
                                            type="text" 
                                            placeholder="Nome da rua"
                                            :class="[
                                                'mt-1 block w-full px-3 py-2 border rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500',
                                                newPatient.street ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-600' : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600'
                                            ]"
                                        />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Código do Logradouro</label>
                                        <input 
                                            v-model="newPatient.logradouro_codigo" 
                                            type="text" 
                                            placeholder="Código do logradouro"
                                            readonly
                                            class="mt-1 block w-full px-3 py-2 bg-gray-50 dark:bg-gray-600 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Número</label>
                                        <input v-model="newPatient.number" type="text" placeholder="Número da residência" class="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Complemento</label>
                                        <input v-model="newPatient.complement" type="text" placeholder="Apartamento, bloco, etc." class="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Bairro</label>
                                        <input 
                                            v-model="newPatient.neighborhood" 
                                            type="text" 
                                            placeholder="Nome do bairro"
                                            :class="[
                                                'mt-1 block w-full px-3 py-2 border rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500',
                                                newPatient.neighborhood ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-600' : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600'
                                            ]"
                                        />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Cidade</label>
                                        <input 
                                            v-model="newPatient.city" 
                                            type="text" 
                                            placeholder="Nome da cidade"
                                            :class="[
                                                'mt-1 block w-full px-3 py-2 border rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500',
                                                newPatient.city ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-600' : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600'
                                            ]"
                                        />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Estado</label>
                                        <input 
                                            v-model="newPatient.state" 
                                            type="text" 
                                            placeholder="SP" 
                                            maxlength="2"
                                            :class="[
                                                'mt-1 block w-full px-3 py-2 border rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500',
                                                newPatient.state ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-600' : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600'
                                            ]"
                                        />
                                    </div>
                                </div>
                            </div>

                            <!-- Informações Médicas -->
                            <div>
                                <h4 class="text-md font-medium text-gray-900 dark:text-white mb-4">Informações Médicas</h4>
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Plano de Saúde</label>
                                        <select v-model="newPatient.health_insurance" class="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500">
                                            <option value="">Selecione um plano ({{ insurances.length }} convênios disponíveis)</option>
                                            <option v-if="insurances.length === 0" value="" disabled>Carregando convênios...</option>
                                            <option v-for="insurance in insurances" :key="insurance.id" :value="insurance.label">{{ insurance.label }}</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Número da Carteirinha</label>
                                        <input v-model="newPatient.insurance_id" type="text" placeholder="Número do plano de saúde" class="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                                    </div>
                                    <div class="md:col-span-2">
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Alergias</label>
                                        <textarea v-model="newPatient.allergies" rows="2" placeholder="Liste as alergias conhecidas do paciente..." class="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"></textarea>
                                    </div>
                                    <div class="md:col-span-2">
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Condições Médicas</label>
                                        <textarea v-model="newPatient.medical_conditions" rows="2" placeholder="Doenças crônicas, condições especiais..." class="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"></textarea>
                                    </div>
                                    <div class="md:col-span-2">
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Observações</label>
                                        <textarea v-model="newPatient.notes" rows="2" placeholder="Informações adicionais sobre o paciente..." class="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"></textarea>
                                    </div>
                                </div>
                            </div>
                        </form>
                        
                        <div class="p-4 mt-auto border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                            <button type="button" @click="closeCreatePatientModal" class="px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-white rounded-lg text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-500">Cancelar</button>
                            <button type="button" @click="handleCreatePatient" :disabled="isCreatingPatient" class="px-4 py-2 clinic-btn text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                                <template v-if="isCreatingPatient">
                                    <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span>Cadastrando...</span>
                                </template>
                                <template v-else>
                                    <span>Cadastrar Paciente</span>
                                </template>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Modal de Cancelamento -->
                <div v-if="isCancellationModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div class="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full" @click.stop>
                        <div class="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
                            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Cancelar Agendamento</h3>
                            <button @click="closeCancellationModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"><XIcon class="w-5 h-5" /></button>
                        </div>
                        
                        <div class="p-6">
                            <div class="mb-4">
                                <p class="text-gray-600 dark:text-gray-400 mb-2">Paciente: <span class="font-medium">{{ cancellationAppointment?.patient_name }}</span></p>
                                <p v-if="cancellationAppointment?.accession_number" class="text-gray-600 dark:text-gray-400 mb-2">Accession: <span class="font-medium text-blue-600 dark:text-blue-400">{{ cancellationAppointment.accession_number }}</span></p>
                                <p class="text-gray-600 dark:text-gray-400 mb-2">Exame: <span class="font-medium">{{ cancellationAppointment?.service_name }}</span></p>
                                <p class="text-gray-600 dark:text-gray-400">Data: <span class="font-medium">{{ formatDate(cancellationAppointment?.date) }} às {{ cancellationAppointment?.time?.slice(0,5) }}</span></p>
                            </div>
                            
                            <div class="mb-4">
                                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Motivo do Cancelamento*</label>
                                <textarea 
                                    v-model="cancellationData.reason" 
                                    rows="3" 
                                    required 
                                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                                    placeholder="Informe o motivo do cancelamento..."
                                ></textarea>
                            </div>
                        </div>
                        
                        <div class="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                            <button type="button" @click="closeCancellationModal" class="px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-white rounded-lg text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-500">Cancelar</button>
                            <button type="button" @click="handleCancelAppointment" :disabled="isCancelling" class="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                                <template v-if="isCancelling">
                                    <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span>Cancelando...</span>
                                </template>
                                <template v-else>
                                    <XIcon class="w-4 h-4" />
                                    <span>Confirmar Cancelamento</span>
                                </template>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Modal de Não Realizado -->
                <div v-if="isNotPerformedModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div class="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full" @click.stop>
                        <div class="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
                            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Marcar como Não Realizado</h3>
                            <button @click="closeNotPerformedModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"><XIcon class="w-5 h-5" /></button>
                        </div>
                        
                        <div class="p-6">
                            <div class="mb-4">
                                <p class="text-gray-600 dark:text-gray-400 mb-2">Paciente: <span class="font-medium">{{ notPerformedAppointment?.patient_name }}</span></p>
                                <p v-if="notPerformedAppointment?.accession_number" class="text-gray-600 dark:text-gray-400 mb-2">Accession: <span class="font-medium text-blue-600 dark:text-blue-400">{{ notPerformedAppointment.accession_number }}</span></p>
                                <p class="text-gray-600 dark:text-gray-400 mb-2">Exame: <span class="font-medium">{{ notPerformedAppointment?.service_name }}</span></p>
                                <p class="text-gray-600 dark:text-gray-400">Data: <span class="font-medium">{{ formatDate(notPerformedAppointment?.date) }} às {{ notPerformedAppointment?.time?.slice(0,5) }}</span></p>
                            </div>
                            
                            <!-- Aviso sobre o status -->
                            <div class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
                                <div class="flex items-start">
                                    <div class="flex-shrink-0">
                                        <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                                        </svg>
                                    </div>
                                    <div class="ml-3">
                                        <h3 class="text-sm font-medium text-yellow-800 dark:text-yellow-200">Paciente Confirmado</h3>
                                        <div class="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                                            <p>Esta ação será registrada na auditoria.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div class="mb-4">
                                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Motivo da Não Realização*</label>
                                <textarea 
                                    v-model="notPerformedData.reason" 
                                    rows="3" 
                                    required 
                                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                                    placeholder="Informe o motivo pelo qual o paciente não realizou o exame..."
                                ></textarea>
                            </div>
                        </div>
                        
                        <div class="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                            <button type="button" @click="closeNotPerformedModal" class="px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-white rounded-lg text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-500">Cancelar</button>
                            <button type="button" @click="handleNotPerformedAppointment" :disabled="isNotPerforming" class="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                                <template v-if="isNotPerforming">
                                    <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span>Salvando...</span>
                                </template>
                                <template v-else>
                                    <XCircleIcon class="w-4 h-4" />
                                    <span>Confirmar Não Realizado</span>
                                </template>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Modal de Reagendamento -->
                <div v-if="isRescheduleModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div class="bg-white dark:bg-gray-800 rounded-lg max-w-lg w-full" @click.stop>
                        <div class="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
                            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Reagendar Agendamento</h3>
                            <button @click="closeRescheduleModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"><XIcon class="w-5 h-5" /></button>
                        </div>
                        
                        <div class="p-6">
                            <div class="mb-4">
                                <p class="text-gray-600 dark:text-gray-400 mb-2">Paciente: <span class="font-medium">{{ rescheduleAppointment?.patient_name }}</span></p>
                                <p v-if="rescheduleAppointment?.accession_number" class="text-gray-600 dark:text-gray-400 mb-2">Accession: <span class="font-medium text-blue-600 dark:text-blue-400">{{ rescheduleAppointment.accession_number }}</span></p>
                                <p class="text-gray-600 dark:text-gray-400 mb-2">Exame: <span class="font-medium">{{ rescheduleAppointment?.service_name }}</span></p>
                                <p class="text-gray-600 dark:text-gray-400">Data Atual: <span class="font-medium">{{ formatDate(rescheduleAppointment?.date) }} às {{ rescheduleAppointment?.time?.slice(0,5) }}</span></p>
                            </div>
                            
                            <!-- Aviso para agendamentos confirmados -->
                            <div v-if="rescheduleAppointment?.status === 'confirmed'" class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
                                <div class="flex items-start">
                                    <div class="flex-shrink-0">
                                        <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                                        </svg>
                                    </div>
                                    <div class="ml-3">
                                        <h3 class="text-sm font-medium text-yellow-800 dark:text-yellow-200">Agendamento Confirmado</h3>
                                        <div class="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                                            <p>Este agendamento já está confirmado. O reagendamento pode afetar o processo de atendimento. Certifique-se de informar o paciente sobre a mudança.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nova Data*</label>
                                    <input v-model="rescheduleData.new_date" type="date" required class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Novo Horário* (HH:MM)</label>
                                    <input 
                                        v-model="rescheduleData.new_time" 
                                        type="time" 
                                        required 
                                        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                                        placeholder="Ex: 14:30"
                                    />
                                    <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Será criado um horário especial para este reagendamento</p>
                                </div>
                            </div>
                            
                            <div class="mb-4">
                                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Motivo do Reagendamento*</label>
                                <textarea 
                                    v-model="rescheduleData.reason" 
                                    rows="3" 
                                    required 
                                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                                    :placeholder="rescheduleAppointment?.status === 'confirmed' ? 'Informe o motivo do reagendamento (agendamento confirmado)' : 'Informe o motivo do reagendamento...'"
                                ></textarea>
                            </div>
                        </div>
                        
                        <div class="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                            <button type="button" @click="closeRescheduleModal" class="px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-white rounded-lg text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-500">Cancelar</button>
                            <button type="button" @click="handleRescheduleAppointment" :disabled="isRescheduling" class="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                                <template v-if="isRescheduling">
                                    <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span>Reagendando...</span>
                                </template>
                                <template v-else>
                                    <CalendarIcon class="w-4 h-4" />
                                    <span>Confirmar Reagendamento</span>
                                </template>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Modal de Cancelar Reagendamento -->
                <div v-if="isCancelRescheduleModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div class="bg-white dark:bg-gray-800 rounded-lg max-w-lg w-full" @click.stop>
                        <div class="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
                            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Cancelar Reagendamento</h3>
                            <button @click="closeCancelRescheduleModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"><XIcon class="w-5 h-5" /></button>
                        </div>
                        
                        <div class="p-6">
                            <div class="mb-4">
                                <p class="text-gray-600 dark:text-gray-400 mb-2">Paciente: <span class="font-medium">{{ cancelRescheduleAppointment?.patient_name }}</span></p>
                                <p v-if="cancelRescheduleAppointment?.accession_number" class="text-gray-600 dark:text-gray-400 mb-2">Accession: <span class="font-medium text-blue-600 dark:text-blue-400">{{ cancelRescheduleAppointment.accession_number }}</span></p>
                                <p class="text-gray-600 dark:text-gray-400 mb-2">Exame: <span class="font-medium">{{ cancelRescheduleAppointment?.service_name }}</span></p>
                                <p class="text-gray-600 dark:text-gray-400 mb-2">Data Atual: <span class="font-medium">{{ formatDate(cancelRescheduleAppointment?.date) }} às {{ cancelRescheduleAppointment?.time?.slice(0,5) }}</span></p>
                                <p class="text-gray-600 dark:text-gray-400">Reagendado para: <span class="font-medium text-orange-600 dark:text-orange-400">{{ formatDate(cancelRescheduleAppointment?.rescheduled_to?.date) }} às {{ cancelRescheduleAppointment?.rescheduled_to?.time?.slice(0,5) }}</span></p>
                            </div>
                            
                            <!-- Aviso de confirmação -->
                            <div class="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4 mb-4">
                                <div class="flex items-start">
                                    <div class="flex-shrink-0">
                                        <svg class="h-5 w-5 text-orange-400" viewBox="0 0 20 20" fill="currentColor">
                                            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                                        </svg>
                                    </div>
                                    <div class="ml-3">
                                        <h3 class="text-sm font-medium text-orange-800 dark:text-orange-200">Confirmar Cancelamento</h3>
                                        <div class="mt-2 text-sm text-orange-700 dark:text-orange-300">
                                            <p>Ao cancelar o reagendamento, o agendamento original será restaurado e o novo agendamento será removido. Esta ação não pode ser desfeita.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div class="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                            <button type="button" @click="closeCancelRescheduleModal" class="px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-white rounded-lg text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-500">Cancelar</button>
                            <button type="button" @click="handleCancelReschedule" class="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium flex items-center gap-2">
                                <XIcon class="w-4 h-4" />
                                <span>Confirmar Cancelamento</span>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Modal de Desconfirmação de Agendamento -->
                <div v-if="isUnconfirmModalOpen" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-lg p-6" @click.stop>
                        <div class="flex justify-between items-center">
                            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Desconfirmar Agendamento</h3>
                            <button @click="closeUnconfirmModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                                <XIcon class="w-5 h-5" />
                            </button>
                        </div>
                        <p class="mt-4 text-gray-600 dark:text-gray-300">
                            Tem certeza que deseja desconfirmar o agendamento do paciente
                            <strong>{{ unconfirmModalAppointment?.patient_name }}</strong>?
                            Os dados de confirmação serão removidos e o status voltará para Pendente.
                        </p>
                        <div class="mt-4 bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                            <div class="text-xs space-y-1 text-gray-700 dark:text-gray-300">
                                <div><span class="font-medium">Data:</span> {{ formatDate(unconfirmModalAppointment?.date) }}</div>
                                <div><span class="font-medium">Horário:</span> {{ unconfirmModalAppointment?.time?.slice(0,5) }}</div>
                                <div><span class="font-medium">Exame:</span> {{ unconfirmModalAppointment?.service_name }}</div>
                                <div v-if="unconfirmModalAppointment?.accession_number"><span class="font-medium">OS:</span> <span class="text-blue-600 dark:text-blue-400 font-mono">{{ unconfirmModalAppointment.accession_number }}</span></div>
                            </div>
                        </div>
                        <div class="flex justify-end gap-2 pt-4">
                            <button @click="closeUnconfirmModal" class="px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
                                Cancelar
                            </button>
                            <button @click="handleUnconfirmAppointment" :disabled="isUnconfirming" class="px-4 py-2 clinic-btn text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                                <RotateCcwIcon class="w-4 h-4" />
                                <span v-if="isUnconfirming">Desconfirmando...</span>
                                <span v-else>Desconfirmar</span>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Modal de Histórico do Paciente Melhorado -->
                <div v-if="isPatientHistoryModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div class="bg-white dark:bg-gray-800 rounded-lg max-w-7xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
                        <div class="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700">
                            <div class="flex items-center space-x-3">
                                <UserIcon class="w-8 h-8 text-blue-600 dark:text-blue-400" />
                                <div>
                                    <h3 class="text-xl font-bold text-gray-900 dark:text-white">História Completa do Paciente</h3>
                                    <p class="text-sm text-gray-600 dark:text-gray-400">Visualização detalhada de todos os agendamentos</p>
                                </div>
                            </div>
                            <button @click="closePatientHistoryModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                                <XIcon class="w-6 h-6" />
                            </button>
                        </div>
                        
                        <div class="p-6 overflow-y-auto max-h-[75vh]">
                            <div v-if="patientHistoryData" class="space-y-6">
                                <!-- Informações do Paciente -->
                                <div class="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-600 rounded-xl p-6 border border-blue-200 dark:border-gray-600">
                                    <div class="flex items-start justify-between">
                                        <div class="flex-1">
                                            <h4 class="text-2xl font-bold text-gray-900 dark:text-white mb-3">{{ patientHistoryData.patient_name }}</h4>
                                            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                <div class="flex items-center space-x-2">
                                                    <IdentificationIcon class="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                                    <div>
                                                        <p class="text-sm font-medium text-gray-700 dark:text-gray-300">ID do Paciente</p>
                                                        <p class="text-lg font-semibold text-gray-900 dark:text-white">{{ patientHistoryData.patient_id }}</p>
                                                    </div>
                                                </div>
                                                <div v-if="patientHistoryData.patient_cpf" class="flex items-center space-x-2">
                                                    <DocumentTextIcon class="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                                    <div>
                                                        <p class="text-sm font-medium text-gray-700 dark:text-gray-300">CPF</p>
                                                        <p class="text-lg font-semibold text-gray-900 dark:text-white">{{ patientHistoryData.patient_cpf }}</p>
                                                    </div>
                                                </div>
                                                <div v-if="patientHistoryData.patient_phone" class="flex items-center space-x-2">
                                                    <PhoneIcon class="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                                    <div>
                                                        <p class="text-sm font-medium text-gray-700 dark:text-gray-300">Telefone</p>
                                                        <p class="text-lg font-semibold text-gray-900 dark:text-white">{{ patientHistoryData.patient_phone }}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="text-right">
                                            <div class="inline-flex items-center px-4 py-2 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-sm font-medium rounded-lg">
                                                <CalendarIcon class="w-4 h-4 mr-2" />
                                                {{ patientHistoryData.total_appointments }} agendamento(s)
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Filtros e Busca -->
                                <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                                    <div class="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                                        <div class="flex flex-col sm:flex-row gap-3">
                                            <select v-model="historyStatusFilter" class="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                                                <option value="">Todos os status</option>
                                                <option value="pending">Pendente</option>
                                                <option value="confirmed">Confirmado</option>
                                                <option value="completed">Concluído</option>
                                                <option value="cancelled">Cancelado</option>
                                            </select>
                                            <select v-model="historyDateFilter" class="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                                                <option value="">Todos os períodos</option>
                                                <option value="last_30">Últimos 30 dias</option>
                                                <option value="last_90">Últimos 90 dias</option>
                                                <option value="last_year">Último ano</option>
                                                <option value="older">Mais antigos</option>
                                            </select>
                                        </div>
                                        <div class="text-sm text-gray-600 dark:text-gray-400">
                                            Mostrando {{ filteredHistoryAppointments.length }} de {{ patientHistoryData.total_appointments }} agendamentos
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- Lista de Agendamentos -->
                                <div class="space-y-4">
                                    <h5 class="text-lg font-semibold text-gray-800 dark:text-gray-200 flex items-center">
                                        <ClockIcon class="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
                                        Histórico de Agendamentos
                                    </h5>
                                    
                                    <div v-if="filteredHistoryAppointments.length === 0" class="text-center py-8 text-gray-500 dark:text-gray-400">
                                        <CalendarIcon class="w-12 h-12 mx-auto mb-3 opacity-50" />
                                        <p>Nenhum agendamento encontrado para os filtros aplicados.</p>
                                    </div>
                                    
                                    <div v-for="appointment in filteredHistoryAppointments" :key="appointment.id" 
                                         class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
                                        <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                                            <!-- Informações Principais -->
                                            <div class="flex-1">
                                                <div class="flex items-start justify-between mb-3">
                                                    <div class="flex items-center space-x-3">
                                                        <component :is="getStatusIcon(appointment.status)" :class="getStatusIconClass(appointment.status)" />
                                                        <div>
                                                            <h6 class="font-semibold text-gray-900 dark:text-white text-lg">{{ appointment.service_name }}</h6>
                                                            <p class="text-sm text-gray-600 dark:text-gray-400">{{ appointment.schedule_name }}</p>
                                                        </div>
                                                    </div>
                                                    <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium" :class="getStatusBadgeClass(appointment.status)">
                                                        {{ getStatusDisplay(appointment.status) }}
                                                    </span>
                                                </div>
                                                
                                                <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                                    <div class="flex items-center space-x-2">
                                                        <CalendarIcon class="w-4 h-4 text-gray-400" />
                                                        <div>
                                                            <p class="text-gray-500 dark:text-gray-400">Data</p>
                                                            <p class="font-medium text-gray-900 dark:text-white">{{ formatDate(appointment.date) }}</p>
                                                        </div>
                                                    </div>
                                                    <div class="flex items-center space-x-2">
                                                        <ClockIcon class="w-4 h-4 text-gray-400" />
                                                        <div>
                                                            <p class="text-gray-500 dark:text-gray-400">Horário</p>
                                                            <p class="font-medium text-gray-900 dark:text-white">{{ appointment.time }}</p>
                                                        </div>
                                                    </div>
                                                    <div v-if="appointment.accession_number" class="flex items-center space-x-2">
                                                        <DocumentTextIcon class="w-4 h-4 text-gray-400" />
                                                        <div>
                                                            <p class="text-gray-500 dark:text-gray-400">OS</p>
                                                            <p class="font-medium text-gray-900 dark:text-white">{{ appointment.accession_number }}</p>
                                                        </div>
                                                    </div>
                                                    <div v-if="appointment.cid10" class="flex items-center space-x-2">
                                                        <ClipboardDocumentListIcon class="w-4 h-4 text-gray-400" />
                                                        <div>
                                                            <p class="text-gray-500 dark:text-gray-400">CID-10</p>
                                                            <p class="font-medium text-gray-900 dark:text-white">{{ appointment.cid10 }}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                <!-- Indicadores de Status do Processo -->
                                                <div class="flex items-center space-x-4 mt-3">
                                                    <div class="flex items-center space-x-1">
                                                        <FileTextIcon :class="['w-3 h-3', appointment.worklist_generated ? 'text-green-500' : 'text-gray-300']" />
                                                        <span class="text-xs text-gray-600 dark:text-gray-400">Worklist</span>
                                                    </div>
                                                    <div class="flex items-center space-x-1">
                                                        <ClipboardDocumentListIcon :class="['w-3 h-3', appointment.anamnesis_completed ? 'text-green-500' : 'text-gray-300']" />
                                                        <span class="text-xs text-gray-600 dark:text-gray-400">Anamnese</span>
                                                    </div>
                                                    <div class="flex items-center space-x-1">
                                                        <CheckIcon :class="['w-3 h-3', appointment.attended ? 'text-green-500' : 'text-gray-300']" />
                                                        <span class="text-xs text-gray-600 dark:text-gray-400">Atendido</span>
                                                    </div>
                                                </div>
                                                
                                                <!-- Observações -->
                                                <div v-if="appointment.notes" class="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                                                    <p class="text-sm text-yellow-800 dark:text-yellow-200">
                                                        <span class="font-medium">Observações:</span> {{ appointment.notes }}
                                                    </p>
                                                </div>
                                            </div>
                                            
                                            <!-- Ações -->
                                            <div class="flex flex-col space-y-2 lg:ml-4">
                                                <button 
                                                    @click="selectSearchResult(appointment)" 
                                                    class="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-200 dark:hover:bg-blue-800 rounded-lg transition-colors">
                                                    <EyeIcon class="w-4 h-4 mr-1" />
                                                    Ver na Agenda
                                                </button>
                                                <button 
                                                    v-if="can('Schedules.view_appointment') && ['confirmed','completed','laudado','reported'].includes(appointment.status)"
                                                    @click="navigateToDocuments(appointment)" 
                                                    class="inline-flex items-center px-3 py-2 text-sm font-medium text-green-700 bg-green-100 hover:bg-green-200 dark:bg-green-900 dark:text-green-200 dark:hover:bg-green-800 rounded-lg transition-colors">
                                                    <FileText class="w-4 h-4 mr-1" />
                                                    Documentos
                                                </button>
                                                <button
                                                    v-if="appointment.accession_number && isAttachmentAllowed(appointment) && can('Schedules.view_appointment')"
                                                    @click="scanPatient(appointment)"
                                                    class="inline-flex items-center px-3 py-2 text-sm font-medium text-purple-700 bg-purple-100 hover:bg-purple-200 dark:bg-purple-900 dark:text-purple-200 dark:hover:bg-purple-800 rounded-lg transition-colors">
                                                    <ScanIcon class="w-4 h-4 mr-1" />
                                                    Escanear
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <!-- Loading -->
                            <div v-if="!patientHistoryData" class="flex items-center justify-center py-12">
                                <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                <span class="ml-3 text-gray-600 dark:text-gray-400">Carregando histórico...</span>
                            </div>
                        </div>
                        
                        <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex justify-between items-center">
                            <div class="text-sm text-gray-600 dark:text-gray-400">
                                <span v-if="patientHistoryData">
                                    Última atualização: {{ new Date().toLocaleString('pt-BR') }}
                                </span>
                            </div>
                            <div class="flex space-x-3">
                                <button 
                                    @click="exportPatientHistory" 
                                    class="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-200 dark:hover:bg-blue-800 rounded-lg transition-colors">
                                    <DocumentArrowDownIcon class="w-4 h-4 mr-2" />
                                    Exportar PDF
                                </button>
                                <button 
                                    type="button" 
                                    @click="closePatientHistoryModal" 
                                    class="px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-white rounded-lg text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors">
                                    Fechar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <BannerMessage
                    v-if="banner.show"
                    :type="banner.type"
                    :message="banner.message"
                    @close="closeBanner"
                />



                <!-- Modal de Pedir Complemento -->
                <div v-if="isRecallModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40 p-4">
                    <div class="bg-white dark:bg-gray-800 rounded-lg max-w-lg w-full" @click.stop>
                        <div class="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
                            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Pedir Complemento</h3>
                            <button @click="closeRecallModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                                <XIcon class="w-5 h-5" />
                            </button>
                        </div>
                        
                        <div class="p-6">
                            <!-- Componente RecallPatientButton com autoOpen=true -->
                            <RecallPatientButton
                                :appointment="recallAppointment"
                                :disabled="false"
                                :auto-open="true"
                                @recall-patient="handleRecallPatientFromComponent"
                            />
                        </div>
                    </div>
                </div>

                <!-- Modal de Importação SISREG -->
                <div v-if="isImportModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div class="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col" @click.stop>
                        <div class="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
                            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Importar Agenda SISREG</h3>
                            <button @click="closeImportModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"><XIcon class="w-5 h-5" /></button>
                        </div>
                        
                        <div class="p-6 overflow-y-auto flex-1">
                            <!-- Banner de Aviso - Agenda sem Serviços Vinculados -->
                            <div v-if="!currentScheduleServices || currentScheduleServices.length === 0" class="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
                                <div class="flex items-start">
                                    <div class="flex-shrink-0">
                                        <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                                        </svg>
                                    </div>
                                    <div class="ml-3">
                                        <h3 class="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                                            Atenção: Agenda sem Serviços Vinculados
                                        </h3>
                                        <div class="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                                            <p class="mb-2">Esta agenda não possui serviços vinculados. Isso pode causar problemas durante a importação:</p>
                                            <ul class="list-disc list-inside space-y-1">
                                                <li>Serviços não encontrados serão ignorados</li>
                                                <li>A importação pode falhar para alguns agendamentos</li>
                                                <li>Recomendamos vincular os serviços necessários antes de importar</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div class="mb-4">
                                <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                    Selecione um arquivo TXT com os dados do SISREG da Prefeitura do Rio de Janeiro. 
                                    O arquivo deve conter os dados separados por ponto e vírgula (;).
                                </p>
                            </div>
                            
                            <!-- Barra de Progresso Melhorada com Efeitos Visuais -->
                            <div v-if="isImporting" class="mb-6">
                                <div class="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-900/30 dark:via-indigo-900/30 dark:to-purple-900/30 border border-blue-200 dark:border-blue-700 rounded-xl p-6 shadow-lg backdrop-blur-sm relative overflow-hidden">
                                    <div class="relative z-10">
                                        <!-- Header com ícone animado e título -->
                                        <div class="flex items-center justify-between mb-4">
                                            <div class="flex items-center gap-4">
                                                <div class="relative">
                                                    <!-- Ícone principal com efeito de rotação -->
                                                    <div class="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg">
                                                        <svg width="200" height="200" viewBox="-25 -25 250 250" version="1.1" xmlns="http://www.w3.org/2000/svg" style="transform:rotate(-90deg)">
                                                            <circle r="90" cx="100" cy="100" fill="transparent" stroke="#e0e0e0" stroke-width="16px"></circle>
                                                            <circle r="90" cx="100" cy="100" stroke="#76e5b1" stroke-width="16px" stroke-linecap="round" stroke-dashoffset="118.692px" fill="transparent" stroke-dasharray="565.48px"></circle>
                                                            <text x="71px" y="115px" fill="#6bdba7" font-size="52px" font-weight="bold" style="transform:rotate(90deg) translate(0px, -196px)">{{ Math.round(importProgress) }}</text>
                                                        </svg>
                                                    </div>
                                                    <!-- Círculo de progresso ao redor do ícone -->
                                                </div>
                                                <div>
                                                    <h4 class="text-lg font-bold text-blue-900 dark:text-blue-100 mb-1">Importando Agenda SISREG</h4>
                                                    <p class="text-sm text-blue-700 dark:text-blue-200 font-medium">Processando dados do arquivo...</p>
                                                </div>
                                            </div>
                                            <!-- Porcentagem com efeito de destaque -->
                                            <div class="text-right">
                                                <div class="text-right">
    <div class="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-1">{{ Math.round(importProgress) }}%</div>
    <div class="text-xs text-blue-500 dark:text-blue-300 font-medium">Concluído</div>
</div>
                                            </div>
                                        </div>

                                        <!-- Status atual com ícone dinâmico -->
                                        <div class="flex items-center gap-3 mb-4">
                                            <div class="flex-shrink-0">
                                                <div v-if="importProgress < 30" class="w-8 h-8 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center">
                                                    <FileText class="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                                </div>
                                                <div v-else-if="importProgress < 60" class="w-8 h-8 bg-indigo-100 dark:bg-indigo-800 rounded-full flex items-center justify-center">
                                                    <Search class="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                                                </div>
                                                <div v-else-if="importProgress < 90" class="w-8 h-8 bg-purple-100 dark:bg-purple-800 rounded-full flex items-center justify-center">
                                                    <Zap class="w-4 h-4 text-purple-600 dark:text-purple-400" />
                                                </div>
                                                <div v-else class="w-8 h-8 bg-green-100 dark:bg-green-800 rounded-full flex items-center justify-center">
                                                    <CheckIcon class="w-4 h-4 text-green-600 dark:text-green-400" />
                                                </div>
                                            </div>
                                            <div class="flex-1">
                                                <div class="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                                                    {{ importStatus }}
                                                </div>
                                            
                                            </div>
                                        </div>
                                        
                                        <!-- Etapas do processo com indicadores visuais -->
                                        <div class="grid grid-cols-4 gap-2">
                                            <div class="text-center">
                                                <div class="w-8 h-8 mx-auto rounded-full flex items-center justify-center text-xs font-bold mb-1"
                                                     :class="[
                                                         importProgress >= 15 ? 'bg-green-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                                                     ]">
                                                    1
                                                </div>
                                                <div class="text-xs text-gray-600 dark:text-gray-400">Leitura</div>
                                            </div>
                                            <div class="text-center">
                                                <div class="w-8 h-8 mx-auto rounded-full flex items-center justify-center text-xs font-bold mb-1"
                                                     :class="[
                                                         importProgress >= 35 ? 'bg-green-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                                                     ]">
                                                    2
                                                </div>
                                                <div class="text-xs text-gray-600 dark:text-gray-400">Análise</div>
                                            </div>
                                            <div class="text-center">
                                                <div class="w-8 h-8 mx-auto rounded-full flex items-center justify-center text-xs font-bold mb-1"
                                                     :class="[
                                                         importProgress >= 55 ? 'bg-green-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                                                     ]">
                                                    3
                                                </div>
                                                <div class="text-xs text-gray-600 dark:text-gray-400">Processo</div>
                                            </div>
                                            <div class="text-center">
                                                <div class="w-8 h-8 mx-auto rounded-full flex items-center justify-center text-xs font-bold mb-1"
                                                     :class="[
                                                         importProgress >= 85 ? 'bg-green-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                                                     ]">
                                                    4
                                                </div>
                                                <div class="text-xs text-gray-600 dark:text-gray-400">Finalizar</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div class="space-y-4">
                                <div v-if="!isImporting">
                                    <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Arquivo TXT do SISREG</label>
                                    <div
                                        class="w-full flex flex-col items-center justify-center border-2 border-dashed border-blue-400 dark:border-blue-600 rounded-xl p-10 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-900/10 dark:via-indigo-900/10 dark:to-purple-900/10 cursor-pointer hover:from-blue-100 hover:via-indigo-100 hover:to-purple-100 dark:hover:from-blue-900/20 dark:hover:via-indigo-900/20 dark:hover:to-purple-900/20 transition-all duration-300 relative group"
                                        @click="triggerFileInput"
                                        @dragover.prevent="onDragOver"
                                        @dragleave.prevent="onDragLeave"
                                        @drop.prevent="onDropFile"
                                        :class="{ 'ring-4 ring-blue-400 dark:ring-blue-500 shadow-lg scale-105': isDragActive }"
                                    >

                                    <div class="relative z-10 flex flex-col items-center">
                                        <div class="w-16 h-16 rounded-full flex items-center justify-center mb-4 shadow-lg group-hover:shadow-xl transition-all duration-300 transform group-hover:scale-110 mx-auto">
                                            <UploadIcon class="w-8 h-8 text-white flex-shrink-0" />
                                        </div>                                         
                                        <h3 class="text-lg font-bold text-blue-700 dark:text-blue-200 mb-2">Arraste o arquivo TXT aqui</h3>
                                        <p class="text-sm text-blue-600 dark:text-blue-300 font-medium mb-1">ou clique para selecionar</p>
                                        <div class="flex items-center justify-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                                            <FileTextIcon class="w-4 h-4" />
                                            <span class="px-2 py-1 bg-white/50 dark:bg-gray-800/50 rounded-full">
                                                Apenas arquivos .txt
                                            </span>
                                        </div>
                                    </div>

                                        <input
                                            type="file"
                                            ref="fileInput"
                                            @change="handleFileSelect"
                                            accept=".txt"
                                            class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                            style="z-index:2;"
                                            tabindex="-1"
                                        />
                                    </div>
                                </div>
                                <div v-if="selectedFile && !isImporting" class="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-700 rounded-xl p-4 shadow-sm">
                                    <div class="flex items-center gap-3 mb-3">
                                        <div class="w-10 h-10 bg-green-100 dark:bg-green-800 rounded-full flex items-center justify-center">
                                            <span class="text-green-600 dark:text-green-400 text-lg">📄</span>
                                        </div>
                                        <div>
                                            <h4 class="font-semibold text-green-900 dark:text-green-100">Arquivo Selecionado</h4>
                                            <p class="text-sm text-green-700 dark:text-green-200">Pronto para importação</p>
                                        </div>
                                    </div>
                                    <div class="space-y-2">
                                        <div class="flex items-center justify-between">
                                            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Nome:</span>
                                            <span class="text-sm text-gray-900 dark:text-white font-mono">{{ selectedFile.name }}</span>
                                        </div>
                                        <div class="flex items-center justify-between">
                                            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Tamanho:</span>
                                            <span class="text-sm text-gray-900 dark:text-white">{{ formatFileSize(selectedFile.size) }}</span>
                                        </div>
                                    </div>
                                </div>
                                
                                <div v-if="filePreview && !isImporting" class="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-700 rounded-xl p-4 shadow-sm">
                                    <div class="flex items-center gap-3 mb-3">
                                        <div class="w-10 h-10 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center">
                                            <span class="text-blue-600 dark:text-blue-400 text-lg">👁️</span>
                                        </div>
                                        <div>
                                            <h4 class="font-semibold text-blue-900 dark:text-blue-100">Prévia do Arquivo</h4>
                                            <p class="text-sm text-blue-700 dark:text-blue-200">Primeiras 3 linhas do conteúdo</p>
                                        </div>
                                    </div>
                                    <div class="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                                        <pre class="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap max-h-32 overflow-y-auto font-mono leading-relaxed">{{ filePreview }}</pre>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div class="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                            <button @click="closeImportModal" 
                                    class="px-6 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl text-sm font-semibold transition-all duration-300 transform hover:scale-105 shadow-sm hover:shadow-md">
                                Cancelar
                            </button>
                            <button 
                                @click="handleImportSisreg" 
                                :disabled="!selectedFile || isImporting"
                                class="px-8 py-3 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 hover:from-blue-600 hover:via-indigo-600 hover:to-purple-600 text-white rounded-xl text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none disabled:shadow-lg relative overflow-hidden group"
                            >
                                <!-- Efeito de brilho no hover -->
                                <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                                
                                <template v-if="isImporting">
                                    <div class="relative">
                                        <svg class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                    </div>
                                    <span class="font-semibold">Processando...</span>
                                </template>
                                <template v-else>
                                    <UploadIcon class="w-5 h-5" />
                                    <span class="font-semibold">Importar Agenda</span>
                                </template>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Modal de Resultado da Importação -->
                <div v-if="importResult" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div class="bg-white dark:bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl" @click.stop>
                        <div class="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
                            <div class="flex items-center gap-3">
                                <div :class="['w-10 h-10 rounded-full flex items-center justify-center', importResult.success ? 'bg-green-100 dark:bg-green-800' : 'bg-red-100 dark:bg-red-800']">
                                    <CheckIcon v-if="importResult.success" class="w-6 h-6 text-green-600 dark:text-green-400" />
                                    <XIcon v-else class="w-6 h-6 text-red-600 dark:text-red-400" />
                                </div>
                                <h3 class="text-xl font-bold text-gray-900 dark:text-white">Resultado da Importação</h3>
                            </div>
                            <button @click="closeImportResult" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                                <XIcon class="w-6 h-6" />
                            </button>
                        </div>
                        
                        <div class="p-6 overflow-y-auto flex-1">
                            <!-- Resumo principal -->
                            <div class="mb-6">
                                <div :class="['p-6 rounded-xl border-2 shadow-sm', importResult.success ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-700' : 'bg-gradient-to-r from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20 border-red-200 dark:border-red-700']">
                                    <div class="flex items-center gap-4">
                                        <div :class="['w-12 h-12 rounded-full flex items-center justify-center shadow-lg', importResult.success ? 'bg-green-500' : 'bg-red-500']">
                                            <CheckIcon v-if="importResult.success" class="w-7 h-7 text-white" />
                                            <XIcon v-else class="w-7 h-7 text-white" />
                                        </div>
                                        <div class="flex-1">
                                            <h4 :class="['text-lg font-bold mb-1', importResult.success ? 'text-green-900 dark:text-green-100' : 'text-red-900 dark:text-red-100']">
                                                {{ importResult.success ? '🎉 Importação Concluída com Sucesso!' : 'Erro na Importação' }}
                                            </h4>
                                            <p :class="['text-sm font-medium', importResult.success ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200']">
                                                {{ importResult.message }}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <!-- Detalhes da importação -->
                            <div class="space-y-4">
                                <!-- Agendamentos importados -->
                                <div v-if="importResult.imported_count > 0" class="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-700 rounded-xl p-5 shadow-sm">
                                    <div class="flex items-center gap-3 mb-3">
                                        <div class="w-8 h-8 bg-green-100 dark:bg-green-800 rounded-full flex items-center justify-center">
                                            <span class="text-green-600 dark:text-green-400 text-sm">✅</span>
                                        </div>
                                        <h4 class="font-semibold text-green-900 dark:text-green-100">Agendamentos Importados</h4>
                                    </div>
                                    <div class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-green-100 dark:border-green-800">
                                        <div class="flex items-center justify-between">
                                            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Total importado:</span>
                                            <span class="text-lg font-bold text-green-600 dark:text-green-400">{{ importResult.imported_count }} agendamentos</span>
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- Erros encontrados -->
                                <div v-if="importResult.errors.length > 0" class="bg-gradient-to-r from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20 border border-red-200 dark:border-red-700 rounded-xl p-5 shadow-sm">
                                    <div class="flex items-center gap-3 mb-3">
                                        <div class="w-8 h-8 bg-red-100 dark:bg-red-800 rounded-full flex items-center justify-center">
                                            <span class="text-red-600 dark:text-red-400 text-sm">⚠️</span>
                                        </div>
                                        <h4 class="font-semibold text-red-900 dark:text-red-100">Erros Encontrados ({{ importResult.errors.length }})</h4>
                                    </div>
                                    <div class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-red-100 dark:border-red-800 max-h-40 overflow-y-auto">
                                        <ul class="text-sm text-red-800 dark:text-red-200 space-y-2">
                                            <li v-for="(error, index) in importResult.errors" :key="index" class="flex items-start gap-3 p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
                                                <span class="text-red-600 dark:text-red-400 font-bold mt-0.5">•</span>
                                                <span class="flex-1">{{ error }}</span>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                                
                                <!-- Avisos -->
                                <div v-if="importResult.warnings.length > 0" class="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 border border-yellow-200 dark:border-yellow-700 rounded-xl p-5 shadow-sm">
                                    <div class="flex items-center gap-3 mb-3">
                                        <div class="w-8 h-8 bg-yellow-100 dark:bg-yellow-800 rounded-full flex items-center justify-center">
                                            <span class="text-yellow-600 dark:text-yellow-400 text-sm">💡</span>
                                        </div>
                                        <h4 class="font-semibold text-yellow-900 dark:text-yellow-100">Avisos ({{ importResult.warnings.length }})</h4>
                                    </div>
                                    <div class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-yellow-100 dark:border-yellow-800 max-h-40 overflow-y-auto">
                                        <ul class="text-sm text-yellow-800 dark:text-yellow-200 space-y-2">
                                            <li v-for="(warning, index) in importResult.warnings" :key="index" class="flex items-start gap-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                                                <span class="text-yellow-600 dark:text-yellow-400 font-bold mt-0.5">•</span>
                                                <span class="flex-1">{{ warning }}</span>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div class="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                            <button @click="closeImportResult" 
                                    class="px-6 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl text-sm font-semibold transition-all duration-300 transform hover:scale-105 shadow-sm hover:shadow-md">
                                Fechar
                            </button>
                            <button 
                                v-if="importResult.success"
                                @click="refreshAfterImport" 
                                class="px-8 py-3 bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white rounded-xl text-sm font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center gap-2"
                            >
                                <span>🔄</span>
                                <span>Atualizar Agenda</span>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Modal de Horário Especial -->
                <div v-if="isSpecialSlotModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" @click="closeSpecialSlotModal">
                    <div class="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" @click.stop>
                        <div class="flex justify-between items-start mb-4">
                            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Gerar Horário Especial</h3>
                            <button @click="closeSpecialSlotModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"><XIcon class="w-5 h-5" /></button>
                        </div>
                        
                        <form @submit.prevent="handleCreateSpecialSlot" class="space-y-4">
                            <div>
                                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Data</label>
                                <input 
                                    v-model="specialSlotData.date" 
                                    type="date" 
                                    required
                                    class="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            
                            <div>
                                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Horário</label>
                                <input 
                                    v-model="specialSlotData.time" 
                                    type="time" 
                                    required
                                    class="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            

                            
                            <div>
                                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Observações (opcional)</label>
                                <textarea 
                                    v-model="specialSlotData.notes" 
                                    rows="3"
                                    placeholder="Observações sobre o horário especial..."
                                    class="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                ></textarea>
                            </div>
                            
                            <div class="flex justify-end gap-3 pt-4">
                                <button 
                                    type="button" 
                                    @click="closeSpecialSlotModal"
                                    class="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button 
                                    type="submit" 
                                    :disabled="isCreatingSpecialSlot"
                                    class="px-4 py-2 clinic-btn text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    <svg v-if="isCreatingSpecialSlot" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    {{ isCreatingSpecialSlot ? 'Criando...' : 'Criar Horário' }}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                <!-- Modal de Edição de Agendamento -->
                <div v-if="isEditModalOpen" class="fixed inset-0 bg-black bg-opacity-50 mt-12 flex items-center justify-center z-50 p-4">
                    <div class="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col" @click.stop>
                        <div class="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
                            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Editar Agendamento</h3>
                            <button @click="closeEditModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"><XIcon class="w-5 h-5" /></button>
                        </div>
                        
                        <form @submit.prevent="handleSaveEdit" class="p-6 space-y-4 overflow-y-auto flex-1">
                            <!-- Informações do Agendamento -->
                            <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
                                <h4 class="font-medium text-gray-900 dark:text-white mb-2">Dados do Agendamento</h4>
                                <div class="grid grid-cols-2 gap-4 text-sm">
                                    <div><span class="text-gray-900 dark:text-white">Data:</span> <span class="text-gray-900 dark:text-white">{{ formatDate(editAppointment?.date) }}</span></div>
                                    <div><span class="text-gray-900 dark:text-white">Status:</span> <span class="text-gray-900 dark:text-white">{{ getStatusLabel(editAppointment?.status) }}</span></div>
                                    <div><span class="text-gray-900 dark:text-white">Paciente Atual:</span> <span class="text-gray-900 dark:text-white">{{ editAppointment?.patient_name }}</span></div>
                                    <div><span class="text-gray-900 dark:text-white">Exame Atual:</span> <span class="text-gray-900 dark:text-white">{{ editAppointment?.service_name }}</span></div>
                                </div>
                            </div>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">

                            <div>
                                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Chave de Confirmação SISREG</label>
                                <input v-model="editData.sisreg_confirmation_key" type="text" placeholder="Digite a chave de confirmação" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                            </div>

                            <div>
                                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Procedência*</label>
                                <select v-model="editData.procedencia" required class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500">
                                    </select>
                            </div>
                            
                            </div>

                            <!-- Dados do Paciente -->
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome do Paciente*</label>
                                    <input v-model="editData.patient_name" type="text" required class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Telefone</label>
                                    <input v-model="editData.patient_phone" type="text" @input="editData.patient_phone = formatarTelefone($event.target.value)" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                                </div>
                            </div>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">E-mail</label>
                                    <input v-model="editData.patient_email" type="email" @input="editData.patient_email = formatarEmail($event.target.value)" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                                </div>
                                <div>
                                    <div v-if="editData.service_id && filteredInsurancesForEdit.length > 0">
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Plano</label>
                                    <select v-model="editData.insurance_id" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500">
                                        <option :value="null">Nenhum</option>
                                        <option v-for="i in filteredInsurancesForEdit" :key="i.id" :value="i.id">{{ i.label }}</option>
                                    </select>
                                </div>
                                </div>
                            </div>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Bairro</label>
                                    <input v-model="editData.patient_neighborhood" type="text" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                                </div>
                            </div>

                            <!-- Raça do Paciente -->
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Raça*</label>
                                    <select v-model="editData.race" required class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500">
                                        <option value="" disabled>Selecione</option>
                                        <option value="01">Branca</option>
                                        <option value="02">Preta</option>
                                        <option value="03">Parda</option>
                                        <option value="04">Amarela</option>
                                        <option value="05">Indígena</option>
                                    </select>
                                </div>
                            </div>

                            <!-- Dados do Exame -->
                            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Serviço*</label>
                                    <select v-model="editData.service_id" required class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500">
                                        <option :value="null" disabled>Selecione um serviço</option>
                                        <option v-for="s in services" :key="s.id" :value="s.id">{{ s.label }}</option>
                                    </select>
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Horário*</label>
                                    <select 
                                        v-model="editData.time" 
                                        required 
                                        :disabled="!editData.service_id"
                                        :class="[
                                            'w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500',
                                            !editData.service_id
                                                ? 'bg-gray-100 dark:bg-gray-600 border-gray-300 dark:border-gray-500 text-gray-500 dark:text-gray-400 cursor-not-allowed' 
                                                : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white'
                                        ]"
                                    >
                                        <option value="" disabled>
                                            {{ !editData.service_id ? 'Selecione um serviço primeiro' : 'Selecione' }}
                                        </option>
                                        <option v-for="t in availableTimes" :key="t" :value="t">{{ t }}</option>
                                    </select>
                                    <p v-if="!editData.service_id" class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                        Selecione um serviço para escolher o horário
                                    </p>
                                </div>
                            
                            </div>

                            <!-- Dados Médicos -->
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Médico Solicitante</label>
                                    
                                    <!-- Campo de texto para médicos importados do SISREG -->
                                    <div v-if="!editData.requesting_doctor_id && editData.requesting_doctor_name && editData.requesting_doctor_name !== 'Médico não informado'" class="mb-2">
                                        <input 
                                            v-model="editData.requesting_doctor_name" 
                                            type="text" 
                                            placeholder="Nome do médico solicitante"
                                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                                        />
                                        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                            Médico importado do SISREG (não cadastrado no sistema)
                                        </p>
                                    </div>
                                    
                                    <!-- Select para médicos cadastrados no sistema -->
                                    <div v-else>
                                        <select v-model="editData.requesting_doctor_id" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500">
                                            <option :value="null">Nenhum</option>
                                            <option v-for="d in doctors" :key="d.id" :value="d.id">{{ d.name }}</option>
                                        </select>
                                        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                            Selecione um médico cadastrado no sistema
                                        </p>
                                    </div>
                                    
                                    <!-- Botão para alternar entre os modos -->
                                    <button 
                                        v-if="editData.requesting_doctor_name || editData.requesting_doctor_id"
                                        @click="toggleDoctorMode" 
                                        type="button"
                                        class="mt-2 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 underline"
                                    >
                                        {{ !editData.requesting_doctor_id && editData.requesting_doctor_name && editData.requesting_doctor_name !== 'Médico não informado' ? 'Usar médico cadastrado' : 'Usar nome personalizado' }}
                                    </button>
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Médico Interno</label>
                                    <select v-model="editData.internal_doctor_id" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500">
                                        <option :value="null">Nenhum</option>
                                        <option v-for="d in doctors" :key="d.id" :value="d.id">{{ d.name }}</option>
                                    </select>
                                </div>
                            </div>

                            <!-- Dados Adicionais -->
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Procedência*</label>
                                    <select v-model="editData.procedencia" required class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500">
                                        <option value="" disabled>Selecione</option>
                                        <option v-for="p in procedenciaOptions" :key="p" :value="p">{{ p }}</option>
                                    </select>
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Motivo da Marcação*</label>
                                    <select v-model="editData.motivo_marcacao" required class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500">
                                        <option value="" disabled>Selecione</option>
                                        <option v-for="m in motivoMarcacaoOptions" :key="m" :value="m">{{ m }}</option>
                                    </select>
                                </div>
                            </div>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CID 10</label>
                                    <input v-model="editData.cid10" type="text" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                                </div>
                            </div>

                            <div>
                                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Observações</label>
                                <textarea v-model="editData.notes" rows="3" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"></textarea>
                            </div>
                        </form>
                        
                        <div class="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                            <button @click="closeEditModal" class="px-4 py-2 bg-gray-200 text-gray-900 dark:bg-gray-600 dark:text-white rounded-lg text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-500">Cancelar</button>
                            <button @click="handleSaveEdit" class="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-medium">Salvar Alterações</button>
                        </div>
                    </div>
                </div>

                <!-- Modal de Pesquisa -->
                <div v-if="isSearchModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div class="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col" @click.stop>
                        <div class="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
                            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Pesquisar Agendamentos</h3>
                            <button @click="closeSearchModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"><XIcon class="w-5 h-5" /></button>
                        </div>
                        
                        <div class="p-6 overflow-y-auto flex-1">
                            <!-- Filtros de Pesquisa -->
                            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                <!-- Agenda -->
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Agenda</label>
                                    <select v-model="searchFilters.schedule_id" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500">
                                        <option :value="null">Todas as agendas</option>
                                        <option v-for="agenda in agendas" :key="agenda.id" :value="agenda.id">{{ agenda.name }}</option>
                                    </select>
                                </div>
                                
                                <!-- Data Inicial -->
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Data Inicial</label>
                                    <input 
                                        v-model="searchFilters.date_from" 
                                        type="date" 
                                        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                
                                <!-- Data Final -->
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Data Final</label>
                                    <input 
                                        v-model="searchFilters.date_to" 
                                        type="date" 
                                        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>
                            
                            <!-- Tipo de Pesquisa -->
                            <div class="mb-6">
                                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tipo de Pesquisa</label>
                                <div class="flex gap-4">
                                    <label class="flex items-center">
                                        <input type="radio" v-model="searchType" value="accession" class="h-4 w-4 text-blue-600 focus:ring-blue-500" />
                                        <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">Número da OS</span>
                                    </label>
                                    <label class="flex items-center">
                                        <input type="radio" v-model="searchType" value="patient_id" class="h-4 w-4 text-blue-600 focus:ring-blue-500" />
                                        <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">ID do Paciente</span>
                                    </label>
                                    <label class="flex items-center">
                                        <input type="radio" v-model="searchType" value="patient_name" class="h-4 w-4 text-blue-600 focus:ring-blue-500" />
                                        <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">Nome do Paciente</span>
                                    </label>
                                    <label class="flex items-center">
                                        <input type="radio" v-model="searchType" value="patient_cpf" class="h-4 w-4 text-blue-600 focus:ring-blue-500" />
                                        <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">CPF do Paciente</span>
                                    </label>
                                </div>
                            </div>
                            
                            <!-- Campo de Pesquisa -->
                            <div class="mb-6">
                                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {{ getSearchFieldLabel() }}
                                </label>
                                <div class="flex gap-2">
                                    <input 
                                        v-model="searchQuery" 
                                        type="text" 
                                        :placeholder="getSearchPlaceholder()"
                                        class="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                                        @keyup.enter="performSearch"
                                    />
                                    <button 
                                        @click="performSearch" 
                                        :disabled="isSearching || !searchQuery.trim()"
                                        class="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                    >
                                        <template v-if="isSearching">
                                            <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            <span>Pesquisando...</span>
                                        </template>
                                        <template v-else>
                                            <SearchIcon class="w-4 h-4" />
                                            <span>Pesquisar</span>
                                        </template>
                                    </button>
                                    <button 
                                        @click="clearSearchFilters"
                                        class="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-md flex items-center gap-2"
                                    >
                                        <XIcon class="w-4 h-4" />
                                        <span>Limpar</span>
                                    </button>
                                </div>
                            </div>
                            
                            <!-- Resultados da Pesquisa -->
                            <div v-if="searchResults.length > 0" class="space-y-4">
                                <div class="flex justify-between items-center">
                                    <h4 class="font-medium text-gray-900 dark:text-white">Resultados ({{ searchResults.length }})</h4>
                                    <span class="text-sm text-gray-500 dark:text-gray-400">
                                        {{ searchFilters.schedule_id !== null && searchFilters.schedule_id !== '' ? 'Agenda específica' : 'Todas as agendas' }}
                                    </span>
                                </div>
                                <div class="space-y-2 max-h-96 overflow-y-auto">
                                    <div 
                                        v-for="appointment in searchResults" 
                                        :key="appointment.id"
                                        @click="selectSearchResult(appointment)"
                                        class="p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                                    >
                                        <div class="flex justify-between items-start">
                                            <div class="flex-1">
                                                <div class="flex items-center gap-2 mb-2">
                                                    <span class="font-medium text-gray-900 dark:text-white">{{ appointment.patient_name }}</span>
                                                    <span class="text-sm text-gray-500 dark:text-gray-400">(ID: {{ appointment.patient_id }})</span>
                                                    <span v-if="appointment.patient_cpf" class="text-sm text-gray-500 dark:text-gray-400">CPF: {{ appointment.patient_cpf }}</span>
                                                </div>
                                                <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                                    <div>
                                                        <span class="text-gray-600 dark:text-gray-400">OS:</span>
                                                        <span class="ml-1 font-mono text-blue-600 dark:text-blue-400">{{ appointment.accession_number || 'N/A' }}</span>
                                                    </div>
                                                    <div>
                                                        <span class="text-gray-600 dark:text-gray-400">Exame:</span>
                                                        <span class="ml-1">{{ appointment.service_name }}</span>
                                                    </div>
                                                    <div>
                                                        <span class="text-gray-600 dark:text-gray-400">Data:</span>
                                                        <span class="ml-1">{{ formatDate(appointment.date) }}</span>
                                                    </div>
                                                    <div>
                                                        <span class="text-gray-600 dark:text-gray-400">Horário:</span>
                                                        <span class="ml-1">{{ appointment.time?.slice(0,5) }}</span>
                                                    </div>
                                                </div>
                                                <div class="mt-2">
                                                    <span class="text-gray-600 dark:text-gray-400">Agenda:</span>
                                                    <span class="ml-1 font-medium">{{ appointment.schedule_name }}</span>
                                                </div>
                                            </div>
                                            <div class="flex flex-col items-end gap-2">
                                                <span :class="['px-2 py-1 rounded text-xs font-medium', 
                                                    appointment.status === 'confirmed' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : '', 
                                                    appointment.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' : '', 
                                                    appointment.status === 'cancelled' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' : '', 
                                                    appointment.status === 'completed' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' : '',
                                                    appointment.status === 'rescheduled' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' : ''
                                                ]">{{ getStatusLabel(appointment.status) }}</span>
                                                <button class="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm">
                                                    Ir para agenda →
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <!-- Mensagem quando não há resultados -->
                            <div v-else-if="searchQuery && !isSearching" class="text-center py-8 text-gray-500 dark:text-gray-400">
                                <SearchIcon class="w-12 h-12 mx-auto mb-4 opacity-50" />
                                <p>Nenhum agendamento encontrado para sua pesquisa.</p>
                                <p class="text-sm mt-2">Tente ajustar os termos de busca ou os filtros de data.</p>
                            </div>
                        </div>
                        
                        <div class="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                            <button @click="closeSearchModal" class="px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-white rounded-lg text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-500">Fechar</button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Modal de Scanner -->
            <ScanModal 
                v-if="isScanModalOpen"
                :appointment-id="contextMenu.data?.id"
                :schedule-id="selectedAgendaId"
                :is-appointment-confirmed="isAttachmentAllowed(contextMenu.data)"
                @close="closeScanModal"
                @document-saved="handleDocumentSaved"
            />

            <!-- Modal de Visualização de Documentos -->
            <DocumentsViewer 
                v-if="isDocumentsViewerOpen"
                :appointment="contextMenu.data"
                :schedule-id="selectedAgendaId"
                @close="closeDocumentsViewer"
                @document-deleted="handleDocumentDeleted"
            />

            <!-- Modal de Compartilhamento de Imagens -->
            <div v-if="isShareImagesModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div class="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full" @click.stop>
                    <div class="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
                        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Compartilhar Imagens</h3>
                        <button @click="closeShareImagesModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                            <XIcon class="w-5 h-5" />
                        </button>
                    </div>
                    
                    <div class="p-6 space-y-4">
                        <!-- Informações do agendamento -->
                        <div v-if="shareImagesAppointment" class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                            <h4 class="font-medium text-gray-900 dark:text-white mb-2">Agendamento</h4>
                            <div class="text-sm text-gray-600 dark:text-gray-300">
                                <p><strong>Paciente:</strong> {{ shareImagesAppointment.patient_name }}</p>
                                <p><strong>Exame:</strong> {{ shareImagesAppointment.service_name }}</p>
                                <p><strong>Data:</strong> {{ formatDate(shareImagesAppointment.date) }} às {{ shareImagesAppointment.time?.slice(0,5) }}</p>
                                <p><strong>OS:</strong> {{ shareImagesAppointment.accession_number }}</p>
                            </div>
                        </div>

                        <!-- Formulário de configuração -->
                        <div v-if="!shareResult" class="space-y-4">
                            <div>
                                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Duração do link (horas)
                                </label>
                                <select v-model.number="shareImagesData.duration_hours" class="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                                    <option value="1">1 hora</option>
                                    <option value="6">6 horas</option>
                                    <option value="12">12 horas</option>
                                    <option value="24">24 horas</option>
                                    <option value="48">48 horas</option>
                                    <option value="72">72 horas</option>
                                    <option value="168">7 dias</option>
                                </select>
                            </div>

                            <div>
                                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Descrição (opcional)
                                </label>
                                <textarea v-model="shareImagesData.description" rows="3" class="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Descrição do compartilhamento..."></textarea>
                            </div>

                            <div class="flex justify-end gap-2 pt-4">
                                <button @click="closeShareImagesModal" class="px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
                                    Cancelar
                                </button>
                                <button @click="handleShareImages" class="px-4 py-2 clinic-btn text-white rounded-lg transition-colors flex items-center gap-2">
                                    <ShareIcon class="w-4 h-4" />
                                    Gerar Link
                                </button>
                            </div>
                        </div>

                        <!-- Resultado do compartilhamento -->
                        <div v-if="shareResult" class="space-y-4">
                            <div v-if="shareResult.success" class="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                                <div class="flex items-center gap-2 mb-3">
                                    <CheckIcon class="w-5 h-5 text-green-500" />
                                    <h4 class="font-medium text-green-900 dark:text-green-100">Link gerado com sucesso!</h4>
                                </div>
                                
                                <div class="space-y-3">
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Link de compartilhamento:
                                        </label>
                                        <div class="flex gap-2">
                                            <input :value="shareResult.share_url" readonly class="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg text-gray-900 dark:text-white text-sm font-mono" />
                                            <button @click="copyShareUrl(shareResult.share_url)" class="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                                                <FileText class="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                    
                                    <div class="text-sm text-gray-600 dark:text-gray-400">
                                        <p><strong>Expira em:</strong> {{ new Date(shareResult.expires_at).toLocaleString('pt-BR') }}</p>
                                    </div>
                                    
                                    <!-- QR Code e Botão de Impressão -->
                                    <div v-if="qrCodeDataUrl" class="space-y-3">
                                        <div class="text-center">
                                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                QR Code para acesso:
                                            </label>
                                            <div class="flex justify-center">
                                                <img :src="qrCodeDataUrl" alt="QR Code" class="border border-gray-300 dark:border-gray-600 rounded-lg" />
                                            </div>
                                        </div>
                                        
                                        <div class="flex justify-center">
                                            <button 
                                                @click="printShareDeclaration" 
                                                class="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center gap-2"
                                                :disabled="isGeneratingQR"
                                            >
                                                <Print class="w-4 h-4" />
                                                <span v-if="isGeneratingQR">Gerando...</span>
                                                <span v-else>Imprimir Declaração</span>
                                            </button>
                                        </div>
                                    </div>
                                    
                                    <div v-else-if="isGeneratingQR" class="text-center py-4">
                                        <Loader2 class="w-8 h-8 animate-spin mx-auto text-blue-600" />
                                        <p class="text-sm text-gray-600 dark:text-gray-400 mt-2">Gerando QR Code...</p>
                                    </div>
                                </div>
                            </div>

                            <div v-else class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                                <div class="flex items-center gap-2 mb-2">
                                    <XIcon class="w-5 h-5 text-red-500" />
                                    <h4 class="font-medium text-red-900 dark:text-red-100">Erro ao gerar link</h4>
                                </div>
                                <p class="text-sm text-red-700 dark:text-red-300">{{ shareResult.message }}</p>
                            </div>

                            <div class="flex justify-end gap-2 pt-4">
                                <button @click="closeShareImagesModal" class="px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
                                    Fechar
                                </button>
                                <button v-if="!shareResult.success" @click="handleShareImages" class="px-4 py-2 clinic-btn text-white rounded-lg transition-colors">
                                    Tentar Novamente
                                </button>
                            </div>
                        </div>
                                </div>
        </div>

        <!-- Modal da Ficha do Protocolo removido conforme solicitado -->
    </div>
</template>
    </BaseTemplate>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import BaseTemplate from '../templates/BaseTemplate.vue'
import ScanModal from './ScanModal.vue'
import DocumentsViewer from './DocumentsViewer.vue'
import RecallPatientButton from '@/components/RecallPatientButton.vue'

import apiClient from '@/services/authService';
import { useClinicStore } from '@/stores/settings/clinicStore';
import { useUserStore } from '@/stores/userStore';
import { usePerfilStore } from '@/stores/settings/perfilStore';
import {
    CalendarIcon, ClockIcon, PlusIcon, EditIcon, TrashIcon, CheckIcon, XIcon,
    ChevronLeftIcon, ChevronRightIcon, StethoscopeIcon, ActivityIcon, MoreVerticalIcon,
    ScanIcon, ZapIcon, EyeIcon, UserIcon, FileTextIcon, UploadIcon, SearchIcon,
    IdentificationIcon, DocumentTextIcon, PhoneIcon, ClipboardDocumentListIcon, 
    FileText, DocumentArrowDownIcon, Print, Loader2, ShareIcon, XCircleIcon, Forward, RotateCcwIcon
} from 'lucide-vue-next'
import QRCode from 'qrcode'
import BannerMessage from '@/components/BannerMessage.vue';


// --- ESTADO REATIVO ---
const agendas = ref([])
const appointments = ref([])
const selectedAgenda = ref(null)
// Função para obter a data atual no fuso horário local
const getCurrentDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

// Função para formatar data para YYYY-MM-DD sem problemas de fuso horário
const formatDateToISO = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

// Função para adicionar/subtrair dias de uma data
const addDaysToDate = (dateString, days) => {
    const date = new Date(dateString + 'T00:00:00');
    date.setDate(date.getDate() + days);
    return formatDateToISO(date);
}

const selectedDate = ref(getCurrentDate())
const filterStatus = ref('all')
const selectedAppointment = ref(null)
const contextMenu = ref({ show: false, x: 0, y: 0, actions: [], data: null })
const availableTimes = ref([]);
const isLoading = ref(true)
const error = ref(null)
const isCreateModalOpen = ref(false)
const isCreatePatientModalOpen = ref(false)
const isCreatingFromSpecialSlot = ref(false)

// --- ESTADO PARA MODAIS DE CONFIRMAÇÃO E ANAMNESE ---
const isConfirmationModalOpen = ref(false)
const isAnamnesisModalOpen = ref(false)
const isCancellationModalOpen = ref(false)
const isNotPerformedModalOpen = ref(false)
const isRescheduleModalOpen = ref(false)
const isPatientHistoryModalOpen = ref(false)
// isDeliveryProtocolModalOpen removido conforme solicitado
const isImportModalOpen = ref(false)
const isEditModalOpen = ref(false)
const isScanModalOpen = ref(false)
const isDocumentsViewerOpen = ref(false)
const isShareImagesModalOpen = ref(false)
const isDeleteConfirmationModalOpen = ref(false)
const isDeleteSpecialSlotModalOpen = ref(false)
const recallAppointment = ref(null)

const isCancelRescheduleModalOpen = ref(false)
const cancelRescheduleAppointment = ref(null)

const isUnconfirmModalOpen = ref(false)
const unconfirmModalAppointment = ref(null)
const isUnconfirming = ref(false)

const confirmationAppointment = ref(null)
const cancellationAppointment = ref(null)
const notPerformedAppointment = ref(null)
const rescheduleAppointment = ref(null)
const deleteConfirmationAppointment = ref(null)
const deleteSpecialSlotData = ref(null)
const patientHistoryData = ref(null)
const historyStatusFilter = ref('')
const historyDateFilter = ref('')
// deliveryProtocolData removido conforme solicitado
const importResult = ref(null)
const editAppointment = ref(null)
const shareImagesAppointment = ref(null)
const shareImagesData = ref({
    duration_hours: 24,
    description: ''
})
const shareResult = ref(null)
const qrCodeDataUrl = ref('')
const isGeneratingQR = ref(false)

const confirmationData = ref({
    sisreg_confirmation_key: '',
    doctor_name: '',
    guide_number: '',
    guide_issue_date: '',
    authorization_date: '',
    cid10: '',
    race: '',
    procedencia: ''
})

const cancellationData = ref({
    reason: ''
})

const notPerformedData = ref({
    reason: ''
})

const rescheduleData = ref({
    new_schedule_id: null,
    new_date: '',
    new_time: '',
    reason: ''
})

const editData = ref({
    patient_name: '',
    patient_phone: '',
    patient_email: '',
    race: '',
    service_id: null,
    time: '',
    notes: '',
    procedencia: '',
    motivo_marcacao: '',
    cid10: '',
    requesting_doctor_id: null,
    requesting_doctor_name: '', // Novo campo para o nome do médico solicitante
    internal_doctor_id: null,
    sisreg_id: '',
    insurance_id: null
})

const isConfirming = ref(false)
const isCancelling = ref(false)
const isNotPerforming = ref(false)
const isRescheduling = ref(false)
const requiredAnamneses = ref([])
const hasRequiredAnamnesis = ref(false)

// --- DADOS PARA MODAIS DE CONFIRMAÇÃO E ANAMNESE ---
const users = ref([]) // Para Pacientes
const services = ref([]) // Para Serviços
const insurances = ref([]) // Para Planos
const doctors = ref([]) // Para Médicos (Solicitante e Interno)
const procedenciaOptions = ['Ambulatório', 'Emergência', 'Internação', 'Externo', 'Outro'];
const motivoMarcacaoOptions = ['Primeira Vez', 'Retorno', 'SISREG', 'Judicial', 'Outro'];

// --- VARIÁVEIS PARA PESQUISA DE PACIENTES ---
const patientSearchTerm = ref('')
const isLoadingPatients = ref(false)
const patientSearchTimeout = ref(null)

const userStore = useUserStore();
const clinicStore = useClinicStore();
const userPermissions = computed(() => userStore.permissions || []);
const can = (permission) => {
    return userPermissions.value.includes(permission);
};

const canAddAppointment = computed(() => {
    return can('Schedules.add_appointment');
});

const canImportSisreg = computed(() => {
    return can('Schedules.import_sisreg_schedule');
});

const canAddSpecialSlot = computed(() => {
    return can('Schedules.add_special_slot');
});

// Computed property para filtrar serviços baseado na agenda selecionada
const filteredServices = computed(() => {
    // Se não há agenda selecionada, não mostra nenhum serviço
    if (!selectedAgendaId.value) {
        return [];
    }
    
    // Se os serviços da agenda ainda não foram carregados, aguarda o carregamento
    if (currentScheduleServices.value === null || currentScheduleServices.value === undefined) {
        return [];
    }
    
    // Se a agenda não tem serviços vinculados, retorna lista vazia
    if (currentScheduleServices.value.length === 0) {
        console.log('Agenda sem serviços vinculados - lista vazia');
        return [];
    }
    
    // Filtra apenas os serviços vinculados à agenda atual
    const filtered = services.value.filter(service => {
        return currentScheduleServices.value.some(scheduleService => {
            const serviceId = service.id;
            const scheduleServiceId = scheduleService.id;
            const scheduleServiceServiceId = scheduleService.service_id;
            const scheduleServiceService = scheduleService.service;
            
            return serviceId === scheduleServiceId || 
                   serviceId === scheduleServiceServiceId ||
                   serviceId === scheduleServiceService;
        });
    });
    
    console.log('Serviços filtrados para agenda:', filtered.length);
    return filtered;
});

// Computed property para filtrar convênios baseado no serviço selecionado
const filteredInsurances = computed(() => {
    if (!newAppointment.value.service_id) {
        return [];
    }
    
    const selectedService = services.value.find(s => s.id === newAppointment.value.service_id);
    if (!selectedService || !selectedService.convenios) {
        return insurances.value;
    }
    
    // Se o serviço tem convênios específicos, filtra por eles
    if (selectedService.convenios.length > 0) {
        return insurances.value.filter(insurance => 
            selectedService.convenios.some(conv => conv.id === insurance.id)
        );
    }
    
    // Se não tem convênios específicos, mostra todos
    return insurances.value;
});

const initialNewAppointmentState = {
    patient_id: null,
    service_id: null,
    insurance_id: null,
    procedencia: '',
    requesting_doctor_id: null,
    internal_doctor_id: null,
    is_inpatient: false,
    is_urgent: false,
    motivo_marcacao: '',
    send_preparation_email: false,
    send_preparation_sms: false,
    time: '',
    cid10: '',
    notes: ''
};
const newAppointment = ref({ ...initialNewAppointmentState });
// Adicionar esta linha para controlar campos que devem piscar
const blinkingFields = ref(new Set());
const blinkingPatientFields = ref(new Set()); // Adicionar esta linha

// --- DADOS PARA CADASTRO DE PACIENTE ---
const newPatient = ref({
    name: '',
    social_name: '', // Campo já adicionado - será enviado como string vazia se não preenchido
    birthdate: '',
    gender: '',
    race: '',
    document_type: 'CPF',
    cpf: '',
    cns: '',
    phone: '',
    email: '',
    emergency_contact: '',
    cep: '',
    logradouro: '', // Adicionado: nome completo do logradouro
    logradouro_codigo: '', // Adicionado: código do tipo de logradouro como string
    logradouro_tipo: '', // Adicionado: descrição do tipo de logradouro
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    state_ibge_code: '',
    health_insurance: '',
    insurance_id: '',
    allergies: '',
    medical_conditions: '',
    notes: ''
});

const isCreatingPatient = ref(false);
const selectedLogradouroTipo = ref('');

// --- ESTADO PARA ATUALIZAÇÃO AUTOMÁTICA ---
const isUserInactive = ref(false)
const autoRefreshCountdown = ref(10)
const nextRefreshCountdown = ref(15)
const autoRefreshInterval = ref(null)
const inactivityTimer = ref(null)
const countdownTimer = ref(null)
const nextRefreshTimer = ref(null)
const isAutoRefreshing = ref(false)
const showInactivityBanner = ref(false)

// --- CONFIGURAÇÕES DE INATIVIDADE ---
const INACTIVITY_TIMEOUT = 30000 // 30 segundos de inatividade
const COUNTDOWN_DURATION = 10 // 10 segundos de contagem regressiva
const AUTO_REFRESH_INTERVAL = 15000 // 15 segundos entre atualizações

// --- ESTADO PARA CONSULTA DE CEP ---
const isConsultingCep = ref(false);
const cepError = ref('');

// --- VARIÁVEIS PARA HORÁRIO ESPECIAL ---
const isSpecialSlotModalOpen = ref(false);
const isCreatingSpecialSlot = ref(false);
const specialSlotData = ref({
    date: '',
    time: '',
    notes: ''
});

const filteredInsurancesForEdit = computed(() => {
    if (!editData.value.service_id) {
        return [];
    }
    
    const selectedService = services.value.find(s => s.id === editData.value.service_id);
    if (!selectedService || !selectedService.convenios) {
        return insurances.value;
    }
    
    // Se o serviço tem convênios específicos, filtra por eles
    if (selectedService.convenios.length > 0) {
        return insurances.value.filter(insurance => 
            selectedService.convenios.some(conv => conv.id === insurance.id)
        );
    }
    
    // Se não tem convênios específicos, mostra todos
    return insurances.value;
});

// --- FUNÇÕES DE BUSCA DE DADOS (APIs) ---
const fetchAgendas = async () => {
    try {
        const response = await apiClient.get('/api/v1/schedules/list/', { params: { page_size: 50 } });
        agendas.value = response.data.results.map(agenda => ({
            id: parseInt(agenda.id), // Garante que seja número
            name: agenda.name,
            location: agenda.doctor.name,
            allow_attach_after_report: agenda.allow_attach_after_report === true,
            allow_attach_after_attendance: agenda.allow_attach_after_attendance === true,
            allow_unconfirm: agenda.allow_unconfirm === true,
            worklist_generation_mode: agenda.worklist_generation_mode || null,
        }));
        
        if (agendas.value.length > 0) {
            // Tenta carregar a agenda salva do localStorage
            const savedAgendaId = localStorage.getItem('selectedAgendaId');
            const parsedSavedId = savedAgendaId ? parseInt(savedAgendaId) : null;
            
            // Se houver uma agenda salva válida e ela existir na lista, usa ela
            const savedAgendaExists = parsedSavedId && agendas.value.some(agenda => agenda.id === parsedSavedId);
            
            if (savedAgendaExists) {
                selectedAgenda.value = parsedSavedId;
                console.log('Agenda recuperada do localStorage:', parsedSavedId);
            } else if (!selectedAgenda.value) {
                // Caso contrário, usa a primeira agenda como fallback
                const firstAgendaId = parseInt(agendas.value[0].id);
                if (!isNaN(firstAgendaId) && firstAgendaId > 0) {
                    selectedAgenda.value = firstAgendaId;
                    localStorage.setItem('selectedAgendaId', firstAgendaId.toString());
                    console.log('Agenda selecionada automaticamente e salva:', firstAgendaId);
                }
            }
        }
    } catch (err) {
        console.error("Erro ao buscar agendas:", err);
        const errorMessage = err.response?.data?.detail || err.response?.data?.message || 'Erro desconhecido';
        showBanner('error', errorMessage);
    }
};

const fetchAppointments = async () => {
    // Não executa se o modal de importação estiver aberto
    if (isImportModalOpen.value) {
        console.log('fetchAppointments: Modal de importação aberto, requisição cancelada');
        return;
    }
    
    console.log('fetchAppointments chamada com:', {
        selectedAgenda: selectedAgenda.value,
        selectedAgendaId: selectedAgendaId.value,
        selectedDate: selectedDate.value,
        tipoSelectedAgenda: typeof selectedAgenda.value
    });
    
    if (!isValidScheduleId() || !selectedDate.value) {
        console.warn('fetchAppointments: selectedAgendaId inválido ou selectedDate faltando', {
            selectedAgendaId: selectedAgendaId.value,
            selectedDate: selectedDate.value
        });
        appointments.value = [];
        isLoading.value = false;
        return;
    }
    isLoading.value = true;
    error.value = null;
    try {
        const response = await apiClient.get('/api/v1/schedules/appointments/', {
            params: { schedule_id: selectedAgendaId.value, date: selectedDate.value }
        });
        appointments.value = response.data;
        console.log('Agendamentos recebidos:', appointments.value.length);
    } catch (err) {
        console.error("Erro ao buscar agendamentos:", err);
        console.error("Detalhes do erro:", err.response?.data);
        
        // Verifica se é erro de parsing de inteiro
        if (err.response?.data?.detail?.some?.(detail => detail.type === 'int_parsing' && detail.loc?.includes('schedule_id'))) {
            console.error('ERRO DE PARSING: schedule_id inválido na URL:', selectedAgendaId.value);
            showBanner('error', `ID da agenda inválido (${selectedAgendaId.value}). Recarregue a página.`);
        } else {
            const errorMessage = err.response?.data?.detail || err.response?.data?.message || 'Erro desconhecido';
            showBanner('error', errorMessage);
        }
        
        appointments.value = [];
    } finally {
        isLoading.value = false;
    }
};

const fetchAvailableTimes = async (date) => {
    // Não executa se o modal de importação estiver aberto
    if (isImportModalOpen.value) {
        console.log('fetchAvailableTimes: Modal de importação aberto, requisição cancelada');
        return;
    }
    
    const dateToUse = date || selectedDate.value;
    if (!isValidScheduleId() || !dateToUse) {
        console.warn('fetchAvailableTimes: selectedAgendaId inválido ou data faltando', {
            selectedAgendaId: selectedAgendaId.value,
            dateToUse: dateToUse
        });
        availableTimes.value = [];
        return;
    }
    try {
        if (!isValidScheduleId()) {
            console.error('fetchAvailableTimes: selectedAgendaId inválido:', selectedAgendaId.value);
            availableTimes.value = [];
            return;
        }
        
        const res = await apiClient.get(
            `/api/v1/schedules/${selectedAgendaId.value}/available-times/`,
            { params: { date: dateToUse } }
        );
        console.log('Horários disponíveis carregados:', res.data);
        availableTimes.value = (res.data.times || []).map(time => normalizarHorario(time));
        console.log('Horários disponíveis mapeados:', availableTimes.value);
    } catch (err) {
        console.error("Erro ao buscar horários:", err);
        console.error("Detalhes do erro:", err.response?.data);
        availableTimes.value = [];
    }
};

// Funções para carregar dados dos novos selects
const fetchUsers = async (gender = null, search = null) => {
    try {
        isLoadingPatients.value = true;
        const params = { page_size: 200 };
        if (gender) params.gender = gender;
        if (search && search.trim()) params.search = search.trim();
        const res = await apiClient.get('/api/v1/patients/', { params });
        users.value = res.data.results.map(u => {
            let documentLabel = 'Documento não informado';
            if (u.cpf) {
                // Formata CPF se não estiver formatado
                const cpfClean = u.cpf.replace(/\D/g, '');
                if (cpfClean.length === 11) {
                    const formattedCpf = cpfClean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
                    documentLabel = `CPF: ${formattedCpf}`;
                } else {
                    documentLabel = `CPF: ${u.cpf}`;
                }
            } else if (u.cns) {
                documentLabel = `CNS: ${u.cns}`;
            } else if (u.document_type === 'GEN' || (!u.cpf && !u.cns)) {
                documentLabel = `Genérico: GEN-${String(u.id).padStart(6, '0')}`;
            }
            
            return {
                id: u.id,
                label: u.social_name || u.name || 'Nome não informado',
                gender: u.gender,
                document: documentLabel,
                birthdate: u.birthdate ? formatDate(u.birthdate) : 'Data não informada'
            };
        });
    } catch (err) {
        console.error('Erro ao buscar pacientes:', err);
        const errorMessage = err.response?.data?.detail || err.response?.data?.message || 'Erro desconhecido';
        showBanner('error', errorMessage);
        users.value = [];
    } finally {
        isLoadingPatients.value = false;
    }
};

// Função para pesquisa com debounce
const debouncedPatientSearch = () => {
    // Limpa o timeout anterior
    if (patientSearchTimeout.value) {
        clearTimeout(patientSearchTimeout.value);
    }
    
    // Define um novo timeout para executar a pesquisa após 300ms
    patientSearchTimeout.value = setTimeout(async () => {
        // Se o campo de pesquisa estiver vazio, limpa a lista de pacientes
        if (!patientSearchTerm.value.trim()) {
            users.value = [];
            return;
        }
        
        // Busca pacientes com o termo de pesquisa atual
        let gender = null;
        if (newAppointment.value.service_id) {
            const selectedService = services.value.find(s => s.id === newAppointment.value.service_id);
            if (selectedService && selectedService.sexo && selectedService.sexo !== 'ambos') {
                gender = selectedService.sexo === 'feminino' ? 'F' : selectedService.sexo === 'masculino' ? 'M' : null;
            }
        }
        await fetchUsers(gender, patientSearchTerm.value);
    }, 300);
};

// Função para fechar o modal de criação e limpar a pesquisa
const closeCreateModal = () => {
    isCreateModalOpen.value = false;
    isCreatingFromSpecialSlot.value = false;
    patientSearchTerm.value = '';
    // Limpa o timeout de pesquisa se existir
    if (patientSearchTimeout.value) {
        clearTimeout(patientSearchTimeout.value);
        patientSearchTimeout.value = null;
    }
    // Limpa a lista de pacientes quando o modal é fechado
    users.value = [];
};

// Função para pesquisa imediata quando o usuário pressiona Enter
const performImmediateSearch = async () => {
    // Limpa o timeout de pesquisa se existir
    if (patientSearchTimeout.value) {
        clearTimeout(patientSearchTimeout.value);
        patientSearchTimeout.value = null;
    }
    
    // Se o campo de pesquisa estiver vazio, limpa a lista de pacientes
    if (!patientSearchTerm.value.trim()) {
        users.value = [];
        return;
    }
    
    // Busca pacientes imediatamente
    let gender = null;
    if (newAppointment.value.service_id) {
        const selectedService = services.value.find(s => s.id === newAppointment.value.service_id);
        if (selectedService && selectedService.sexo && selectedService.sexo !== 'ambos') {
            gender = selectedService.sexo === 'feminino' ? 'F' : selectedService.sexo === 'masculino' ? 'M' : null;
        }
    }
    await fetchUsers(gender, patientSearchTerm.value);
};

const fetchServices = async () => {
    try {
        const res = await apiClient.get('/api/v1/services/list/?page_size=100');
        console.log('Serviços carregados:', res.data.results);
        services.value = res.data.results.map(s => ({
            id: s.id,
            label: s.descricao,
            sexo: s.sexo // Adicionando o campo sexo
        }));
        console.log('Serviços mapeados:', services.value);
    } catch (err) {
        console.error('Erro ao buscar serviços:', err);
        const errorMessage = err.response?.data?.detail || err.response?.data?.message || 'Erro desconhecido';
        showBanner('error', errorMessage);
    }
};

const fetchInsurances = async () => {
    try {
        console.log('🔄 Buscando convênios...');
        const res = await apiClient.get('/api/v1/insurances/list/?page_size=100'); // Rota de planos/convênios
        console.log('📥 Convênios recebidos:', res.data);
        insurances.value = res.data.results.map(i => ({
            id: i.id,
            label: i.name // Usando 'label' para manter consistência com o template
        }));
        console.log('✅ Convênios mapeados:', insurances.value);
        
        // Se houver apenas 1 convênio, preenche automaticamente
        if (insurances.value.length === 1) {
            newPatient.value.health_insurance = insurances.value[0].label;
            console.log('🎯 Convênio único preenchido automaticamente:', insurances.value[0].label);
        }
    } catch (err) {
        console.error('❌ Erro ao buscar planos:', err);
        const errorMessage = err.response?.data?.detail || err.response?.data?.message || 'Erro ao carregar convênios';
        showBanner('error', errorMessage);
        // Em caso de erro, deixa a lista vazia para não quebrar a interface
        insurances.value = [];
    }
};

const fetchDoctors = async () => {
    try {
        const res = await apiClient.get('/api/v1/doctors/list/?page_size=100'); // Rota de médicos
        console.log('Médicos carregados:', res.data.results);
        doctors.value = res.data.results.map(d => ({
            id: d.id,
            name: d.name
        }));
        console.log('Médicos mapeados:', doctors.value);
    } catch (err) {
        console.error('Erro ao buscar médicos:', err);
        const errorMessage = err.response?.data?.detail || err.response?.data?.message || 'Erro desconhecido';
        showBanner('error', errorMessage);
    }
};

// --- CICLO DE VIDA ---
onMounted(async () => {
    // Inicializa o clinicstore
    clinicStore.initialize();
    
    isLoading.value = true;
    
    // Carrega dados básicos necessários
    const promises = [
        fetchAgendas(),
        fetchInsurances(), // Carrega convênios desde o início
        fetchServices(),   // Carrega serviços desde o início
        fetchDoctors()     // Carrega médicos desde o início
    ];
    
    await Promise.all(promises);
    isLoading.value = false;
    document.addEventListener('click', hideContextMenu);
    
    // Configura os listeners de atividade do usuário
    setupActivityListeners();
    
    // Inicia o timer de inatividade
    resetInactivityTimer();
});

onUnmounted(() => {
    document.removeEventListener('click', hideContextMenu);
    
    // Limpa os listeners de atividade
    cleanupActivityListeners();
    
    // Para a atualização automática
    stopAutoRefresh();
    
    // Limpa todos os timers
    if (inactivityTimer.value) {
        clearTimeout(inactivityTimer.value);
    }
});

watch([selectedAgenda, selectedDate], () => {
    // Não executa se o modal de importação estiver aberto
    if (isImportModalOpen.value) {
        console.log('Watch [selectedAgenda, selectedDate]: Modal de importação aberto, requisição cancelada');
        return;
    }
    
    // Valida se selectedAgenda é um inteiro válido antes de fazer as chamadas
    if (!selectedAgenda.value || !selectedDate.value) {
        console.log('Valores não disponíveis para buscar dados');
        return;
    }
    
    let scheduleId;
    if (typeof selectedAgenda.value === 'string') {
        scheduleId = parseInt(selectedAgenda.value.trim());
    } else {
        scheduleId = parseInt(selectedAgenda.value);
    }
    
    if (isNaN(scheduleId) || scheduleId <= 0) {
        console.warn('ID de agenda inválido no watch:', selectedAgenda.value, 'Tipo:', typeof selectedAgenda.value);
        return;
    }
    
    console.log('Watch: buscando dados para agenda:', scheduleId, 'data:', selectedDate.value);
    fetchAppointments();
    // fetchAvailableTimes();
});



// Watcher para o modal de importação - reinicia o timer quando o modal é fechado
watch(isImportModalOpen, (isOpen) => {
    if (!isOpen) {
        // Quando o modal é fechado, reinicia o timer de inatividade
        resetInactivityTimer();
    }
});

// Watcher para o termo de pesquisa de pacientes - limpa o paciente selecionado quando a pesquisa muda
watch(patientSearchTerm, () => {
    // Limpa o paciente selecionado quando o usuário digita na pesquisa
    newAppointment.value.patient_id = null;
});

// Watcher para quando a agenda é alterada - recarrega os serviços da agenda
watch(selectedAgenda, async (newAgendaId) => {
    if (newAgendaId) {
        // Salva a agenda selecionada no localStorage
        localStorage.setItem('selectedAgendaId', newAgendaId.toString());
        console.log('Agenda salva no localStorage:', newAgendaId);
        
        // Limpa os serviços da agenda anterior
        currentScheduleServices.value = [];
        
        // Limpa qualquer serviço selecionado no modal se estiver aberto
        if (isCreateModalOpen.value) {
            newAppointment.value.service_id = null;
        }
        
        // Carrega os serviços da nova agenda
        try {
            const response = await apiClient.get(`/api/v1/schedules/${newAgendaId}/`);
            const scheduleData = response.data;
            currentScheduleServices.value = scheduleData.services || [];
            console.log('Serviços da nova agenda carregados:', currentScheduleServices.value.length);
            
            if (currentScheduleServices.value.length === 0) {
                console.log('⚠️ Agenda selecionada não possui serviços vinculados');
            }
        } catch (error) {
            console.error('Erro ao carregar serviços da nova agenda:', error);
            // Em caso de erro, deixa a lista vazia
            currentScheduleServices.value = [];
        }
    }
});

// --- AÇÕES DO MODAL E AGENDAMENTO ---
const openCreateModal = async () => {
    newAppointment.value = { ...initialNewAppointmentState };
    isCreatingFromSpecialSlot.value = false;
    patientSearchTerm.value = '';

    const promises = [];
    if (services.value.length === 0) promises.push(fetchServices());
    if (insurances.value.length === 0) promises.push(fetchInsurances());
    if (doctors.value.length === 0) promises.push(fetchDoctors());

    // Carrega serviços antes para saber o sexo permitido
    if (promises.length > 0) {
        await Promise.all(promises);
    }

    // Carrega os serviços da agenda atual se não estiverem carregados
    if (selectedAgendaId.value && currentScheduleServices.value.length === 0) {
        try {
            const response = await apiClient.get(`/api/v1/schedules/${selectedAgendaId.value}/`);
            const scheduleData = response.data;
            currentScheduleServices.value = scheduleData.services || [];
            console.log('Serviços da agenda carregados:', currentScheduleServices.value.length);
        } catch (error) {
            console.error('Erro ao carregar serviços da agenda:', error);
            // Em caso de erro, deixa a lista vazia para forçar o fallback
            currentScheduleServices.value = [];
        }
    }

    // Busca pacientes conforme sexo do serviço selecionado (se já houver)
    let gender = null;
    if (newAppointment.value.service_id) {
        const selectedService = services.value.find(s => s.id === newAppointment.value.service_id);
        if (selectedService && selectedService.sexo && selectedService.sexo !== 'ambos') {
            gender = selectedService.sexo === 'feminino' ? 'F' : selectedService.sexo === 'masculino' ? 'M' : null;
        }
    }
    await fetchUsers(gender, patientSearchTerm.value);

    isCreateModalOpen.value = true;
};

const openCreateModalFromSpecialSlot = async (specialSlot) => {
    newAppointment.value = { ...initialNewAppointmentState };
    
    // Pré-preenche os dados do horário especial
    newAppointment.value.date = specialSlot.date;
    newAppointment.value.time = specialSlot.time;
    
    // Marca que está criando a partir de um horário especial
    isCreatingFromSpecialSlot.value = true;
    patientSearchTerm.value = '';

    const promises = [];
    if (services.value.length === 0) promises.push(fetchServices());
    if (insurances.value.length === 0) promises.push(fetchInsurances());
    if (doctors.value.length === 0) promises.push(fetchDoctors());

    // Carrega serviços antes para saber o sexo permitido
    if (promises.length > 0) {
        await Promise.all(promises);
    }

    // Carrega os serviços da agenda atual se não estiverem carregados
    if (selectedAgendaId.value && currentScheduleServices.value.length === 0) {
        try {
            const response = await apiClient.get(`/api/v1/schedules/${selectedAgendaId.value}/`);
            const scheduleData = response.data;
            currentScheduleServices.value = scheduleData.services || [];
            console.log('Serviços da agenda carregados:', currentScheduleServices.value.length);
        } catch (error) {
            console.error('Erro ao carregar serviços da agenda:', error);
            // Em caso de erro, deixa a lista vazia para forçar o fallback
            currentScheduleServices.value = [];
        }
    }

    // Busca pacientes conforme sexo do serviço selecionado (se já houver)
    let gender = null;
    if (newAppointment.value.service_id) {
        const selectedService = services.value.find(s => s.id === newAppointment.value.service_id);
        if (selectedService && selectedService.sexo && selectedService.sexo !== 'ambos') {
            gender = selectedService.sexo === 'feminino' ? 'F' : selectedService.sexo === 'masculino' ? 'M' : null;
        }
    }
    await fetchUsers(gender, patientSearchTerm.value);

    isCreateModalOpen.value = true;
};

// --- FUNÇÕES PARA HORÁRIO ESPECIAL ---
const openSpecialSlotModal = () => {
    // Define a data atual como padrão
    specialSlotData.value.date = selectedDate.value;
    specialSlotData.value.time = '';
    specialSlotData.value.notes = '';
    isSpecialSlotModalOpen.value = true;
};

const closeSpecialSlotModal = () => {
    isSpecialSlotModalOpen.value = false;
    specialSlotData.value = {
        date: '',
        time: '',
        notes: ''
    };
};

const handleCreateSpecialSlot = async () => {
    if (!isValidScheduleId()) {
        showBanner('error', 'Agenda inválida.');
        return;
    }

    isCreatingSpecialSlot.value = true;

    try {
        await apiClient.post(
            `/api/v1/schedules/${selectedAgendaId.value}/special-slots/`,
            specialSlotData.value
        );

        showBanner('success', 'Horário especial criado com sucesso!');
        closeSpecialSlotModal();
        
        // Recarrega os agendamentos para mostrar o novo horário disponível
        await fetchAppointments();
        
    } catch (err) {
        console.error('Erro ao criar horário especial:', err);
        const errorMessage = err.response?.data?.detail || err.response?.data?.message || 'Erro desconhecido';
        showBanner('error', `Erro ao criar horário especial: ${errorMessage}`);
    } finally {
        isCreatingSpecialSlot.value = false;
    }
};

// --- FUNÇÕES PARA CADASTRO DE PACIENTE ---
const openCreatePatientModal = async () => {
    // Carrega os convênios se ainda não foram carregados
    if (insurances.value.length === 0) {
        await fetchInsurances();
    }
    
    // Limpa o formulário
    newPatient.value = {
        name: '',
        birthdate: '',
        gender: '',
        race: '',
        document_type: 'CPF',
        cpf: '',
        cns: '',
        phone: '',
        email: '',
        emergency_contact: '',
        cep: '',
        street: '',
        number: '',
        complement: '',
        city: '',
        state: '',
        health_insurance: '',
        insurance_id: '',
        allergies: '',
        medical_conditions: '',
        notes: ''
    };
    
    // Se houver apenas 1 convênio, preenche automaticamente
    if (insurances.value.length === 1) {
        newPatient.value.health_insurance = insurances.value[0].label;
        console.log('🎯 Convênio único preenchido automaticamente no modal:', insurances.value[0].label);
    }
    
    // Limpa os estados de CEP
    cepError.value = '';
    isConsultingCep.value = false;
    isCreatePatientModalOpen.value = true;
};

const resetNewPatient = () => {
    newPatient.value = {
        name: '',
        social_name: '',
        birthdate: '',
        gender: '',
        race: '',
        document_type: 'CPF',
        cpf: '',
        cns: '',
        phone: '',
        email: '',
        emergency_contact: '',
        cep: '',
        logradouro: '',
        logradouro_codigo: '',
        logradouro_tipo: '',
        street: '',
        number: '',
        complement: '',
        neighborhood: '',
        city: '',
        state: '',
        state_ibge_code: '',
        health_insurance: '',
        insurance_id: '',
        allergies: '',
        medical_conditions: '',
        notes: ''
    };
    selectedLogradouroTipo.value = '';
};

const closeCreatePatientModal = () => {
    isCreatePatientModalOpen.value = false;
    resetNewPatient();
};

// Função para verificar documentos duplicados
const checkDuplicateDocument = async (documentType, documentValue) => {
    try {
        const cleanDocument = documentValue.replace(/\D/g, '');
        
        // Buscar pacientes com o mesmo documento
        const response = await apiClient.get('/api/v1/patients/', {
            params: {
                search: cleanDocument,
                page_size: 1000 // Buscar todos para verificar duplicatas
            }
        });
        
        const patients = response.data.results || [];
        
        // Verificar se existe paciente com o mesmo documento
        const duplicatePatient = patients.find(patient => {
            if (documentType === 'CPF') {
                const patientCpf = patient.cpf ? patient.cpf.replace(/\D/g, '') : '';
                return patientCpf === cleanDocument;
            } else if (documentType === 'CNS') {
                const patientCns = patient.cns ? patient.cns.replace(/\D/g, '') : '';
                return patientCns === cleanDocument;
            }
            return false;
        });
        
        return duplicatePatient;
    } catch (error) {
        console.error('Erro ao verificar documento duplicado:', error);
        return null;
    }
};

const handleCreatePatient = async () => {
    // Validação dos campos obrigatórios
    const requiredFields = ['name', 'birthdate', 'gender', 'phone'];
    const missingFields = requiredFields.filter(field => !newPatient.value[field]);

    if (missingFields.length > 0) {
        // Limpar campos piscando anteriores
        blinkingPatientFields.value.clear();
        
        // Adicionar campos faltantes para piscar
        missingFields.forEach(field => blinkingPatientFields.value.add(field));
        
        // Parar de piscar após 3 segundos
        setTimeout(() => {
            blinkingPatientFields.value.clear();
        }, 3000);
        
        showBanner('error', 'Por favor, preencha todos os campos obrigatórios (*)');
        return;
    }

    // Limpar campos piscando se tudo estiver válido
    blinkingPatientFields.value.clear();

    // Validação de documento
    if (newPatient.value.document_type === 'CPF' && newPatient.value.cpf) {
        const cpfClean = newPatient.value.cpf.replace(/\D/g, '');
        
        // Verifica se é um CPF conhecido como inválido
        if (newPatient.value.cpf === '000.000.000-00' || cpfClean === '00000000000') {
            showBanner('error', 'CPF inválido.');
            return;
        }
        
        // Validação completa de CPF
        if (!validateCpf(cpfClean)) {
            showBanner('error', 'CPF inválido. Verifique os números digitados.');
            return;
        }
        
        // NOVA VALIDAÇÃO: Verificar se CPF já está cadastrado
        const duplicatePatient = await checkDuplicateDocument('CPF', newPatient.value.cpf);
        if (duplicatePatient) {
            showBanner('error', `CPF já cadastrado para o paciente: ${duplicatePatient.name}`);
            return;
        }
    }

    if (newPatient.value.document_type === 'CNS' && newPatient.value.cns) {
        // Validação básica de formato de CNS
        const cnsClean = newPatient.value.cns.replace(/\D/g, '');
        if (cnsClean.length !== 15) {
            showBanner('error', 'CNS deve ter 15 dígitos.');
            return;
        }
        
        // NOVA VALIDAÇÃO: Verificar se CNS já está cadastrado
        const duplicatePatient = await checkDuplicateDocument('CNS', newPatient.value.cns);
        if (duplicatePatient) {
            showBanner('error', `CNS já cadastrado para o paciente: ${duplicatePatient.name}`);
            return;
        }
    }

    // Para tipo genérico, não precisa validar documento
    if (newPatient.value.document_type === 'GEN') {
        // Limpa campos de CPF e CNS para garantir que não sejam enviados
        newPatient.value.cpf = '';
        newPatient.value.cns = '';
    }

    // Validação aprimorada de telefone
    const phoneClean = newPatient.value.phone.replace(/\D/g, '');
    if (phoneClean.length < 10 || phoneClean.length > 11) {
        showBanner('error', 'Telefone deve ter 10 ou 11 dígitos.');
        return;
    }
    
    // Validação adicional para telefones com padrões inválidos
    if (phoneClean.length === 10 && !phoneClean.startsWith('1') && !phoneClean.startsWith('2') && !phoneClean.startsWith('3') && !phoneClean.startsWith('4') && !phoneClean.startsWith('5')) {
        // Para telefones fixos (10 dígitos), o terceiro dígito deve ser 2, 3, 4 ou 5
        const thirdDigit = phoneClean.charAt(2);
        if (!['2', '3', '4', '5'].includes(thirdDigit)) {
            showBanner('warning', 'Formato de telefone pode estar incorreto. Verifique o número.');
        }
    }
    
    if (phoneClean.length === 11) {
        // Para celulares (11 dígitos), o terceiro dígito deve ser 9
        const thirdDigit = phoneClean.charAt(2);
        if (thirdDigit !== '9') {
            showBanner('warning', 'Formato de celular pode estar incorreto. Verifique o número.');
        }
    }

    // Validação de email se fornecido
    if (newPatient.value.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newPatient.value.email)) {
        showBanner('error', 'Email inválido.');
        return;
    }

    isCreatingPatient.value = true;

    try {
        // Garantir que todos os campos obrigatórios estejam presentes
        const patientPayload = {
            ...newPatient.value,
            social_name: newPatient.value.social_name || '', // Garantir que não seja null/undefined
            logradouro: newPatient.value.logradouro || newPatient.value.street, // Garantir que logradouro seja preenchido
            logradouro_codigo: newPatient.value.logradouro_codigo || '', // Garantir que seja string vazia se não preenchido
            logradouro_tipo: newPatient.value.logradouro_tipo || '' // Garantir que não seja null/undefined
        };
        
        const response = await apiClient.post('/api/v1/patients/', patientPayload);
        const createdPatient = response.data;

        // Adiciona o novo paciente à lista de usuários
        let documentLabel = 'Documento não informado';
        if (createdPatient.cpf) {
            // Formata CPF se não estiver formatado
            const cpfClean = createdPatient.cpf.replace(/\D/g, '');
            if (cpfClean.length === 11) {
                const formattedCpf = cpfClean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
                documentLabel = `CPF: ${formattedCpf}`;
            } else {
                documentLabel = `CPF: ${createdPatient.cpf}`;
            }
        } else if (createdPatient.cns) {
            documentLabel = `CNS: ${createdPatient.cns}`;
        } else if (createdPatient.document_type === 'GEN' || (!createdPatient.cpf && !createdPatient.cns)) {
            documentLabel = `Genérico: GEN-${String(createdPatient.id).padStart(6, '0')}`;
        }
        
        const newUserOption = {
            id: createdPatient.id,
            label: createdPatient.social_name || createdPatient.name,
            gender: createdPatient.gender,
            document: documentLabel,
            birthdate: createdPatient.birthdate ? formatDate(createdPatient.birthdate) : 'Data não informada'
        };
        users.value.push(newUserOption);

        // Seleciona automaticamente o paciente recém-cadastrado
        newAppointment.value.patient_id = createdPatient.id;

        // Fecha o modal de cadastro
        closeCreatePatientModal();

        showBanner('success', `Paciente ${createdPatient.name} cadastrado com sucesso!`);
    } catch (err) {
        console.error('Erro ao cadastrar paciente:', err);
        const errorMessage = err.response?.data?.detail || err.response?.data?.message || 'Erro desconhecido';
        showBanner('error', `Erro ao cadastrar paciente: ${errorMessage}`);
    } finally {
        isCreatingPatient.value = false;
    }
};

// --- FUNÇÕES DE FORMATAÇÃO ---
const formatarCep = (cep) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length <= 5) {
        return cepLimpo;
    }
    return cepLimpo.replace(/^(\d{5})(\d)/, '$1-$2');
};

const formatarTelefone = (telefone) => {
    const telLimpo = telefone.replace(/\D/g, '');
    
    // Limita a 11 dígitos
    const telTruncated = telLimpo.substring(0, 11);
    
    if (telTruncated.length <= 2) {
        return telTruncated;
    } else if (telTruncated.length <= 6) {
        return `(${telTruncated.slice(0, 2)}) ${telTruncated.slice(2)}`;
    } else if (telTruncated.length <= 10) {
        return `(${telTruncated.slice(0, 2)}) ${telTruncated.slice(2, 6)}-${telTruncated.slice(6)}`;
    } else {
        return `(${telTruncated.slice(0, 2)}) ${telTruncated.slice(2, 7)}-${telTruncated.slice(7, 11)}`;
    }
};

// Função para formatar e-mail (converte para minúsculas)
const formatarEmail = (email) => {
    return email.toLowerCase();
};

// Função para normalizar formato de horário (remove segundos se houver)
const normalizarHorario = (time) => {
    if (!time) return '';
    // Se o horário tem segundos (formato HH:MM:SS), remove os segundos
    if (time.includes(':') && time.split(':').length === 3) {
        return time.substring(0, 5); // Retorna apenas HH:MM
    }
    return time;
};

// Função para formatar nome em maiúsculas
const formatPatientName = () => {
    if (newPatient.value.name) {
        newPatient.value.name = newPatient.value.name.toUpperCase();
    }
};

// Função para lidar com mudança no tipo de documento
const onDocumentTypeChange = () => {
    // Limpa os campos de documento quando muda o tipo
    newPatient.value.cpf = '';
    newPatient.value.cns = '';
};

const formatarCpf = (cpf) => {
    const cpfLimpo = cpf.replace(/\D/g, '');
    
    // Limita a 11 dígitos
    const cpfTruncated = cpfLimpo.substring(0, 11);
    
    let formatted = '';
    if (cpfTruncated.length <= 3) {
        formatted = cpfTruncated;
    } else if (cpfTruncated.length <= 6) {
        formatted = `${cpfTruncated.slice(0, 3)}.${cpfTruncated.slice(3)}`;
    } else if (cpfTruncated.length <= 9) {
        formatted = `${cpfTruncated.slice(0, 3)}.${cpfTruncated.slice(3, 6)}.${cpfTruncated.slice(6)}`;
    } else {
        formatted = `${cpfTruncated.slice(0, 3)}.${cpfTruncated.slice(3, 6)}.${cpfTruncated.slice(6, 9)}-${cpfTruncated.slice(9, 11)}`;
    }
    
    // Valida CPF quando tem 11 dígitos
    if (cpfTruncated.length === 11) {
        const isValid = validateCpf(cpfTruncated);
        if (!isValid) {
            // Você pode adicionar uma indicação visual de erro aqui
            console.warn('CPF inválido:', formatted);
        }
    }
    
    return formatted;
};

// Função para validar CPF
const validateCpf = (cpf) => {
    // Remove caracteres não numéricos
    cpf = cpf.replace(/\D/g, '');
    
    // Verifica se tem 11 dígitos
    if (cpf.length !== 11) return false;
    
    // Verifica se todos os dígitos são iguais
    if (/^(\d)\1{10}$/.test(cpf)) return false;
    
    // Validação do primeiro dígito verificador
    let sum = 0;
    for (let i = 0; i < 9; i++) {
        sum += parseInt(cpf.charAt(i)) * (10 - i);
    }
    let remainder = sum % 11;
    let digit1 = remainder < 2 ? 0 : 11 - remainder;
    
    if (parseInt(cpf.charAt(9)) !== digit1) return false;
    
    // Validação do segundo dígito verificador
    sum = 0;
    for (let i = 0; i < 10; i++) {
        sum += parseInt(cpf.charAt(i)) * (11 - i);
    }
    remainder = sum % 11;
    let digit2 = remainder < 2 ? 0 : 11 - remainder;
    
    return parseInt(cpf.charAt(10)) === digit2;
};

// Função para validar campo de CPF no formulário
const validateCpfField = () => {
    if (!newPatient.value.cpf) return;
    
    const cpfClean = newPatient.value.cpf.replace(/\D/g, '');
    
    if (cpfClean.length === 11 && !validateCpf(cpfClean)) {
        blinkingPatientFields.value.add('cpf');
        showBanner('warning', 'CPF inválido. Verifique os números digitados.');
        
        // Remove o destaque após 3 segundos
        setTimeout(() => {
            blinkingPatientFields.value.delete('cpf');
        }, 3000);
    } else {
        blinkingPatientFields.value.delete('cpf');
    }
};

// Função para validar campo de telefone
const validatePhoneField = () => {
    if (!newPatient.value.phone) return;
    
    const phoneClean = newPatient.value.phone.replace(/\D/g, '');
    
    // Verifica se o telefone tem o número correto de dígitos
    if (phoneClean.length > 0 && (phoneClean.length < 10 || phoneClean.length > 11)) {
        blinkingPatientFields.value.add('phone');
        showBanner('warning', 'Telefone deve ter 10 ou 11 dígitos.');
        
        // Remove o destaque após 3 segundos
        setTimeout(() => {
            blinkingPatientFields.value.delete('phone');
        }, 3000);
        return;
    }
    
    // Validação adicional para telefones com padrões inválidos
    if (phoneClean.length === 10) {
        // Para telefones fixos (10 dígitos), o terceiro dígito deve ser 2, 3, 4 ou 5
        const thirdDigit = phoneClean.charAt(2);
        if (!['2', '3', '4', '5'].includes(thirdDigit)) {
            showBanner('warning', 'Formato de telefone pode estar incorreto. Verifique o número.');
        }
    } else if (phoneClean.length === 11) {
        // Para celulares (11 dígitos), o terceiro dígito deve ser 9
        const thirdDigit = phoneClean.charAt(2);
        if (thirdDigit !== '9') {
            showBanner('warning', 'Formato de celular pode estar incorreto. Verifique o número.');
        }
    }
    
    blinkingPatientFields.value.delete('phone');
};

const formatarCns = (cns) => {
    const cnsLimpo = cns.replace(/\D/g, '');
    if (cnsLimpo.length <= 3) {
        return cnsLimpo;
    } else if (cnsLimpo.length <= 7) {
        return `${cnsLimpo.slice(0, 3)} ${cnsLimpo.slice(3)}`;
    } else if (cnsLimpo.length <= 11) {
        return `${cnsLimpo.slice(0, 3)} ${cnsLimpo.slice(3, 7)} ${cnsLimpo.slice(7)}`;
    } else {
        return `${cnsLimpo.slice(0, 3)} ${cnsLimpo.slice(3, 7)} ${cnsLimpo.slice(7, 11)} ${cnsLimpo.slice(11, 15)}`;
    }
};

// --- MAPA DE LOGRADOUROS ---
const logradouroMap = {
    1: "Rua", 2: "Avenida", 3: "Praça", 4: "Travessa", 5: "Alameda", 25: "Estrada", 7: "Rodovia", 8: "Viela", 9: "Beco", 10: "Largo", 11: "Parque", 12: "Jardim", 13: "Condomínio", 14: "Loteamento", 15: "Conjunto", 16: "Quadra", 17: "Lote", 18: "Chácara", 19: "Fazenda", 20: "Sítio", 21: "Colônia", 22: "Zona", 23: "Setor", 24: "Área", 26: "Caminho", 27: "Passagem", 28: "Pátio", 29: "Recanto", 30: "Residencial", 31: "Vila", 32: "Reserva", 33: "Trevo", 34: "Acesso", 35: "Anel", 36: "Marginal", 37: "Via", 38: "Viaduto", 39: "Túnel", 40: "Ponte", 41: "Elevado", 42: "Passarela", 43: "Retorno", 44: "Rotatória", 45: "Desvio", 46: "Variante", 47: "Interligação", 48: "Ramal", 49: "Servidão", 50: "Vereda", 51: "Ladeira", 52: "Morro", 53: "Vale", 54: "Serra", 55: "Colina", 56: "Monte", 57: "Alto", 58: "Acesso", 59: "Cais", 60: "Caminho", 61: "Canal", 62: "Colônia", 63: "Condomínio", 64: "Conjunto", 65: "Conjunto Habitacional", 66: "Distrito", 67: "Esplanada", 68: "Estância", 69: "Favela", 70: "Feira", 71: "Galeria", 72: "Granja", 73: "Ilha", 74: "Lago", 75: "Lagoa", 76: "Loteamento", 77: "Mercado", 78: "Núcleo", 79: "Parque Residencial", 80: "Passeio", 81: "Praia", 82: "Prolongamento", 83: "Quadra", 84: "Recanto", 85: "Residencial", 86: "Rua de Ligação", 87: "Rua Particular", 88: "Sítio", 89: "Trecho", 90: "Vale", 91: "Vereda", 92: "Via Coletora", 93: "Via de Acesso", 94: "Via de Pedestre", 95: "Via Expressa", 96: "Via Local", 97: "Via Principal", 98: "Viela", 99: "Vila", 100: "Zona", 101: "Área Especial", 102: "Área de Lazer", 103: "Área Verde", 104: "Balneário", 105: "Campo", 106: "Ciclovia", 107: "Comunidade", 108: "Estacionamento", 109: "Forte", 110: "Gleba", 111: "Horto", 112: "Jardim", 113: "Largo", 114: "Lote", 115: "Morro", 116: "Núcleo Rural", 117: "Parque Municipal", 118: "Passeio Público", 119: "Pátio", 120: "Praça", 121: "Ramal", 122: "Residencial", 123: "Retorno", 124: "Rotatória", 125: "Servidão", 126: "Setor", 127: "Travessa", 128: "Trecho", 129: "Trevo", 130: "Vale", 131: "Vereda", 132: "Via Costeira", 133: "Via de Acesso", 134: "Via Ecológica", 135: "Via Lateral", 136: "Via Marginal", 137: "Via Parque", 138: "Via Rápida", 139: "Viaduto", 140: "Viela", 141: "Vila", 142: "Zona Rural", 143: "Zona Urbana", 144: "Condomínio Fechado", 145: "Loteamento Fechado", 146: "Condomínio Residencial", 147: "Condomínio Horizontal"
};

// --- FUNÇÃO PARA DETECTAR TIPO DE LOGRADOURO ---
const detectLogradouroType = (streetName) => {
    if (!streetName || typeof streetName !== 'string') {
        return { codigo: 1, descricao: "Rua" }; // Default para Rua
    }

    const normalizedStreet = streetName.toLowerCase().trim();
    
    // Mapeamento de prefixos para tipos de logradouro (priorizando início da string)
    const prefixMap = {
        'estrada': 25,
        'estr.': 25,
        'estr': 25,
        'avenida': 2,
        'av.': 2,
        'av': 2,
        'praça': 3,
        'praca': 3,
        'pc': 3,
        'travessa': 4,
        'tv': 4,
        'alameda': 5,
        'al': 5,
        'rodovia': 7,
        'rod.': 7,
        'br': 7,
        'rua': 1,
        'viela': 8,
        'beco': 9,
        'largo': 10,
        'parque': 11,
        'jardim': 12,
        'jd': 12,
        'condomínio': 13,
        'cond': 13,
        'loteamento': 14,
        'lt': 14,
        'conjunto': 15,
        'cj': 15,
        'quadra': 16,
        'qd': 16,
        'lote': 17,
        'chácara': 18,
        'chacara': 18,
        'fazenda': 19,
        'sítio': 20,
        'sitio': 20,
        'colônia': 21,
        'colonia': 21,
        'zona': 22,
        'setor': 23,
        'área': 24,
        'area': 24,
        'caminho': 26,
        'passagem': 27,
        'pátio': 28,
        'patio': 28,
        'recanto': 29,
        'residencial': 30,
        'res': 30,
        'vila': 31,
        'reserva': 32,
        'trevo': 33,
        'acesso': 34,
        'anel': 35,
        'marginal': 36,
        'via': 37,
        'viaduto': 38,
        'túnel': 39,
        'tunel': 39,
        'ponte': 40,
        'elevado': 41,
        'passarela': 42,
        'retorno': 43,
        'rotatória': 44,
        'desvio': 45,
        'variante': 46,
        'interligação': 47,
        'ramal': 48,
        'servidão': 49,
        'vereda': 50,
        'ladeira': 51,
        'morro': 52,
        'vale': 53,
        'serra': 54,
        'colina': 55,
        'monte': 56,
        'alto': 57,
        'cais': 59,
        'canal': 61,
        'distrito': 66,
        'esplanada': 67,
        'estância': 68,
        'estancia': 68,
        'favela': 69,
        'feira': 70,
        'galeria': 71,
        'granja': 72,
        'ilha': 73,
        'lago': 74,
        'lagoa': 75,
        'mercado': 77,
        'núcleo': 78,
        'nucleo': 78,
        'parque residencial': 79,
        'passeio': 80,
        'praia': 81,
        'prolongamento': 82,
        'rua de ligação': 86,
        'rua particular': 87,
        'trecho': 89,
        'via coletora': 92,
        'via de acesso': 93,
        'via de pedestre': 94,
        'via expressa': 95,
        'via local': 96,
        'via principal': 97,
        'via costeira': 132,
        'via ecológica': 134,
        'via lateral': 135,
        'via marginal': 136,
        'via parque': 137,
        'via rápida': 138,
        'zona rural': 142,
        'zona urbana': 143,
        'condomínio fechado': 144,
        'loteamento fechado': 145,
        'condomínio residencial': 146,
        'condomínio horizontal': 147
    };

    // Verifica prefixos no início da string (prioridade máxima)
    for (const [prefix, codigo] of Object.entries(prefixMap)) {
        if (normalizedStreet.startsWith(prefix)) {
            return { codigo, descricao: logradouroMap[codigo] };
        }
    }

    // Se não encontrar prefixo, verifica palavras-chave contidas
    const keywordMap = {
        'estrada': 25,
        'estr.': 25,
        'estr': 25,
        'avenida': 2,
        'av.': 2,
        'av': 2,
        'praça': 3,
        'praca': 3,
        'pc': 3,
        'travessa': 4,
        'tv': 4,
        'alameda': 5,
        'al': 5,
        'rodovia': 7,
        'rod.': 7,
        'br': 7,
        'rua': 1,
        'viela': 8,
        'beco': 9,
        'largo': 10,
        'parque': 11,
        'jardim': 12,
        'jd': 12,
        'condomínio': 13,
        'cond': 13,
        'loteamento': 14,
        'lt': 14,
        'conjunto': 15,
        'cj': 15,
        'quadra': 16,
        'qd': 16,
        'lote': 17,
        'chácara': 18,
        'chacara': 18,
        'fazenda': 19,
        'sítio': 20,
        'sitio': 20,
        'colônia': 21,
        'colonia': 21,
        'zona': 22,
        'setor': 23,
        'área': 24,
        'area': 24,
        'caminho': 26,
        'passagem': 27,
        'pátio': 28,
        'patio': 28,
        'recanto': 29,
        'residencial': 30,
        'res': 30,
        'vila': 31,
        'reserva': 32,
        'trevo': 33,
        'acesso': 34,
        'anel': 35,
        'marginal': 36,
        'via': 37,
        'viaduto': 38,
        'túnel': 39,
        'tunel': 39,
        'ponte': 40,
        'elevado': 41,
        'passarela': 42,
        'retorno': 43,
        'rotatória': 44,
        'desvio': 45,
        'variante': 46,
        'interligação': 47,
        'ramal': 48,
        'servidão': 49,
        'vereda': 50,
        'ladeira': 51,
        'morro': 52,
        'vale': 53,
        'serra': 54,
        'colina': 55,
        'monte': 56,
        'alto': 57,
        'cais': 59,
        'canal': 61,
        'distrito': 66,
        'esplanada': 67,
        'estância': 68,
        'estancia': 68,
        'favela': 69,
        'feira': 70,
        'galeria': 71,
        'granja': 72,
        'ilha': 73,
        'lago': 74,
        'lagoa': 75,
        'mercado': 77,
        'núcleo': 78,
        'nucleo': 78,
        'parque residencial': 79,
        'passeio': 80,
        'praia': 81,
        'prolongamento': 82,
        'rua de ligação': 86,
        'rua particular': 87,
        'trecho': 89,
        'via coletora': 92,
        'via de acesso': 93,
        'via de pedestre': 94,
        'via expressa': 95,
        'via local': 96,
        'via principal': 97,
        'via costeira': 132,
        'via ecológica': 134,
        'via lateral': 135,
        'via marginal': 136,
        'via parque': 137,
        'via rápida': 138,
        'zona rural': 142,
        'zona urbana': 143,
        'condomínio fechado': 144,
        'loteamento fechado': 145,
        'condomínio residencial': 146,
        'condomínio horizontal': 147
    };

    for (const [keyword, codigo] of Object.entries(keywordMap)) {
        if (normalizedStreet.includes(keyword)) {
            return { codigo, descricao: logradouroMap[codigo] };
        }
    }

    return { codigo: 1, descricao: "Rua" }; // Default para Rua
};

// --- FUNÇÃO PARA TRATAR MUDANÇA DE TIPO DE LOGRADOURO ---
const onLogradouroTipoChange = () => {
    if (selectedLogradouroTipo.value) {
        const codigo = parseInt(selectedLogradouroTipo.value);
        newPatient.value.logradouro_codigo = selectedLogradouroTipo.value; // Mantém como string
        newPatient.value.logradouro_tipo = logradouroMap[codigo] || "Rua";
    } else {
        newPatient.value.logradouro_codigo = "";
        newPatient.value.logradouro_tipo = "";
    }
};

// --- FUNÇÃO PARA CONSULTAR CEP ---
const consultarCep = async () => {
    const cep = newPatient.value.cep?.replace(/\D/g, '');
    
    if (!cep || cep.length !== 8) {
        cepError.value = 'CEP deve ter 8 dígitos';
        return;
    }
    
    isConsultingCep.value = true;
    cepError.value = '';
    
    // Mapeamento de códigos IBGE por estado
    const stateIbgeMap = {
        'AC': '12',
        'AL': '27',
        'AP': '16',
        'AM': '13',
        'BA': '29',
        'CE': '23',
        'DF': '53',
        'ES': '32',
        'GO': '52',
        'MA': '21',
        'MT': '51',
        'MS': '50',
        'MG': '31',
        'PA': '15',
        'PB': '25',
        'PR': '41',
        'PE': '26',
        'PI': '22',
        'RJ': '33',
        'RN': '24',
        'RS': '43',
        'RO': '11',
        'RR': '14',
        'SC': '42',
        'SP': '35',
        'SE': '28',
        'TO': '17'
    };
    
    try {
        const response = await apiClient.get(`/api/v1/schedules/consultar-cep/${cep}/`);
        
        if (response.data.success) {
            // Preenche automaticamente os campos de endereço
            newPatient.value.street = response.data.logradouro || '';
            newPatient.value.city = response.data.localidade || '';
            newPatient.value.state = response.data.uf || '';
            newPatient.value.neighborhood = response.data.bairro || '';
            newPatient.value.logradouro = response.data.logradouro || ''; // Adicionado: campo logradouro completo
            
            // Preenche o código IBGE do estado
            if (response.data.uf) {
                newPatient.value.state_ibge_code = stateIbgeMap[response.data.uf] || '';
            }
            
            // Detecta e preenche o tipo de logradouro automaticamente
            if (response.data.logradouro) {
                const logradouroInfo = detectLogradouroType(response.data.logradouro);
                newPatient.value.logradouro_codigo = logradouroInfo.codigo.toString(); // Convertido para string
                newPatient.value.logradouro_tipo = logradouroInfo.descricao;
                selectedLogradouroTipo.value = logradouroInfo.codigo.toString();
                
                // Sincroniza o campo logradouro com o street
                newPatient.value.logradouro = response.data.logradouro;
            }
            
            // Se não houver complemento específico, pode usar o bairro como complemento
            if (!newPatient.value.complement && response.data.bairro && !newPatient.value.neighborhood) {
                newPatient.value.complement = response.data.bairro;
            }
            showBanner('success', 'Endereço preenchido automaticamente!');
        } else {
            cepError.value = response.data.error || 'CEP não encontrado';
            showBanner('error', cepError.value);
        }
    } catch (err) {
        console.error('Erro ao consultar CEP:', err);
        cepError.value = 'Erro ao consultar CEP. Tente novamente.';
        showBanner('error', cepError.value);
    } finally {
        isConsultingCep.value = false;
    }
};

const handleSaveAppointment = async () => {
    // Verifica se há serviços disponíveis para a agenda
    if (filteredServices.value.length === 0) {
        showBanner('error', 'Esta agenda não possui serviços vinculados. Vincule pelo menos um serviço antes de criar agendamentos.');
        return;
    }

    const requiredFields = ['patient_id', 'service_id', 'time', 'procedencia', 'motivo_marcacao'];
    const missingFields = requiredFields.filter(field => !newAppointment.value[field]);

    if (missingFields.length > 0) {
        // Limpar campos piscando anteriores
        blinkingFields.value.clear();
        
        // Adicionar campos faltantes para piscar
        missingFields.forEach(field => blinkingFields.value.add(field));
        
        // Parar de piscar após 3 segundos
        setTimeout(() => {
            blinkingFields.value.clear();
        }, 3000);
        
        showBanner('error','Por favor, preencha todos os campos obrigatórios (*)');
        return;
    }

    // Limpar campos piscando se tudo estiver válido
    blinkingFields.value.clear();

    if (!isValidScheduleId()) {
        showBanner('error','ID da agenda inválido.');
        return;
    }

    const payload = {
        date: selectedDate.value,
        patient_id: parseInt(newAppointment.value.patient_id),
        service_id: parseInt(newAppointment.value.service_id),
        time: newAppointment.value.time,
        notes: newAppointment.value.notes,
        procedencia: newAppointment.value.procedencia,
        motivo_marcacao: newAppointment.value.motivo_marcacao,
        cid10: newAppointment.value.cid10,
        is_inpatient: newAppointment.value.is_inpatient,
        is_urgent: newAppointment.value.is_urgent,
        send_preparation_email: newAppointment.value.send_preparation_email,
        send_preparation_sms: newAppointment.value.send_preparation_sms,
        insurance_id: newAppointment.value.insurance_id ? parseInt(newAppointment.value.insurance_id) : null,
        requesting_doctor_id: newAppointment.value.requesting_doctor_id ? parseInt(newAppointment.value.requesting_doctor_id) : null,
        internal_doctor_id: newAppointment.value.internal_doctor_id ? parseInt(newAppointment.value.internal_doctor_id) : null
    };

    try {
        if (!isValidScheduleId()) {
            showBanner('error','ID da agenda inválido para criar agendamento.');
            return;
        }
        
        await apiClient.post(`/api/v1/schedules/${selectedAgendaId.value}/appointments/`, payload);
        isCreateModalOpen.value = false;
        isCreatingFromSpecialSlot.value = false;
        await fetchAppointments();
        showBanner('success','Agendamento criado com sucesso!');
    } catch (err) {
        console.error("Erro ao criar agendamento:", err);
        const errorDetail = err.response?.data?.detail || 'Verifique os dados e tente novamente.';
        showBanner('error',`Ocorreu um erro ao salvar: ${errorDetail}`);
    }
};

// O restante dos seus métodos (updateAppointmentStatus, deleteAppointment, etc.) continua aqui...

// --- PROPRIEDADES COMPUTADAS ---
const filteredAppointments = computed(() => {
    if (filterStatus.value === 'all') {
        return appointments.value;
    }
    return appointments.value.filter(apt => apt.status === filterStatus.value);
});

const currentAgenda = computed(() => {
    return agendas.value.find(agenda => agenda.id === selectedAgendaId.value) || {};
});

const updateAppointmentStatus = async (appointment, newStatus) => {
    if (!validateScheduleIdForApi('atualizar status do agendamento')) return;
    
    try {
        const response = await apiClient.patch(`/api/v1/schedules/${selectedAgendaId.value}/appointments/${appointment.id}/status/`, { status: newStatus });
        const updatedAppointment = response.data;

        const index = appointments.value.findIndex(a => a.id === updatedAppointment.id);
        if (index !== -1) {
            appointments.value[index] = updatedAppointment;
        }
        if (selectedAppointment.value && selectedAppointment.value.id === updatedAppointment.id) {
            selectedAppointment.value = updatedAppointment;
        }
    } catch (err) {
        console.error(`Erro ao atualizar status para ${newStatus}:`, err);
        showBanner('error','Ocorreu um erro ao atualizar o status.');
    }
};

const openDeleteConfirmationModal = (appointment) => {
    deleteConfirmationAppointment.value = appointment;
    isDeleteConfirmationModalOpen.value = true;
};

const closeDeleteConfirmationModal = () => {
    isDeleteConfirmationModalOpen.value = false;
    deleteConfirmationAppointment.value = null;
};

const openDeleteSpecialSlotModal = (appointment) => {
    deleteSpecialSlotData.value = appointment;
    isDeleteSpecialSlotModalOpen.value = true;
};

const closeDeleteSpecialSlotModal = () => {
    isDeleteSpecialSlotModalOpen.value = false;
    deleteSpecialSlotData.value = null;
};

const deleteAppointment = async (appointmentId) => {
    try {
        await apiClient.delete(`/api/v1/schedules/${selectedAgendaId.value}/appointments/${appointmentId}/`);
        appointments.value = appointments.value.filter(apt => apt.id !== appointmentId);
        showBanner('success', 'Agendamento excluído com sucesso!');
        closeDeleteConfirmationModal();
    } catch (err) {
        console.error("Erro ao deletar agendamento:", err);
        showBanner('error','Ocorreu um erro ao excluir o agendamento.');
    }
};

const deleteSpecialSlot = async (specialSlotId) => {
    try {
        await apiClient.delete(`/api/v1/schedules/${selectedAgendaId.value}/special-slots/${specialSlotId}/`);
        showBanner('success','Horário especial excluído com sucesso!');
        await fetchAppointments();
        closeDeleteSpecialSlotModal();
    } catch (err) {
        console.error('Erro ao excluir horário especial:', err);
        showBanner('error','Ocorreu um erro ao excluir o horário especial.');
    }
};

const scanPatient = async (appointment) => {
    try {
        // Verifica se o agendamento tem accession number
        if (!appointment.accession_number) {
            showBanner('error', 'Este agendamento não possui número de acesso para escaneamento.');
            return;
        }

        // Caminho padrão do InovaScan.exe
        const scanPath = 'C:\\Inova\\InovaScan.exe';
        
        // Parâmetros para o InovaScan (accession number do paciente)
        const params = [appointment.accession_number];
        
        // Tenta executar o InovaScan.exe usando a API do Electron (se disponível)
        if (window.electronAPI && window.electronAPI.openExternal) {
            // Se estiver no Electron, usa a API nativa
            await window.electronAPI.openExternal(scanPath, params);
            showBanner('success', `InovaScan iniciado com sucesso para o paciente ${appointment.patient_name} (OS: ${appointment.accession_number})!`);
        } else {
            // Fallback para navegador web - tenta abrir via protocolo personalizado
            const protocolUrl = `inovascan://scan?accession=${appointment.accession_number}&patient=${encodeURIComponent(appointment.patient_name)}&service=${encodeURIComponent(appointment.service_name)}`;
            
            // Mostra mensagem de sucesso imediatamente
            showBanner('success', `InovaScan iniciado para ${appointment.patient_name} (OS: ${appointment.accession_number})!`);
            
            // Método mais eficaz: cria um link temporário e clica automaticamente
            const tempLink = document.createElement('a');
            tempLink.href = protocolUrl;
            tempLink.style.display = 'none';
            tempLink.target = '_blank';
            document.body.appendChild(tempLink);
            
            // Clica no link automaticamente
            tempLink.click();
            
            // Remove o link temporário
            setTimeout(() => {
                if (document.body.contains(tempLink)) {
                    document.body.removeChild(tempLink);
                }
            }, 100);
        }
        
    } catch (error) {
        console.error('Erro ao executar InovaScan:', error);
        
        // Mostra apenas uma mensagem genérica de erro
        showBanner('error', 'Erro ao executar o InovaScan. Verifique se o arquivo C:\\Inova\\InovaScan.exe existe.');
    }
};

const executeAction = async (action) => {
    const appointment = contextMenu.value.data;
    if (!appointment) return;

    // Fechar o menu de contexto imediatamente para evitar cliques duplos
    contextMenu.value.show = false;

    switch (action.action) {
        case 'view-details':
            selectAppointment(appointment);
            break;
        case 'edit-appointment':
            openEditModal(appointment);
            break;
        case 'confirm-appointment':
            openConfirmationModal(appointment);
            break;
        case 'unconfirm-appointment':
            openUnconfirmModal(appointment);
            break;
        case 'cancel-appointment':
            openCancellationModal(appointment);
            break;
        case 'reschedule-appointment':
            openRescheduleModal(appointment);
            break;
        case 'cancel-reschedule':
            openCancelRescheduleModal(appointment);
            break;
        case 'generate-worklist':
            generateWorklist(appointment);
            break;
        case 'patient-history':
            openPatientHistoryModal(appointment);
            break;
        // Removido case 'delivery-protocol' conforme solicitado
        case 'scan-documents':
            openScanModal(appointment);
            break;
        case 'view-documents':
            openDocumentsViewer(appointment);
            break;
        case 'share-images':
            openShareImagesModal(appointment);
            break;
        case 'scan-patient':
            scanPatient(appointment);
            break;
        case 'print':
            // A impressão é tratada pelo submenu no template
            break;
        case 'delete-appointment':
            openDeleteConfirmationModal(appointment);
            break;
        case 'delete-special-slot':
            openDeleteSpecialSlotModal(appointment);
            break;
        case 'add-appointment-to-slot':
            openCreateModalFromSpecialSlot(appointment);
            break;
        case 'recall-patient':
            // Evitar abrir o modal se já estiver aberto
            if (isRecallModalOpen.value) {
                break;
            }
            
            // Buscar dados completos do agendamento para o componente
            try {
                const response = await apiClient.get(`/api/v1/medical-reports/agendamentos/por-accession/${appointment.accession_number}/`);
                const data = response.data;
                console.log('Dados recebidos para recall:', data);
                recallAppointment.value = {
                  ...data,
                  patient_name: data.patient_name || 'Paciente não informado',
                  service_name: data.service?.descricao || data.service_name || 'Exame não informado',
                };
                console.log('Dados padronizados para recall:', recallAppointment.value);
                isRecallModalOpen.value = true;
            } catch (error) {
                console.error('Erro ao buscar dados do agendamento para reconvocação:', error);
                // Fallback para dados básicos se a API falhar
                recallAppointment.value = {
                  ...appointment,
                  patient_name: appointment.patient_name || 'Paciente não informado',
                  service_name: appointment.service?.descricao || appointment.service_name || 'Exame não informado',
                };
                console.log('Dados de fallback para recall:', recallAppointment.value);
                isRecallModalOpen.value = true;
            }
            break;
        case 'not-performed':
            openNotPerformedModal(appointment);
            break;
    }
}

const formatSelectedDate = (date) => {
    if (!date) return '';
    return new Date(date + 'T00:00:00').toLocaleDateString('pt-BR', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });
}

const formatDate = (date) => {
    if (!date) return '';
    return new Date(date + 'T00:00:00').toLocaleDateString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric'
    });
}

const previousDay = () => {
    selectedDate.value = addDaysToDate(selectedDate.value, -1);
}

const nextDay = () => {
    selectedDate.value = addDaysToDate(selectedDate.value, 1);
}

const goToToday = () => {
    selectedDate.value = getCurrentDate();
}

const getExamIcon = (examType) => {
    if (!examType) return ClockIcon;
    const lowerExamType = examType.toLowerCase();
    if (lowerExamType.includes('tc') || lowerExamType.includes('tomografia')) return ActivityIcon;
    if (lowerExamType.includes('rm') || lowerExamType.includes('ressonância')) return StethoscopeIcon;
    if (lowerExamType.includes('rx') || lowerExamType.includes('raio')) return ScanIcon;
    if (lowerExamType.includes('us') || lowerExamType.includes('ultrassom')) return ZapIcon;
    return ClockIcon;
}

const getStatusLabel = (status) => {
    const labels = {
        confirmed: 'Confirmado',
        pending: 'Pendente',
        cancelled: 'Cancelado',
        completed: 'Realizado',
        rescheduled: 'Reagendado',
        no_show: 'Falta',
        laudado: 'Laudado',
        recalled: 'Reconvocado',
        not_performed: 'Não Realizado',
        special_slot: 'Horário Especial',
        available: 'Disponível'
    };
    return labels[status] || status;
};

// Função para normalizar valor da raça (texto completo para código)
const normalizeRaceValue = (race) => {
    if (!race || race === '') return '';
    
    // Se já for um código válido, retorna como está
    const validCodes = ['01', '02', '03', '04', '05'];
    if (validCodes.includes(race)) {
        return race;
    }
    
    // Mapeia texto completo para código
    const raceMap = {
        'branca': '01',
        'preta': '02',
        'parda': '03',
        'amarela': '04',
        'indígena': '05',
        'BRANCA': '01',
        'PRETA': '02',
        'PARDA': '03',
        'AMARELA': '04',
        'INDÍGENA': '05'
    };
    
    if (typeof race === 'string') {
        const normalizedRace = race.toLowerCase().trim();
        return raceMap[normalizedRace] || race;
    }
    
    return race;
};

// Função para mapear códigos de raça para rótulos legíveis
const getRaceLabel = (race) => {
    const labels = {
        '01': 'Branca',
        '02': 'Preta',
        '03': 'Parda',
        '04': 'Amarela',
        '05': 'Indígena'
    };
    
    // Se já for um código válido, retorna o label
    if (labels[race]) {
        return labels[race];
    }
    
    // Se for texto completo, converte para código e depois para label
    const raceReverseMap = {
        'branca': '01',
        'preta': '02',
        'parda': '03',
        'amarela': '04',
        'indígena': '05'
    };
    
    if (race && typeof race === 'string') {
        const normalizedRace = race.toLowerCase().trim();
        const code = raceReverseMap[normalizedRace];
        if (code && labels[code]) {
            return labels[code];
        }
    }
    
    return (race === '' || race === null || !race) ? 'Não informada' : race;
};


const getRecallInfo = (appointment) => {
    if (appointment.status !== 'recalled' || !appointment.notes) {
        return 'Reconvocado';
    }
    
    // Procura por padrão "Reconvocado para DD/MM/AAAA às HH:MM" nas notas
    const recallPattern = /Reconvocado para (\d{2}\/\d{2}\/\d{4}) às (\d{2}:\d{2})/;
    const match = appointment.notes.match(recallPattern);
    
    if (match) {
        return `Reconvocado para ${match[1]} às ${match[2]}`;
    }
    
    return 'Reconvocado';
}

// Alias para getStatusLabel para compatibilidade
const getStatusDisplay = (status) => {
    return getStatusLabel(status);
}

const showAppointmentContextMenu = (event, appointment) => {
    event.preventDefault();
    event.stopPropagation();

    // Evitar mostrar o menu se já estiver visível
    if (contextMenu.value.show) {
        return;
    }

    const actions = [];
    
    // Ações específicas para horários especiais
    if (appointment.status === 'available' && appointment.special_slot_id) {
        // Para horários especiais disponíveis, mostra apenas as opções específicas
        if (can('Schedules.add_appointment')) {
            actions.push({ label: 'Adicionar agendamento ao horário', icon: PlusIcon, action: 'add-appointment-to-slot' });
        }
        if (can('Schedules.delete_special_slot')) {
            actions.push({ label: 'Excluir Horário Especial', icon: TrashIcon, action: 'delete-special-slot' });
        }
    } else {
        // Para agendamentos normais, adiciona as ações padrão
        if (can('Schedules.view_appointment')) {
            actions.push({ label: 'Ver Detalhes', icon: EyeIcon, action: 'view-details' });
        }

        // Usando 'patients.change_patient' para editar agendamento, pois permite alterar dados do paciente.
        // Não exibir "Editar Agendamento" quando o status for "completed" (Realizado) ou "laudado" (Laudado)
        if (can('patients.change_patient') && appointment.status !== 'completed' && appointment.status !== 'laudado') {
            actions.push({ label: 'Editar Agendamento', icon: EditIcon, action: 'edit-appointment' });
        }
        
        // Ações baseadas no status
        if (appointment.status === 'pending') {
            if (can('Schedules.confirm_appointment')) {
                actions.push({ label: 'Confirmar', icon: CheckIcon, action: 'confirm-appointment' });
            }
            if (can('Schedules.cancel_appointment')) {
                actions.push({ label: 'Cancelar', icon: XIcon, action: 'cancel-appointment' });
            }
            if (can('Schedules.reschedule_appointment')) {
                actions.push({ label: 'Reagendar', icon: CalendarIcon, action: 'reschedule-appointment' });
            }
            // Removido "Pedir Complemento" para status pendente conforme solicitado
        }
        
        if (appointment.status === 'confirmed') {
            if (can('Schedules.reschedule_appointment')) {
                actions.push({ label: 'Reagendar', icon: CalendarIcon, action: 'reschedule-appointment' });
            }
            if (can('Schedules.reschedule_appointment')) {
                actions.push({ label: 'Pedir Complemento', icon: PhoneIcon, action: 'recall-patient' });
            }
            
            // Adiciona a ação de Desconfirmar somente se a agenda permitir
            if (can('Schedules.confirm_appointment') && currentAgenda.value && currentAgenda.value.allow_unconfirm === true) {
                actions.push({ label: 'Desconfirmar', icon: RotateCcwIcon, action: 'unconfirm-appointment' });
            }

            if (can('Schedules.mark_not_performed')) {
                actions.push({ label: 'Não Realizado', icon: XCircleIcon, action: 'not-performed' });
            }
            
        }
        
        // Não exibir "Anexar Documentos" quando o status for "completed" (Realizado)
        // if (appointment.status === 'completed') {
        //     if (can('Schedules.upload_document')) {
        //         actions.push({ label: 'Anexar Documentos', icon: Forward, action: 'scan-documents' });
        //     }
        // }
        
        if (appointment.status === 'cancelled') {
            if (can('Schedules.reschedule_appointment')) {
                actions.push({ label: 'Reagendar', icon: CalendarIcon, action: 'reschedule-appointment' });
            }
        }
        
        if (appointment.status === 'rescheduled') {
            if (can('Schedules.reschedule_appointment')) {
                actions.push({ label: 'Reagendar', icon: CalendarIcon, action: 'reschedule-appointment' });
                actions.push({ label: 'Cancelar Reagendamento', icon: XIcon, action: 'cancel-reschedule' });
            }
        }
        
        if (appointment.status === 'no_show') {
            if (can('Schedules.reschedule_appointment')) {
                actions.push({ label: 'Reagendar', icon: CalendarIcon, action: 'reschedule-appointment' });
            }
            if (can('Schedules.reschedule_appointment')) {
                actions.push({ label: 'Pedir Complemento', icon: PhoneIcon, action: 'recall-patient' });
            }
        }
        
        // Ações para visualizar documentos (confirmed/completed/laudado/reported)
        if (can('Schedules.view_appointment') && ['confirmed','completed','laudado','reported'].includes(appointment.status)) {
            actions.push({ label: 'Visualizar Documentos', icon: FileTextIcon, action: 'view-documents' });
        }
        
        // Novo: ação de Anexar Documentos com regra unificada (confirmed/completed/laudado + flags da agenda)
        if (can('Schedules.upload_document') && isAttachmentAllowed(appointment)) {
            actions.push({ label: 'Anexar Documentos', icon: Forward, action: 'scan-documents' });
        }

        if (can('Schedules.view_patient_history')) {
            actions.push({ label: 'História do Paciente', icon: ClockIcon, action: 'patient-history' });
        }

        // Removido botão "Ficha do Protocolo" conforme solicitado
        
        // Botão de escanear (quando permitido anexar e houver accession number)
        if (appointment.accession_number && isAttachmentAllowed(appointment) && can('Schedules.view_appointment')) {
            actions.push({ label: 'Escanear', icon: ScanIcon, action: 'scan-patient' });
        }
        
        // Botão de compartilhar imagens (apenas para agendamentos com accession number)
        if (appointment.accession_number && can('Schedules.view_appointment')) {
            actions.push({ label: 'Compartilhar Imagens', icon: ShareIcon, action: 'share-images' });
        }
        
        // Assumindo permissão 'Schedules.delete_appointment' para a exclusão.
        if (appointment.status !== 'cancelled' && appointment.status !== 'completed') {
            if (can('Schedules.delete_appointment')) {
                actions.push({ label: 'Excluir', icon: TrashIcon, action: 'delete-appointment' });
            }
        }
    }
    
    // Posiciona o menu próximo ao clique, mas dentro dos limites da tela
    const menuWidth = 220; // ajuste conforme o tamanho real do menu
    const menuHeight = 400; // altura estimada do menu
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    
    // Calcula posição baseada no clique, mas ajusta se sair da tela
    let x = event.clientX;
    let y = event.clientY;
    
    // Ajusta horizontalmente se sair da tela
    if (x + menuWidth > windowWidth) {
        x = windowWidth - menuWidth - 10; // 10px de margem
    }
    if (x < 10) {
        x = 10; // margem mínima da esquerda
    }
    
    // Ajusta verticalmente se sair da tela
    if (y + menuHeight > windowHeight) {
        y = windowHeight - menuHeight - 10; // 10px de margem
    }
    if (y < 10) {
        y = 10; // margem mínima do topo
    }

    contextMenu.value = {
        show: true,
        x,
        y,
        data: appointment,
        actions: actions
    };
}

const selectAppointment = (appointment) => {
    selectedAppointment.value = appointment;
}

const hideContextMenu = () => {
    contextMenu.value.show = false;
}

// Fecha submenu ao clicar fora
document.addEventListener('click', hideContextMenu);

// --- FUNÇÕES PARA MODAIS DE CONFIRMAÇÃO E ANAMNESE ---
const openConfirmationModal = async (appointment) => {
    confirmationAppointment.value = appointment;
    await fetchDoctors();
    // Preenche dados iniciais
    confirmationData.value = {
        sisreg_confirmation_key: appointment.sisreg_confirmation_key || '',
        doctor_name: appointment.doctor_name || '',
        guide_number: appointment.guide_number || '',
        guide_issue_date: appointment.guide_issue_date || '',
        authorization_date: appointment.authorization_date || '',
        cid10: appointment.cid10 || '',
        race: normalizeRaceValue(appointment.patient_race || ''),
        procedencia: appointment.procedencia || ''
     };

     
     // Verifica se tem anamnese obrigatória
    try {
        const response = await apiClient.get(`/api/v1/schedules/${selectedAgendaId.value}/appointments/${appointment.id}/anamneses/`);
        requiredAnamneses.value = response.data;
        hasRequiredAnamnesis.value = requiredAnamneses.value.length > 0;
    } catch (err) {
        console.error('Erro ao buscar anamneses:', err);
        requiredAnamneses.value = [];
        hasRequiredAnamnesis.value = false;
    }
    
    isConfirmationModalOpen.value = true;
};

const closeConfirmationModal = () => {
    isConfirmationModalOpen.value = false;
    confirmationAppointment.value = null;
    confirmationData.value = {
        sisreg_confirmation_key: '',
        doctor_name: '',
        guide_number: '',
        guide_issue_date: '',
        authorization_date: '',
        cid10: '',
        race: '',
        procedencia: ''
    };
    requiredAnamneses.value = [];
    hasRequiredAnamnesis.value = false;
};

const confirmationError = ref('');

const isConfirmationFormValid = computed(() => {
  const requiredFields = ['sisreg_confirmation_key', 'doctor_name', 'guide_number', 'guide_issue_date', 'authorization_date', 'cid10', 'race', 'procedencia'];
  return requiredFields.every(field => confirmationData.value[field]);
});

const handleConfirmAppointment = async () => {
    if (!confirmationAppointment.value) return;
    // Validação dos campos obrigatórios
    const requiredFields = ['sisreg_confirmation_key', 'doctor_name', 'guide_number', 'guide_issue_date', 'authorization_date', 'cid10', 'race', 'procedencia'];
    const missingFields = requiredFields.filter(field => !confirmationData.value[field]);
    if (missingFields.length > 0) {
        confirmationError.value = 'Por favor, preencha todos os campos obrigatórios.';
        return;
    } else {
        confirmationError.value = '';
    }
    
    // Se tem anamnese obrigatória, verifica se foi finalizada
    if (hasRequiredAnamnesis.value && !confirmationAppointment.value.anamnesis_completed) {
        showBanner('error','Não é possível confirmar o agendamento. A anamnese obrigatória deve ser finalizada primeiro.');
        return;
    }
    
    if (!validateScheduleIdForApi('confirmar agendamento')) return;
    
    isConfirming.value = true;
    
    try {
        // Confirma o agendamento
        const response = await apiClient.post(`/api/v1/schedules/${selectedAgendaId.value}/appointments/${confirmationAppointment.value.id}/confirm/`, confirmationData.value);
        
        // Atualiza a raça do paciente se foi selecionada durante a confirmação
        if (confirmationData.value.race && confirmationAppointment.value.patient && confirmationAppointment.value.patient.id) {
            try {
                // Busca os dados atuais do paciente para manter os outros campos
                const currentPatientResponse = await apiClient.get(`/api/v1/patients/${confirmationAppointment.value.patient.id}/`);
                const currentPatient = currentPatientResponse.data;
                
                // Atualiza apenas a raça mantendo os demais dados
                await apiClient.put(`/api/v1/patients/${confirmationAppointment.value.patient.id}/`, {
                    ...currentPatient,
                    race: confirmationData.value.race
                });
            } catch (raceUpdateErr) {
                console.warn('Erro ao atualizar raça do paciente:', raceUpdateErr);
                // Não falha a confirmação se houver erro ao atualizar a raça
            }
        }
        
        // Atualiza o agendamento na lista
        const index = appointments.value.findIndex(a => a.id === confirmationAppointment.value.id);
        if (index !== -1) {
            appointments.value[index] = response.data;
        }
        // Se o agendamento selecionado é o mesmo, atualiza
        if (selectedAppointment.value && selectedAppointment.value.id === confirmationAppointment.value.id) {
            selectedAppointment.value = response.data;
        }
        // Busca a agenda selecionada para saber o modo de geração do worklist
        let agendaConfig = null;
        if (agendas.value && agendas.value.length > 0) {
            agendaConfig = agendas.value.find(a => a.id === parseInt(selectedAgendaId.value));
        }
        // Só gera worklist se a configuração for 'confirmation'
        if (agendaConfig && agendaConfig.worklist_generation_mode === 'confirmation') {
            try {
                await apiClient.post(`/api/v1/schedules/${selectedAgendaId.value}/appointments/${confirmationAppointment.value.id}/generate-worklist/`);
                showBanner('success','Agendamento confirmado e worklist gerada com sucesso!');
            } catch (worklistErr) {
                showBanner('error','Agendamento confirmado com sucesso! ' + (worklistErr.response?.data?.detail || 'Erro ao gerar worklist.'));
            }
        } else {
            showBanner('success','Agendamento confirmado com sucesso!');
        }
        closeConfirmationModal();
        await fetchAppointments(); // <-- Adicionado para recarregar a lista
    } catch (err) {
        console.error('Erro ao confirmar agendamento:', err);
        const errorMessage = err.response?.data?.detail || err.response?.data?.message || 'Erro desconhecido';
        showBanner('error', errorMessage);
    } finally {
        isConfirming.value = false;
    }
};

const closeAnamnesisModal = () => {
    isAnamnesisModalOpen.value = false;
    confirmationAppointment.value = null;
    requiredAnamneses.value = [];
};

const openAnamnesisForm = () => {
    // Aqui você implementaria a navegação para o formulário de anamnese
    // Por enquanto, apenas marca como finalizada
    completeAnamnesis();
};

const completeAnamnesis = async () => {
    if (!confirmationAppointment.value) return;
    if (!validateScheduleIdForApi('finalizar anamnese')) return;
    
    try {
        await apiClient.post(`/api/v1/schedules/${selectedAgendaId.value}/appointments/${confirmationAppointment.value.id}/complete-anamnesis/`);
        // Atualiza o agendamento na lista
        const index = appointments.value.findIndex(a => a.id === confirmationAppointment.value.id);
        if (index !== -1) {
            appointments.value[index].anamnesis_completed = true;
            appointments.value[index].anamnesis_completed_at = new Date().toISOString();
        }
        // Se o agendamento selecionado é o mesmo, atualiza
        if (selectedAppointment.value && selectedAppointment.value.id === confirmationAppointment.value.id) {
            selectedAppointment.value.anamnesis_completed = true;
            selectedAppointment.value.anamnesis_completed_at = new Date().toISOString();
        }
        showBanner('success','Anamnese marcada como finalizada!');
        closeAnamnesisModal();
    } catch (err) {
        console.error('Erro ao finalizar anamnese:', err);
        const errorMessage = err.response?.data?.detail || err.response?.data?.message || 'Erro desconhecido';
        showBanner('error', errorMessage);
    }
};

const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    return new Date(dateTimeString).toLocaleString('pt-BR');
};

const onServiceChange = async () => {
    newAppointment.value.insurance_id = null;
    if (newAppointment.value.service_id) {
        await fetchAvailableTimes();
        // Busca sexo do serviço selecionado
        const selectedService = services.value.find(s => s.id === newAppointment.value.service_id);
        let gender = null;
        if (selectedService && selectedService.sexo && selectedService.sexo !== 'ambos') {
            gender = selectedService.sexo === 'feminino' ? 'F' : selectedService.sexo === 'masculino' ? 'M' : null;
        }
        await fetchUsers(gender, patientSearchTerm.value);
    } else {
        await fetchUsers(null, patientSearchTerm.value);
    }
};

// --- FUNÇÕES PARA NOVOS MODAIS ---

const openCancellationModal = (appointment) => {
    cancellationAppointment.value = appointment;
    cancellationData.value.reason = '';
    isCancellationModalOpen.value = true;
};

const closeCancellationModal = () => {
    isCancellationModalOpen.value = false;
    cancellationAppointment.value = null;
    cancellationData.value.reason = '';
};

const handleCancelAppointment = async () => {
    if (!cancellationAppointment.value || !cancellationData.value.reason.trim()) {
        showBanner('error','Por favor, informe o motivo do cancelamento.');
        return;
    }
    
    if (!validateScheduleIdForApi('cancelar agendamento')) return;
    
    isCancelling.value = true;
    
    try {
        const response = await apiClient.post(`/api/v1/schedules/${selectedAgendaId.value}/appointments/${cancellationAppointment.value.id}/cancel/`, {
            reason: cancellationData.value.reason
        });
        
        // Atualiza o agendamento na lista
        const index = appointments.value.findIndex(a => a.id === cancellationAppointment.value.id);
        if (index !== -1) {
            appointments.value[index] = response.data;
        }
        
        showBanner('success','Agendamento cancelado com sucesso!');
        closeCancellationModal();
    } catch (err) {
        console.error('Erro ao cancelar agendamento:', err);
        const errorMessage = err.response?.data?.detail || err.response?.data?.message || 'Erro desconhecido';
        showBanner('error', errorMessage);
    } finally {
        isCancelling.value = false;
    }
};

const openNotPerformedModal = (appointment) => {
    notPerformedAppointment.value = appointment;
    notPerformedData.value.reason = '';
    isNotPerformedModalOpen.value = true;
};

const closeNotPerformedModal = () => {
    isNotPerformedModalOpen.value = false;
    notPerformedAppointment.value = null;
    notPerformedData.value.reason = '';
};

const handleNotPerformedAppointment = async () => {
    if (!notPerformedAppointment.value || !notPerformedData.value.reason.trim()) {
        showBanner('error','Por favor, informe o motivo pelo qual o paciente não realizou o exame.');
        return;
    }
    
    if (!validateScheduleIdForApi('marcar como não realizado')) return;
    
    isNotPerforming.value = true;
    
    try {
        await apiClient.post(`/api/v1/schedules/${selectedAgendaId.value}/appointments/${notPerformedAppointment.value.id}/not-performed/`, {
            reason: notPerformedData.value.reason
        });
        
        showBanner('success','Agendamento marcado como não realizado com sucesso!');
        closeNotPerformedModal();
        
        // Recarrega os agendamentos para garantir sincronização
        await fetchAppointments();
        
    } catch (err) {
        console.error('Erro ao marcar como não realizado:', err);
        const errorMessage = err.response?.data?.detail || err.response?.data?.message || 'Erro desconhecido';
        showBanner('error', errorMessage);
    } finally {
        isNotPerforming.value = false;
    }
};

const openRescheduleModal = async (appointment) => {
    rescheduleAppointment.value = appointment;
    rescheduleData.value = {
        new_schedule_id: selectedAgendaId.value,
        new_date: '',
        new_time: '',
        reason: ''
    };
    isRescheduleModalOpen.value = true;
};

const closeRescheduleModal = () => {
    isRescheduleModalOpen.value = false;
    rescheduleAppointment.value = null;
    rescheduleData.value = {
        new_schedule_id: null,
        new_date: '',
        new_time: '',
        reason: ''
    };
}

const openCancelRescheduleModal = (appointment) => {
    cancelRescheduleAppointment.value = appointment;
    isCancelRescheduleModalOpen.value = true;
}

const closeCancelRescheduleModal = () => {
    isCancelRescheduleModalOpen.value = false;
    cancelRescheduleAppointment.value = null;
};

// --- MODAL DE DESCONFIRMAÇÃO ---
const openUnconfirmModal = (appointment) => {
    unconfirmModalAppointment.value = appointment;
    isUnconfirmModalOpen.value = true;
};

const closeUnconfirmModal = () => {
    isUnconfirmModalOpen.value = false;
    unconfirmModalAppointment.value = null;
};

const handleUnconfirmAppointment = async () => {
    if (!unconfirmModalAppointment.value) return;
    if (!validateScheduleIdForApi('desconfirmar agendamento')) return;

    isUnconfirming.value = true;
    try {
        await apiClient.post(`/api/v1/schedules/appointments/${unconfirmModalAppointment.value.id}/unconfirm/`);
        showBanner('success', 'Agendamento desconfirmado com sucesso!');
        closeUnconfirmModal();
        await fetchAppointments();
    } catch (err) {
        console.error('Erro ao desconfirmar agendamento:', err);
        const errorMessage = err.response?.data?.detail || err.response?.data?.message || 'Erro desconhecido';
        showBanner('error', errorMessage);
    } finally {
        isUnconfirming.value = false;
    }
};



// Função auxiliar para criar horário especial automaticamente
const createSpecialSlotForReschedule = async (date, time, reason) => {
    try {
        const specialSlotPayload = {
            date: date,
            time: time,
            notes: `Horário especial criado automaticamente para reagendamento. Motivo: ${reason}`
        };
        
        const response = await apiClient.post(
            `/api/v1/schedules/${selectedAgendaId.value}/special-slots/`,
            specialSlotPayload
        );
        
        return response.data;
    } catch (error) {
        console.error('Erro ao criar horário especial:', error);
        throw error;
    }
};

// Função modificada de reagendamento
const handleRescheduleAppointment = async () => {
    if (!rescheduleData.value.new_date || !rescheduleData.value.new_time) {
        showBanner('error', 'Por favor, preencha a nova data e horário.');
        return;
    }
    
    isRescheduling.value = true;
    
    try {
        // SEMPRE criar um horário especial para reagendamentos
        // Isso evita conflitos com importações SISREG
        
        try {
            await createSpecialSlotForReschedule(
                rescheduleData.value.new_date,
                rescheduleData.value.new_time,
                rescheduleData.value.reason
            );
            
            showBanner('info', 'Horário especial criado automaticamente para evitar conflitos com importações SISREG.');
        } catch (specialSlotError) {
            console.error('Erro ao criar horário especial:', specialSlotError);
            showBanner('warning', 'Não foi possível criar horário especial automaticamente. Tentando reagendamento normal.');
        }
        
        // Adiciona informação sobre horário especial aos dados do reagendamento
        const reschedulePayload = {
            ...rescheduleData.value,
            is_special_slot: true, // Sempre marcar como horário especial
            special_slot_reason: `Reagendamento com horário especial para evitar conflito SISREG. ${rescheduleData.value.reason}`
        };
        
        // Executa o reagendamento
        await apiClient.post(
            `/api/v1/schedules/${selectedAgendaId.value}/appointments/${rescheduleAppointment.value.id}/reschedule/`, 
            reschedulePayload
        );
        
        const successMessage = 'Agendamento reagendado com sucesso! Horário especial criado para evitar conflitos com SISREG.';
            
        showBanner('success', successMessage);
        closeRescheduleModal();
        await fetchAppointments(); // Recarrega a lista
        
    } catch (err) {
        console.error('Erro ao reagendar agendamento:', err);
        const errorMessage = err.response?.data?.detail || err.response?.data?.message || 'Erro desconhecido';
        showBanner('error', errorMessage);
    } finally {
        isRescheduling.value = false;
    }
};

const handleCancelReschedule = async () => {
    if (!cancelRescheduleAppointment.value) {
        showBanner('error', 'Erro: Agendamento não encontrado.');
        return;
    }
    
    try {
        await apiClient.post(`/api/v1/schedules/${selectedAgendaId.value}/appointments/${cancelRescheduleAppointment.value.id}/cancel_reschedule/`);
        showBanner('success', 'Reagendamento cancelado com sucesso!');
        closeCancelRescheduleModal();
        await fetchAppointments(); // Recarrega a lista
    } catch (err) {
        console.error('Erro ao cancelar reagendamento:', err);
        const errorMessage = err.response?.data?.detail || err.response?.data?.message || 'Erro desconhecido';
        showBanner('error', errorMessage);
    }
};

const generateWorklist = async (appointment) => {
    try {
        await apiClient.post(`/api/v1/schedules/${selectedAgendaId.value}/appointments/${appointment.id}/generate-worklist/`);
        
        // Atualiza o agendamento na lista
        const index = appointments.value.findIndex(a => a.id === appointment.id);
        if (index !== -1) {
            appointments.value[index].worklist_generated = true;
            appointments.value[index].worklist_generated_at = new Date().toISOString();
        }
        
        showBanner('success','Worklist gerada com sucesso!');
    } catch (err) {
        console.error('Erro ao gerar worklist:', err);
        const errorMessage = err.response?.data?.detail || err.response?.data?.message || 'Erro desconhecido';
        showBanner('error', errorMessage);
    }
};


// --- FUNÇÕES DE RECONVOCAÇÃO ---
const isRecallModalOpen = ref(false)



const closeRecallModal = () => {
    isRecallModalOpen.value = false;
    recallAppointment.value = null;
};

const handleRecallPatientFromComponent = async (recallData) => {
    try {
        console.log('handleRecallPatientFromComponent - Dados recebidos:', recallData);
        console.log('handleRecallPatientFromComponent - recallAppointment:', recallAppointment.value);
        
        // Executar reconvocação usando o serviço
        const { recallQueueService } = await import('@/services/recallQueueService');
        const result = await recallQueueService.createRecall({
            appointment_id: recallAppointment.value.id,
            type: recallData.type,
            reason: recallData.reason,
            reason_type: recallData.reason_type,
            description: recallData.description,
            additional_notes: recallData.additional_notes
        });

        if (result && result.data) {
            console.log('handleRecallPatientFromComponent - Resultado da reconvocação:', result);
            
            showBanner('success', 'Paciente adicionado à fila de reconvocação! Um administrativo irá agendar a nova consulta.');
            closeRecallModal();
            await fetchAppointments(); // Recarrega a lista
        }

    } catch (error) {
        console.error('Erro ao reconvocar paciente:', error);
        const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Erro desconhecido';
        showBanner('error', errorMessage);
    }
};



const openPatientHistoryModal = async (appointment) => {
    try {
        const response = await apiClient.get(`/api/v1/schedules/${selectedAgendaId.value}/appointments/${appointment.id}/patient-history`);
        patientHistoryData.value = response.data;
        
        // Reset dos filtros quando abre modal
        historyStatusFilter.value = '';
        historyDateFilter.value = '';
        
        isPatientHistoryModalOpen.value = true;
    } catch (err) {
        console.error('Erro ao carregar histórico:', err);
        const errorMessage = err.response?.data?.detail || err.response?.data?.message || 'Erro desconhecido';
        showBanner('error', errorMessage);
    }
};

const closePatientHistoryModal = () => {
    isPatientHistoryModalOpen.value = false;
    patientHistoryData.value = null;
    historyStatusFilter.value = '';
    historyDateFilter.value = '';
};

// Computed para filtrar agendamentos no histórico
const filteredHistoryAppointments = computed(() => {
    if (!patientHistoryData.value?.appointments) return [];
    
    let filtered = [...patientHistoryData.value.appointments];
    
    // Filtro por status
    if (historyStatusFilter.value) {
        filtered = filtered.filter(apt => apt.status === historyStatusFilter.value);
    }
    
    // Filtro por data
    if (historyDateFilter.value) {
        const now = new Date();
        
        switch (historyDateFilter.value) {
            case 'last_30': {
                const thirty_days_ago = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
                filtered = filtered.filter(apt => new Date(apt.date) >= thirty_days_ago);
                break;
            }
            case 'last_90': {
                const ninety_days_ago = new Date(now.getTime() - (90 * 24 * 60 * 60 * 1000));
                filtered = filtered.filter(apt => new Date(apt.date) >= ninety_days_ago);
                break;
            }
            case 'last_year': {
                const one_year_ago = new Date(now.getTime() - (365 * 24 * 60 * 60 * 1000));
                filtered = filtered.filter(apt => new Date(apt.date) >= one_year_ago);
                break;
            }
            case 'older': {
                const one_year_ago_older = new Date(now.getTime() - (365 * 24 * 60 * 60 * 1000));
                filtered = filtered.filter(apt => new Date(apt.date) < one_year_ago_older);
                break;
            }
        }
    }
    
    return filtered;
});



// Função para obter classes do badge de status
const getStatusBadgeClass = (status) => {
    const classes = {
        'pending': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
        'confirmed': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
        'completed': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
        'cancelled': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
        'rescheduled': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
        'no_show': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
        'laudado': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
        'recalled': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
    };
    return classes[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
};

// Função para obter ícone de status
const getStatusIcon = (status) => {
    const icons = {
        'pending': ClockIcon,
        'confirmed': CheckIcon,
        'completed': CheckIcon,
        'cancelled': XIcon,
        'rescheduled': EditIcon,
        'no_show': XIcon,
        'laudado': CheckIcon,
        'recalled': PhoneIcon
    };
    return icons[status] || ClockIcon;
};

// Função para obter classes do ícone de status
const getStatusIconClass = (status) => {
    const classes = {
        'pending': 'w-5 h-5 text-yellow-500',
        'confirmed': 'w-5 h-5 text-green-500',
        'completed': 'w-5 h-5 text-blue-500',
        'cancelled': 'w-5 h-5 text-red-500',
        'rescheduled': 'w-5 h-5 text-orange-500',
        'no_show': 'w-5 h-5 text-purple-500',
        'laudado': 'w-5 h-5 text-indigo-500',
        'recalled': 'w-5 h-5 text-purple-500'
    };
    return classes[status] || 'w-5 h-5 text-gray-400';
};

// Função para navegar para documentos de um agendamento
const navigateToDocuments = (appointment) => {
    console.log('Abrindo modal de documentos para o agendamento:', appointment.id);
    openDocumentsViewer(appointment);
};

const isAttachmentAllowed = (appointment) => {
    if (!appointment) return false;
    const agenda = currentAgenda.value || {};
    const hasAccession = !!appointment.accession_number;

    // Confirmado (regra atual)
    if (appointment.status === 'confirmed') {
        return hasAccession;
    }

    // Realizado (completed) — permitido se flag da agenda estiver ativa
    if (appointment.status === 'completed') {
        return hasAccession && agenda.allow_attach_after_attendance === true;
    }

    // Laudado — permitido se flag da agenda estiver ativa
    if (appointment.status === 'laudado') {
        return hasAccession && agenda.allow_attach_after_report === true;
    }

    return false;
};

// Função para exportar histórico do paciente em PDF
const exportPatientHistory = async () => {
    if (!patientHistoryData.value) {
        showBanner('error','Nenhum histórico disponível para exportar.');
        return;
    }
    
    try {
        // Criar conteúdo HTML para o PDF
        const content = `
            <html>
            <head>
                <title>Histórico do Paciente - ${patientHistoryData.value.patient_name}</title>
                <style>
                    body { font-family: Arial, sans-serif; margin: 20px; }
                    .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
                    .patient-info { background-color: #f5f5f5; padding: 15px; margin-bottom: 20px; border-radius: 5px; }
                    .appointment { border: 1px solid #ddd; margin-bottom: 10px; padding: 10px; border-radius: 5px; }
                    .status-confirmed { border-left: 4px solid #22c55e; }
                    .status-pending { border-left: 4px solid #eab308; }
                    .status-cancelled { border-left: 4px solid #ef4444; }
                    .status-completed { border-left: 4px solid #3b82f6; }
                    .label { font-weight: bold; }
                    .value { margin-left: 10px; }
                    .row { display: flex; margin-bottom: 5px; }
                    .col { flex: 1; }
                    @media print { body { margin: 0; } }
                </style>
            </head>
            <body>
                <div class="header">
                    <h1>HISTÓRICO COMPLETO DO PACIENTE</h1>
                    <p>Data de Geração: ${new Date().toLocaleDateString('pt-BR')}</p>
                </div>
                
                <div class="patient-info">
                    <h3>DADOS DO PACIENTE</h3>
                    <div class="row">
                        <div class="col">
                            <span class="label">Nome:</span>
                            <span class="value">${patientHistoryData.value.patient_name}</span>
                        </div>
                        <div class="col">
                            <span class="label">ID:</span>
                            <span class="value">${patientHistoryData.value.patient_id}</span>
                        </div>
                    </div>
                    ${patientHistoryData.value.patient_cpf ? `
                    <div class="row">
                        <div class="col">
                            <span class="label">CPF:</span>
                            <span class="value">${patientHistoryData.value.patient_cpf}</span>
                        </div>
                        <div class="col">
                            <span class="label">Total de Agendamentos:</span>
                            <span class="value">${patientHistoryData.value.total_appointments}</span>
                        </div>
                    </div>
                    ` : ''}
                </div>
                
                <h3>HISTÓRICO DE AGENDAMENTOS</h3>
                ${filteredHistoryAppointments.value.map(appointment => `
                    <div class="appointment status-${appointment.status}">
                        <div class="row">
                            <div class="col">
                                <span class="label">Data:</span>
                                <span class="value">${formatDate(appointment.date)}</span>
                            </div>
                            <div class="col">
                                <span class="label">Horário:</span>
                                <span class="value">${appointment.time}</span>
                            </div>
                            <div class="col">
                                <span class="label">Status:</span>
                                <span class="value">${getStatusDisplay(appointment.status)}</span>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col">
                                <span class="label">Exame:</span>
                                <span class="value">${appointment.service_name}</span>
                            </div>
                            <div class="col">
                                <span class="label">Agenda:</span>
                                <span class="value">${appointment.schedule_name}</span>
                            </div>
                        </div>
                        ${appointment.accession_number ? `
                        <div class="row">
                            <div class="col">
                                <span class="label">OS:</span>
                                <span class="value">${appointment.accession_number}</span>
                            </div>
                        </div>
                        ` : ''}
                        ${appointment.notes ? `
                        <div class="row">
                            <div class="col">
                                <span class="label">Observações:</span>
                                <span class="value">${appointment.notes}</span>
                            </div>
                        </div>
                        ` : ''}
                    </div>
                `).join('')}
            </body>
            </html>
        `;
        
        const filename = `historico-paciente-${patientHistoryData.value?.patient_name?.replace(/\s+/g, '-').toLowerCase()}.pdf`;
        printDocument(content, filename);
        showBanner('success', isMobileDevice() ? 'Histórico do paciente baixado com sucesso!' : 'Histórico do paciente enviado para impressão!');
    } catch (err) {
        console.error('Erro ao exportar histórico:', err);
        showBanner('error','Erro ao exportar histórico do paciente.');
    }
};

// Função openDeliveryProtocolModal removida conforme solicitado

const printPatientSheet = async (appointment) => {
    if (!appointment) {
        showBanner('error','Nenhum agendamento selecionado para impressão.');
        return;
    }
    
    // Busca informações da clínica se não estiverem carregadas
    if (!clinicStore.isLoaded) {
        await clinicStore.fetchClinicData();
    }
    
    const clinicData = clinicStore.clinic;
    
    // Busca a logo do relatório do backend
    let reportLogoBase64 = '';
    try {
        const logoResponse = await apiClient.get('/api/v1/clinicsettings/report_logo/');
        reportLogoBase64 = logoResponse.data.logo_base64;
    } catch (err) {
        console.log('Logo do relatório não disponível, usando logo padrão');
    }
    
    // Busca a logo de entrega do backend
    let deliveryLogoBase64 = '';
    try {
        const deliveryLogoResponse = await apiClient.get('/api/v1/clinicsettings/delivery_logo/');
        deliveryLogoBase64 = deliveryLogoResponse.data.logo_base64;
    } catch (err) {
        console.log('Logo de entrega não disponível, usando logo padrão');
    }
    
    // Busca a logo padrão do backend (inovamed.png)
    let defaultLogoBase64 = '';
    try {
        const defaultLogoResponse = await apiClient.get('/api/v1/clinicsettings/default_logo/');
        defaultLogoBase64 = defaultLogoResponse.data.logo_base64;
    } catch (err) {
        console.log('Logo padrão do backend não disponível, usando logo padrão');
    }
    
    // Busca dados do protocolo de entrega
    let protocolData = null;
    try {
        const response = await apiClient.get(`/api/v1/schedules/${selectedAgendaId.value}/appointments/${appointment.id}/protocol/`);
        protocolData = response.data;
    } catch (err) {
        console.log('Protocolo de entrega não disponível para este agendamento');
    }
    
    // Busca dados completos do serviço para obter informações de entrega
    let serviceData = null;
    try {
        serviceData = await getServiceData(appointment.service);
    } catch (err) {
        console.log('Dados do serviço não disponíveis');
    }
    
    // Busca dados completos do paciente
    let patientData = null;
    try {
        const patientResponse = await apiClient.get(`/api/v1/patients/${appointment.patient}/`);
        patientData = patientResponse.data;
    } catch (err) {
        console.log('Dados completos do paciente não disponíveis');
    }
    
    // Busca dados da consulta para obter o horário de início do atendimento
    let consultationData = null;
    try {
        const consultationResponse = await apiClient.get(`/api/v1/consultancy/consultations/by-appointment/${appointment.id}/`);
        consultationData = consultationResponse.data;
    } catch (err) {
        console.log('Dados da consulta não disponíveis');
    }
    
    // Função auxiliar para formatar o horário de início do atendimento
    const formatServiceStartTime = (startTime) => {
        if (!startTime) return '';
        const date = new Date(startTime);
        return date.toLocaleTimeString('pt-BR', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };
    
                // Função para calcular a idade do paciente
            const calculateAge = (birthdate) => {
                if (!birthdate) return '';
                
                const birthDate = new Date(birthdate);
                const today = new Date();
                
                let age = today.getFullYear() - birthDate.getFullYear();
                const monthDiff = today.getMonth() - birthDate.getMonth();
                
                // Se ainda não chegou o aniversário este ano, subtrai 1
                if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                    age--;
                }
                
                return age.toString();
            };

            // Função para consultar CEP e obter o bairro
            const consultarCepParaBairro = async (cep) => {
                if (!cep) return '';
                
                try {
                    const cepLimpo = cep.replace(/\D/g, '');
                    if (cepLimpo.length !== 8) return '';
                    
                    const response = await apiClient.get(`/api/v1/schedules/consultar-cep/${cepLimpo}/`);
                    
                    if (response.data.success && response.data.bairro) {
                        return response.data.bairro;
                    }
                } catch (err) {
                    console.error('Erro ao consultar CEP para bairro:', err);
                }
                
                return '';
            };

            // Função para obter o bairro do paciente (com fallback para ViaCEP)
            const obterBairroPaciente = async (patientData, appointment) => {
                // Primeiro tenta usar os dados existentes
                const bairroExistente = patientData?.district || appointment.address_district;
                if (bairroExistente) {
                    return bairroExistente;
                }
                
                // Se não há bairro, consulta o CEP
                const cep = patientData?.cep || appointment.address_zip;
                if (cep) {
                    return await consultarCepParaBairro(cep);
                }
                
                return '';
            };
    
    // Busca dados do perfil do usuário logado
    const perfilStore = usePerfilStore();
    if (!perfilStore.carregado) {
        await perfilStore.carregarPerfil();
    }
    
    // Determina o ID do paciente baseado no tipo de documento
    let patientId = '';
    if (patientData) {
        if (patientData.document_type === 'CPF' && patientData.cpf) {
            patientId = patientData.cpf;
        } else if (patientData.document_type === 'CNS' && patientData.cns) {
            patientId = patientData.cns;
        } else if (patientData.document_type === 'GEN' && patientData.id) {
            patientId = `ID: ${patientData.id}`;
        }
    }
    
    // Nome completo do usuário logado (atendente)
    const attendantName = `${perfilStore.firstName || ''} ${perfilStore.lastName || ''}`.trim() || userStore.nome || 'Sistema';
    
    // Gerar QR code se houver dados do portal
    let qrCodeDataUrl = null;
    if (protocolData?.portal_access?.enabled && protocolData?.portal_access?.url) {
        try {
            const QRCode = await import('qrcode');
            
            // Criar URL de autenticação automática com credenciais
            const baseUrl = protocolData.portal_access.url.replace(/\/$/, ''); // Remove barra final se existir
            const autoLoginUrl = `${baseUrl}/api/v1/auth/auto-login/?login=${encodeURIComponent(protocolData.portal_access.login)}&password=${encodeURIComponent(protocolData.portal_access.password)}`;
            
            const qrDataUrl = await QRCode.toDataURL(autoLoginUrl, {
                width: 120,
                margin: 2,
                color: {
                    dark: '#000000',
                    light: '#FFFFFF'
                }
            });
            qrCodeDataUrl = qrDataUrl;
        } catch (err) {
            console.error('Erro ao gerar QR code:', err);
        }
    }
    
    // Criar conteúdo da ficha do paciente seguindo o padrão do report_template
    const content = `
<html>
<head>
    <title>REGISTRO DE ATENDIMENTO - ${appointment.patient_name}</title>
    <style>
        body {
            font-family: Arial, sans-serif;
            color: #000;
            margin: 0;
            padding: 15px;
            background: #fff;
            font-size: 9px; /* Base font size */
        }

        .container {
            border: 1px solid #000;
            padding: 5px;
        }

        /* --- HEADER --- */
        .header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 2px solid #000;
            padding-bottom: 5px;
            margin-bottom: 5px;
        }
        .header-logo {
            max-height: 40px;
        }
        .header-title {
            font-size: 14px;
            font-weight: bold;
            text-align: right;
        }

        /* --- TOP INFO BOX --- */
        .id-box-container {
            display: flex;
            justify-content: flex-end;
        }
        .id-box {
            border: 1px solid #000;
            padding: 2px 4px;
            text-align: center;
        }
        .id-box .label {
            font-size: 8px;
            font-weight: bold;
            display: block;
        }
        .id-box .value {
            font-size: 11px;
            font-weight: bold;
        }
        
        /* --- FLEX-GRID LAYOUT --- */
        .flex-grid {
            border-top: 2px solid #000;
            border-bottom: 2px solid #000;
        }
        .flex-row {
            display: flex;
            border-bottom: 1px solid #000;
        }
        .flex-row:last-child {
            border-bottom: none;
        }
        .flex-item {
            padding: 3px 5px;
            border-left: 1px solid #000;
        }
        .flex-item:first-child {
            border-left: none;
        }
        .item-label {
            font-size: 7px;
            font-weight: bold;
            color: #333;
            display: block;
            margin-bottom: 2px;
            text-transform: uppercase;
        }
        .item-value {
            font-size: 11px;
            font-weight: bold;
        }

        /* --- EXAM TABLE --- */
        .exam-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 5px;
            border: 1px solid #000;
        }
        .exam-table th, .exam-table td {
            border: 1px solid #000;
            padding: 4px;
            text-align: center;
            font-size: 10px;
            font-weight: bold;
        }
        .exam-table th {
            font-size: 8px;
        }

        /* --- PROTOCOL SECTION --- */
        .protocol-section {
            border-top: 3px solid #000;
            margin-top: 15px;
            padding-top: 10px;
        }
        .protocol-title {
            font-size: 16px;
            font-weight: bold;
            text-align: center;
            margin-bottom: 10px;
        }
        .protocol-grid {
            display: flex;
            flex-wrap: wrap;
            border: 1px solid #000;
        }
        .protocol-item {
            padding: 5px;
            box-sizing: border-box;
        }
        .protocol-item.full-width {
             width: 100%;
             display: flex;
             text-align: center;
             border-top: 1px solid #000;
        }
        .protocol-item.half-width {
            width: 50%;
        }
        .protocol-item:nth-child(2) {
             border-left: 1px solid #000;
        }
        .protocol-item:nth-child(3), .protocol-item:nth-child(4) {
             border-top: 1px solid #000;
        }

        /* --- ACCESS INFO --- */
        .access-container {
            margin-top: 15px;
        }
        .qr-code {
            width: 90px;
            height: 90px;
            margin-right: 15px;
        }
        .access-details {
            border: 2px solid #000;
            padding: 8px;
            text-align: center;
        }
        .access-header {
            font-weight: bold;
            font-size: 11px;
        }
        .access-website {
            font-weight: bold;
            font-size: 12px;
            margin: 5px 0;
        }
        .access-login-info {
            display: flex;
            justify-content: space-around;
            margin: 8px 0;
        }
        .access-login-item .label {
            font-size: 9px;
            font-weight: bold;
        }
        .access-login-item .value {
            font-size: 11px;
            font-weight: bold;
        }
        .access-contact {
            font-size: 11px;
            font-weight: bold;
        }
        .biopsy-warning {
            color: #000;
            font-weight: bold;
            font-size: 9px;
            margin-top: 8px;
        }

        /* --- FOOTER --- */
        .disclaimer {
            margin-top: 10px;
            font-size: 8px;
            text-align: center;
            line-height: 1.3;
        }
        .footer {
             margin-top: 15px;
             text-align: center;
             font-size: 8px;
             color: #555;
        }

    </style>
</head>
<body>

    <div class="container">
        <div class="header">
            ${defaultLogoBase64 ? `<img src="data:image/png;base64,${defaultLogoBase64}" alt="Inovamed Logo" class="header-logo" style="max-height: 25px;">` : `<img src="${getClinicLogoUrl(clinicData)}" alt="Inovamed Logo" class="header-logo" style="max-height: 25px;">`}
            <div class="header-title">ㅤㅤㅤREGISTRO DE ATENDIMENTO</div>
            <div>
                ${reportLogoBase64 ? `<img src="data:image/png;base64,${reportLogoBase64}" alt="Saúde Pública Logo" class="header-logo" style="display: block; margin-bottom: 2px; max-height: 45px;">` : '<div class="header-logo" style="display: block; margin-bottom: 2px; max-height: 45px; background-color: #f0f0f0; border: 1px solid #ccc; text-align: center; line-height: 45px; font-size: 8px; color: #666;">Logo não disponível</div>'}
                
            </div>
        </div>

        <div class="id-box-container">
             <div class="id-box">
                <span class="label">ID PAC</span>
                <span class="value">${patientId || appointment.patient_id || ''}</span>
            </div>
             <div class="id-box" style="margin-left: 10px;">
                <span class="label">Hora/Atendimento</span>
                <span class="value">${consultationData?.start_time ? formatServiceStartTime(consultationData.start_time) : (appointment.time || '')}</span>
            </div>
        </div>
        
        <div class="flex-grid">
            <div class="flex-row">
                 
                 <div class="flex-item" style="flex-basis: 85%;"><span class="item-label">NOME</span><span class="item-value">${patientData?.name || appointment.patient_name || ''}</span></div>
            </div>
            <div class="flex-row">
                 <div class="flex-item" style="flex-basis: 10%;"><span class="item-label">SEXO</span><span class="item-value">${patientData?.gender || appointment.patient_gender || ''}</span></div>
                 <div class="flex-item" style="flex-basis: 10%;"><span class="item-label">IDADE</span><span class="item-value">${calculateAge(patientData?.birthdate) || calculateAge(appointment.patient_dob) || patientData?.age || appointment.patient_age || ''}</span></div>
                 <div class="flex-item" style="flex-basis: 15%;"><span class="item-label">DATA NASC.</span><span class="item-value">${formatDate(patientData?.birthdate) || formatDate(appointment.patient_dob) || ''}</span></div>
                 <div class="flex-item" style="flex-basis: 30%;"><span class="item-label">RG/CPF</span><span class="item-value">${patientData?.cpf || appointment.patient_cpf || ''}</span></div>
                 <div class="flex-item" style="flex-basis: 35%;"><span class="item-label">CNS</span><span class="item-value">${patientData?.cns || 'Não informado'}</span></div>
            </div>
             <div class="flex-row">
                <div class="flex-item" style="flex-basis: 50%;"><span class="item-label">ENDEREÇO</span><span class="item-value">${patientData?.street || appointment.address_street || ''}</span></div>
                <div class="flex-item" style="flex-basis: 10%;"><span class="item-label">NUM.</span><span class="item-value">${patientData?.number || appointment.address_number || ''}</span></div>
                <div class="flex-item" style="flex-basis: 40%;"><span class="item-label">APTO/COMPL.</span><span class="item-value">${patientData?.complement || appointment.address_complement || ''}</span></div>
            </div>
            <div class="flex-row">
                <div class="flex-item" style="flex-basis: 25%;"><span class="item-label">CIDADE</span><span class="item-value">${patientData?.city || appointment.address_city || ''}</span></div>
                <div class="flex-item" style="flex-basis: 10%;"><span class="item-label">UF</span><span class="item-value">${patientData?.state || appointment.address_state || ''}</span></div>
                <div class="flex-item" style="flex-basis: 40%;"><span class="item-label">BAIRRO</span><span class="item-value">${await obterBairroPaciente(patientData, appointment)}</span></div>
                <div class="flex-item" style="flex-basis: 25%;"><span class="item-label">CEP</span><span class="item-value">${patientData?.cep || appointment.address_zip || ''}</span></div>
            </div>
             <div class="flex-row">
                <div class="flex-item" style="flex-basis: 25%;"><span class="item-label">CONVÊNIO</span><span class="item-value">${patientData?.health_insurance || appointment.insurance_plan || ''}</span></div>
                <div class="flex-item" style="flex-basis: 75%;"><span class="item-label">TELEFONES</span><span class="item-value">${patientData?.phone || appointment.phones || ''}</span></div>
            </div>
             <div class="flex-row">
                <div class="flex-item" style="flex-basis: 25%;"><span class="item-label">PRONTUÁRIO</span><span class="item-value">${patientData?.id || appointment.medical_record_id || ''}</span></div>
                <div class="flex-item" style="flex-basis: 75%;"><span class="item-label">UNIDADE</span><span class="item-value">${clinicData.nome || ''}</span></div>
            </div>
             <div class="flex-row">
                <div class="flex-item" style="flex-basis: 25%;"><span class="item-label">ORDEM</span><span class="item-value">${appointment.order_id || ''}</span></div>
                <div class="flex-item" style="flex-basis: 75%;"><span class="item-label">PROCEDÊNCIA</span><span class="item-value">${appointment.procedencia || ''}</span></div>
            </div>
             <div class="flex-row">
                <div class="flex-item" style="flex-basis: 100%;"><span class="item-label">MÉDICO SOLICITANTE</span><span class="item-value">${appointment.requesting_doctor_name || 'Não informado'}</span></div>
            </div>
            <div class="flex-row">
                <div class="flex-item" style="flex-basis: 25%;"><span class="item-label">DATA AGENDAMENTO</span><span class="item-value">${formatDate(appointment.date) || ''}</span></div>
                <div class="flex-item" style="flex-basis: 75%;"><span class="item-label">HORA AGENDAMENTO</span><span class="item-value">${appointment.time?.slice(0,5) || ''}</span></div>
            </div>
            <div class="flex-row">
                <div class="flex-item" style="width: 100%;"><span class="item-label">ATENDENTE</span><span class="item-value">${attendantName}</span></div>
            </div>
        </div>

        <table class="exam-table">
            <thead>
                <tr>
                    <th>OS - EXAME</th>
                    <th>DESCRIÇÃO DOS EXAMES</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>${appointment.accession_number || ''}</td>
                    <td>${appointment.service_name || ''}</td>
                </tr>
            </tbody>
        </table>
    </div>

    ${protocolData ? `
    <div class="protocol-section">
        <div class="protocol-title">PROTOCOLO DE ENTREGA DE EXAME</div>
        
        <div class="protocol-grid">
            <div class="protocol-item half-width">
                <span class="item-label">PACIENTE</span>
                <span class="item-value">${protocolData.patient_name}</span>
            </div>
            <div class="protocol-item half-width">
                <span class="item-label">MÉDICO SOLICITANTE</span>
                <span class="item-value">${appointment.requesting_doctor_name ? `Dr.(a) ${appointment.requesting_doctor_name}` : 'Médico não informado'}</span>
            </div>
            <div class="protocol-item half-width">
                <span class="item-label">DATA EXAME</span>
                <span class="item-value">${formatDate(protocolData.date)}</span>
            </div>
            <div class="protocol-item half-width">
                 <span class="item-label">ENTREGA</span>
                 <span class="item-value" style="font-size: 9px;">${serviceData?.tempo_entrega ? serviceData.tempo_entrega + ' DIAS APÓS A REALIZAÇÃO DO EXAME*' : '7 DIAS APÓS A REALIZAÇÃO DO EXAME*'}</span>
                 <br>
                 <span class="item-label">HORA DE ENTREGA: ${protocolData.protocol_config?.horario_entrega || 'Segunda a Sexta: 08:00 as 17:00'}</span>
            </div>
            <div class="protocol-item full-width">
                <div class="protocol-item half-width">
                    <span class="item-label">OS - EXAME</span>
                    <span class="item-value">${appointment.accession_number || ''}</span>
                </div>
                <div class="protocol-item half-width">
                    <span class="item-label">DESCRIÇÃO DOS EXAMES</span>
                    <span class="item-value">${protocolData.service}</span>
                </div>
            </div>
        </div>
    ` : ''}

    <div class="access-container">
             <div class="access-details">
                <div class="access-header">ACESSE O SEU EXAME</div>
                <div class="access-website">${clinicData.url_portal_paciente }</div>
                <div style="display: flex; justify-content: space-between; align-items: center;">
                    ${qrCodeDataUrl ? `<img src="${qrCodeDataUrl}" alt="QR Code" class="qr-code">` : ''}
                    <div class="access-login-info">
                        <div class="access-login-item">
                            <span class="label">ㅤㅤㅤㅤLogin:</span>
                            <span class="value">${protocolData.portal_access.login}</span>
                        </div>
                         ${protocolData.portal_access.password ? `
                         <div class="access-login-item" style="margin-left: 15px;">
                            <span class="label">SENHA:</span>
                            <span class="value">${protocolData.portal_access.password}</span>
                        </div>
                         ` : ''}
                    </div>
                     <div class="access-contact">
                        <div style="text-align: center; margin-bottom: 8px;">
                            ${deliveryLogoBase64 ? `<img src="data:image/png;base64,${deliveryLogoBase64}" alt="Logo de Entrega" style="max-height: 40px; max-width: 150px; object-fit: contain;">` : '<div style="max-height: 40px; max-width: 150px; background-color: #f0f0f0; border: 1px solid #ccc; text-align: center; line-height: 40px; font-size: 8px; color: #666;">Logo não disponível</div>'}
                        </div>
                        Fale conosco: 📞 ${clinicData.telefone }
                    </div>
                </div>
                <div class="biopsy-warning">
                    Caso seu exame possua BIOPSIA a mesma somente estará disponível em 15 dias!
                </div>
            </div>
        </div>

        <div class="disclaimer">
            <p>PARA MAIOR COMODIDADE E FACILIDADE, ACESSE SEU LAUDO COM O LINK ACIMA OU COM O QR CODE. ATENÇÃO: O EXAME SOMENTE SERÁ ENTREGUE COM O PROTOCOLO DE ENTREGA. (EXCETO FERIADOS E PONTO FACULTATIVOS)<br>
            *DATA DE ENTREGA DE RESULTADO SUJEITA A ALTERAÇÕES, SEM AVISO PRÉVIO (PARA CASOS EXCEPCIONAIS)*</p>
        </div>
    </div>

    <div class="footer">
        <p>Documento gerado automaticamente pelo sistema InovaMed | Data de geração: ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}</p>
    </div>

</body>
</html>
    `;
    
    const filename = `ficha-paciente-${appointment.patient_name?.replace(/\s+/g, '-').toLowerCase()}-${formatDate(appointment.date).replace(/\//g, '-')}.pdf`;
    printDocument(content, filename);
    showBanner('success', isMobileDevice() ? 'Ficha do paciente baixada com sucesso!' : 'Ficha do paciente enviada para impressão!');
};

const printPreparation = async (appointment) => {
    if (!appointment) {
        showBanner('error','Nenhum agendamento selecionado para impressão.');
        return;
    }
    
    // Busca informações da clínica se não estiverem carregadas
    if (!clinicStore.isLoaded) {
        await clinicStore.fetchClinicData();
    }
    
    // Busca dados completos do serviço
    const serviceData = await getServiceData(appointment.service);
    const clinicData = clinicStore.clinic;
    
    // Busca a logo padrão do backend (inovamed.png)
    let defaultLogoBase64 = '';
    try {
        const defaultLogoResponse = await apiClient.get('/api/v1/clinicsettings/default_logo/');
        defaultLogoBase64 = defaultLogoResponse.data.logo_base64;
    } catch (err) {
        console.log('Logo padrão do backend não disponível, usando logo padrão');
    }
    
    const logoUrl = defaultLogoBase64 ? `data:image/png;base64,${defaultLogoBase64}` : getClinicLogoUrl(clinicData);
    
    // Criar conteúdo do preparo do exame seguindo o padrão do report_template
    const content = `
        <html>
        <head>
            <title>Preparo do Exame - ${appointment.service_name}</title>
            <style>
                body {
                    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                    color: #000;
                    margin: 0;
                    padding: 15px;
                    background: #fff;
                    font-size: 11px;
                    line-height: 1.3;
                }
                .header {
                    text-align: center;
                    margin-bottom: 20px;
                }
                .logo {
                    max-width: 180px;
                    max-height: 70px;
                    margin-bottom: 10px;
                    display: block;
                    margin-left: auto;
                    margin-right: auto;
                }
                .report-title {
                    font-size: 16px;
                    font-weight: bold;
                    margin-bottom: 10px;
                }
                .report-meta {
                    text-align: center;
                    font-size: 11px;
                    color: #222;
                    margin-bottom: 12px;
                }
                .section {
                    margin-bottom: 12px;
                    border: 1px solid #e0e0e0;
                    border-radius: 3px;
                    padding: 10px;
                }
                .section h3 {
                    margin: 0 0 8px 0;
                    color: #333;
                    border-bottom: 1px solid #111;
                    padding-bottom: 3px;
                    font-size: 12px;
                }
                .data-row {
                    display: flex;
                    margin-bottom: 4px;
                    border-bottom: 1px solid #f0f0f0;
                    padding-bottom: 4px;
                    font-size: 10px;
                }
                .data-row:last-child {
                    border-bottom: none;
                    margin-bottom: 0;
                }
                .label {
                    font-weight: bold;
                    min-width: 120px;
                    color: #555;
                }
                .value {
                    flex: 1;
                    color: #000;
                }
                .preparation {
                    background-color: #f8f9fa;
                    padding: 12px;
                    border-radius: 5px;
                    border-left: 3px solid #007bff;
                    margin-top: 8px;
                    font-size: 10px;
                }
                .preparation h4 {
                    color: #007bff;
                    margin: 8px 0 5px 0;
                    font-size: 11px;
                }
                .preparation ul {
                    margin: 5px 0;
                    padding-left: 15px;
                }
                .preparation li {
                    margin-bottom: 3px;
                    line-height: 1.3;
                }
                .warning {
                    color: #dc3545;
                    font-weight: bold;
                    background-color: #f8d7da;
                    padding: 6px;
                    border-radius: 3px;
                    margin-top: 8px;
                    text-align: center;
                    font-size: 9px;
                }
                .contacts {
                    background-color: #e9ecef;
                    padding: 10px;
                    border-radius: 3px;
                    margin-top: 8px;
                    font-size: 9px;
                }
                .contacts p {
                    margin: 3px 0;
                }
                .footer {
                    margin-top: 20px;
                    padding-top: 10px;
                    border-top: 1px solid #ccc;
                    text-align: center;
                    font-size: 8px;
                    color: #333;
                }
                @media print {
                    body { margin: 0; padding: 10px; }
                    .section { break-inside: avoid; }
                }
            </style>
        </head>
        <body>
            <div class="header">
                <img src="${logoUrl}" alt="Logo da Clínica" class="logo">
                <div class="report-title">INSTRUÇÕES DE PREPARO</div>
                <div class="report-meta">
                    Emitido por: ${userStore.nome || 'Sistema'}<br>
                    Unidade: ${clinicData.nome || 'Clínica'}
                </div>
            </div>
            
            <div class="section">
                <h3>DADOS DO PACIENTE</h3>
                <div class="data-row">
                    <span class="label">Nome:</span>
                    <span class="value">${appointment.patient_name}</span>
                </div>
                <div class="data-row">
                    <span class="label">CPF/CNS:</span>
                    <span class="value">${appointment.patient_cpf_cns || 'Não informado'}</span>
                </div>
                <div class="data-row">
                    <span class="label">Telefone:</span>
                    <span class="value">${appointment.phone || 'Não informado'}</span>
                </div>
            </div>
            
            <div class="section">
                <h3>DADOS DO EXAME</h3>
                <div class="data-row">
                    <span class="label">Exame:</span>
                    <span class="value">${appointment.service_name}</span>
                </div>
                <div class="data-row">
                    <span class="label">Data:</span>
                    <span class="value">${formatDate(appointment.date)}</span>
                </div>
                <div class="data-row">
                    <span class="label">Horário:</span>
                    <span class="value">${appointment.time?.slice(0,5)}</span>
                </div>
                <div class="data-row">
                    <span class="label">Número da OS:</span>
                    <span class="value">${appointment.accession_number || 'Não informado'}</span>
                </div>
            </div>
            
            <div class="section">
                <h3>INSTRUÇÕES DE PREPARO</h3>
                <div class="preparation">
                    <p><strong>IMPORTANTE:</strong> Siga rigorosamente estas instruções para garantir a qualidade do exame.</p>
                    
                    <h4>Preparo Geral:</h4>
                    <ul>
                        <li>Compareça com 15 minutos de antecedência</li>
                        <li>Traga documentos de identificação (RG, CPF, CNS)</li>
                        <li>Traga exames anteriores relacionados (se houver)</li>
                        <li>Use roupas confortáveis e sem metais</li>
                        <li>Evite usar joias e acessórios metálicos</li>
                    </ul>
                    
                    ${serviceData && serviceData.preparo ? `
                        <h4>Preparo Específico para ${appointment.service_name}:</h4>
                        <div style="white-space: pre-line; margin: 5px 0;">${serviceData.preparo}</div>
                    ` : `
                        <h4>Preparo Específico para ${appointment.service_name}:</h4>
                        <ul>
                            <li>Jejum de 6 horas (se aplicável ao exame)</li>
                            <li>Evite bebidas alcoólicas 24h antes</li>
                            <li>Informe sobre medicamentos em uso</li>
                            <li>Informe sobre alergias conhecidas</li>
                            <li>Beba bastante água no dia anterior</li>
                        </ul>
                    `}
                    
                    <div class="warning">
                        ⚠️ Em caso de dúvidas, entre em contato com a unidade.
                    </div>
                </div>
            </div>
            
            <div class="section">
                <h3>CONTATOS</h3>
                <div class="contacts">
                    <p><strong>Telefone:</strong> ${clinicData.telefone || '(11) 1234-5678'}</p>
                    <p><strong>Endereço:</strong> ${clinicData.endereco || 'Endereço da clínica'}</p>
                    <p><strong>CNES:</strong> ${clinicData.cnes || 'Não informado'}</p>
                </div>
            </div>
            
            <div class="footer">
                <p>Documento gerado automaticamente pelo sistema InovaMed</p>
                <p>Data de geração: ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}</p>
            </div>
        </body>
        </html>
    `;
    
    const filename = `preparo-exame-${appointment.patient_name?.replace(/\s+/g, '-').toLowerCase()}-${appointment.service_name?.replace(/\s+/g, '-').toLowerCase()}.pdf`;
    printDocument(content, filename);
    showBanner('success', isMobileDevice() ? 'Preparo do exame baixado com sucesso!' : 'Preparo do exame enviado para impressão!');
};

const printAttendanceDeclaration = async (appointment) => {
    if (!appointment) {
        showBanner('error','Nenhum agendamento selecionado para impressão.');
        return;
    }
    
    // Busca informações da clínica se não estiverem carregadas
    if (!clinicStore.isLoaded) {
        await clinicStore.fetchClinicData();
    }
    
    const clinicData = clinicStore.clinic;
    
    // Busca a logo padrão do backend (inovamed.png)
    let defaultLogoBase64 = '';
    try {
        const defaultLogoResponse = await apiClient.get('/api/v1/clinicsettings/default_logo/');
        defaultLogoBase64 = defaultLogoResponse.data.logo_base64;
    } catch (err) {
        console.log('Logo padrão do backend não disponível, usando logo padrão');
    }
    
    const logoUrl = defaultLogoBase64 ? `data:image/png;base64,${defaultLogoBase64}` : getClinicLogoUrl(clinicData);
    
    // Criar conteúdo da declaração de comparecimento seguindo o padrão do report_template
    const content = `
        <html>
        <head>
            <title>Declaração de Comparecimento - ${appointment.patient_name}</title>
            <style>
                body {
                    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                    color: #000;
                    margin: 0;
                    padding: 15px;
                    background: #fff;
                    font-size: 11px;
                    line-height: 1.3;
                }
                .header {
                    text-align: center;
                    margin-bottom: 20px;
                }
                .logo {
                    max-width: 180px;
                    max-height: 70px;
                    margin-bottom: 10px;
                    display: block;
                    margin-left: auto;
                    margin-right: auto;
                }
                .report-title {
                    font-size: 16px;
                    font-weight: bold;
                    margin-bottom: 10px;
                }
                .report-meta {
                    text-align: center;
                    font-size: 11px;
                    color: #222;
                    margin-bottom: 12px;
                }
                .section {
                    margin-bottom: 12px;
                    border: 1px solid #e0e0e0;
                    border-radius: 3px;
                    padding: 10px;
                }
                .section h3 {
                    margin: 0 0 8px 0;
                    color: #333;
                    border-bottom: 1px solid #111;
                    padding-bottom: 3px;
                    font-size: 12px;
                }
                .data-row {
                    display: flex;
                    margin-bottom: 4px;
                    border-bottom: 1px solid #f0f0f0;
                    padding-bottom: 4px;
                    font-size: 10px;
                }
                .data-row:last-child {
                    border-bottom: none;
                    margin-bottom: 0;
                }
                .label {
                    font-weight: bold;
                    min-width: 120px;
                    color: #555;
                }
                .value {
                    flex: 1;
                    color: #000;
                }
                .declaration {
                    background-color: #f8f9fa;
                    padding: 15px;
                    border: 2px solid #28a745;
                    border-radius: 5px;
                    text-align: justify;
                    line-height: 1.4;
                    margin-top: 8px;
                    font-size: 10px;
                }
                .declaration p {
                    margin-bottom: 8px;
                }
                .declaration p:last-child {
                    margin-bottom: 0;
                }
                .signature {
                    margin-top: 25px;
                    text-align: center;
                    border-top: 1px solid #ccc;
                    padding-top: 10px;
                }
                .signature p {
                    margin: 5px 0;
                    font-size: 9px;
                }
                .signature-line {
                    border-top: 1px solid #000;
                    width: 250px;
                    margin: 10px auto;
                    height: 1px;
                }
                .clinic-info {
                    background-color: #e9ecef;
                    padding: 10px;
                    border-radius: 3px;
                    margin-top: 8px;
                    font-size: 9px;
                }
                .clinic-info p {
                    margin: 3px 0;
                }
                .footer {
                    margin-top: 20px;
                    padding-top: 10px;
                    border-top: 1px solid #ccc;
                    text-align: center;
                    font-size: 8px;
                    color: #333;
                }
                @media print {
                    body { margin: 0; padding: 10px; }
                    .section { break-inside: avoid; }
                }
            </style>
        </head>
        <body>
            <div class="header">
                <img src="${logoUrl}" alt="Logo da Clínica" class="logo">
                <div class="report-title">DECLARAÇÃO DE COMPARECIMENTO</div>
                <div class="report-meta">
                    Emitido por: ${userStore.nome || 'Sistema'}<br>
                    Unidade: ${clinicData.nome || 'Clínica'}
                </div>
            </div>
            
            <div class="section">
                <h3>DADOS DO PACIENTE</h3>
                <div class="data-row">
                    <span class="label">Nome:</span>
                    <span class="value">${appointment.patient_name}</span>
                </div>
                <div class="data-row">
                    <span class="label">CPF/CNS:</span>
                    <span class="value">${appointment.patient_cpf_cns || 'Não informado'}</span>
                </div>
                <div class="data-row">
                    <span class="label">Telefone:</span>
                    <span class="value">${appointment.phone || 'Não informado'}</span>
                </div>
            </div>
            
            <div class="section">
                <h3>DADOS DO ATENDIMENTO</h3>
                <div class="data-row">
                    <span class="label">Data:</span>
                    <span class="value">${formatDate(appointment.date)}</span>
                </div>
                <div class="data-row">
                    <span class="label">Horário:</span>
                    <span class="value">${appointment.time?.slice(0,5)}</span>
                </div>
                <div class="data-row">
                    <span class="label">Exame:</span>
                    <span class="value">${appointment.service_name}</span>
                </div>
                <div class="data-row">
                    <span class="label">Status:</span>
                    <span class="value">${getStatusLabel(appointment.status)}</span>
                </div>
                <div class="data-row">
                    <span class="label">Número da OS:</span>
                    <span class="value">${appointment.accession_number || 'Não informado'}</span>
                </div>
            </div>
            
            <div class="section">
                <h3>DECLARAÇÃO</h3>
                <div class="declaration">
                    <p><strong>DECLARAMOS</strong> que o(a) paciente <strong>${appointment.patient_name}</strong>, portador(a) do documento <strong>${appointment.patient_cpf_cns || 'não informado'}</strong>, compareceu a esta unidade no dia <strong>${formatDate(appointment.date)}</strong> às <strong>${appointment.time?.slice(0,5)}</strong> para realização do exame <strong>${appointment.service_name}</strong>.<br><strong>Final do atendimento:</strong> <strong>${appointment.attended_at ? new Date(appointment.attended_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : 'não informado'}</strong></p>
                    
                    <p>Esta declaração é válida para fins de justificativa de ausência escolar, trabalho ou outros fins legais.</p>
                    
                    <p><strong>Local e Data:</strong> ${clinicData.endereco ? clinicData.endereco.split(',')[0] : 'São Paulo'}, ${new Date().toLocaleDateString('pt-BR')}</p>
                </div>
            </div>
            
            <div class="signature">
                <div class="signature-line"></div>
                <p><strong>Assinatura do Responsável</strong></p>
                <p>Carimbo da Unidade</p>
            </div>
            
            <div class="section">
                <h3>INFORMAÇÕES DA UNIDADE</h3>
                <div class="clinic-info">
                    <p><strong>Unidade:</strong> ${clinicData.nome || 'Clínica Médica'}</p>
                    <p><strong>Endereço:</strong> ${clinicData.endereco || 'Endereço da clínica'}</p>
                    <p><strong>CNES:</strong> ${clinicData.cnes || 'Não informado'}</p>
                    <p><strong>Telefone:</strong> ${clinicData.telefone || '(11) 1234-5678'}</p>
                </div>
            </div>
            
            <div class="footer">
                <p>Documento gerado automaticamente pelo sistema InovaMed</p>
                <p>Data de geração: ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}</p>
            </div>
        </body>
        </html>
    `;
    
    const filename = `declaracao-comparecimento-${appointment.patient_name?.replace(/\s+/g, '-').toLowerCase()}-${formatDate(appointment.date).replace(/\//g, '-')}.pdf`;
    printDocument(content, filename);
    showBanner('success', isMobileDevice() ? 'Declaração de comparecimento baixada com sucesso!' : 'Declaração de comparecimento enviada para impressão!');
};

// Função auxiliar para obter a URL do logo da clínica
const getClinicLogoUrl = (clinicData) => {
    if (clinicData.logo) {
        // Se o logo já tem http, retorna como está
        if (clinicData.logo.startsWith('http')) {
            return clinicData.logo;
        }
        // Se não tem http, adiciona o host do backend
        return `http://localhost:8000${clinicData.logo}`;
    }
    return '/styles/public/logo.png';
};



// Função auxiliar para buscar dados completos do serviço
const getServiceData = async (serviceId) => {
    try {
        const response = await apiClient.get(`/api/v1/services/get/${serviceId}/`);
        return response.data;
    } catch (error) {
        console.error('Erro ao buscar dados do serviço:', error);
        return null;
    }
};

// Função para detectar dispositivos móveis
const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (navigator.maxTouchPoints && navigator.maxTouchPoints > 2 && /MacIntel/.test(navigator.platform));
};

// Função para criar PDF como blob e fazer download
const downloadPDF = (content, filename = 'documento.pdf') => {
    // Cria um elemento temporário para renderizar o HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.top = '-9999px';
    document.body.appendChild(tempDiv);
    
    // Usa html2canvas e jsPDF para gerar o PDF
    import('html2canvas').then(html2canvas => {
        import('jspdf').then(({ jsPDF }) => {
            html2canvas.default(tempDiv, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff'
            }).then(canvas => {
                const imgData = canvas.toDataURL('image/png');
                const pdf = new jsPDF('p', 'mm', 'a4');
                const imgWidth = 210;
                const pageHeight = 295;
                const imgHeight = (canvas.height * imgWidth) / canvas.width;
                let heightLeft = imgHeight;
                let position = 0;
                
                pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;
                
                while (heightLeft >= 0) {
                    position = heightLeft - imgHeight;
                    pdf.addPage();
                    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
                    heightLeft -= pageHeight;
                }
                
                // Remove o elemento temporário
                document.body.removeChild(tempDiv);
                
                // Faz o download do PDF
                pdf.save(filename);
            }).catch(error => {
                console.error('Erro ao gerar PDF:', error);
                document.body.removeChild(tempDiv);
                showBanner('error', 'Erro ao gerar PDF. Tente novamente.');
            });
        }).catch(error => {
            console.error('Erro ao carregar jsPDF:', error);
            document.body.removeChild(tempDiv);
            showBanner('error', 'Erro ao carregar biblioteca de PDF.');
        });
    }).catch(error => {
        console.error('Erro ao carregar html2canvas:', error);
        document.body.removeChild(tempDiv);
        showBanner('error', 'Erro ao carregar biblioteca de renderização.');
    });
};

// Função auxiliar para imprimir documentos
const printDocument = (content, filename = 'documento.pdf') => {
    // Se for dispositivo móvel, faz download do PDF
    if (isMobileDevice()) {
        downloadPDF(content, filename);
        return;
    }
    
    // Comportamento original para desktop
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
        // Fallback se window.open falhar
        showBanner('error', 'Popup bloqueado. Por favor, permita popups para este site.');
        downloadPDF(content, filename);
        return;
    }
    
    printWindow.document.write(content);
    printWindow.document.close();
    
    // Aguarda o carregamento e imprime
    printWindow.onload = function() {
        try {
            printWindow.print();
            printWindow.close();
        } catch (error) {
            console.error('Erro ao imprimir:', error);
            printWindow.close();
            // Fallback para download
            downloadPDF(content, filename);
        }
    };
};

const banner = ref({ show: false, type: 'success', message: '' });

function showBanner(type, message) {
  // Garante que a mensagem seja uma string
  let formattedMessage = String(message || '');
  
  // Se a mensagem vier do backend com detalhes, formata melhor
  if (typeof message === 'string' && message.length > 0) {
    // Remove prefixos comuns de erro do backend
    formattedMessage = message
      .replace(/^Erro ao .*?: /, '')
      .replace(/^Ocorreu um erro ao .*?: /, '')
      .replace(/^Erro: /, '')
      .replace(/^Erro desconhecido$/, 'Ocorreu um erro inesperado. Tente novamente.')
      .replace(/^Erro ao realizar a pesquisa: /, '')
      .replace(/^Erro ao confirmar agendamento: /, '')
      .replace(/^Erro ao finalizar anamnese: /, '')
      .replace(/^Erro ao cancelar agendamento: /, '')
      .replace(/^Erro ao reagendar agendamento: /, '')
      .replace(/^Erro ao gerar worklist: /, '')
      .replace(/^Erro ao marcar atendimento: /, '')
      .replace(/^Erro ao carregar histórico do paciente: /, '')
      .replace(/^Erro ao importar agenda: /, '')
      .replace(/^Erro ao atualizar agendamento: /, '')
      .replace(/^Erro ao gerar link de compartilhamento: /, '');
    
    // Trata mensagens específicas do backend
    if (formattedMessage.includes('Input should be a valid integer')) {
      formattedMessage = 'Valor inválido fornecido. Verifique os dados e tente novamente.';
    } else if (formattedMessage.includes('unable to parse string as an integer')) {
      formattedMessage = 'Formato de número inválido. Verifique os dados e tente novamente.';
    } else if (formattedMessage.includes('not found')) {
      formattedMessage = 'Registro não encontrado. Verifique os dados e tente novamente.';
    } else if (formattedMessage.includes('permission')) {
      formattedMessage = 'Você não tem permissão para realizar esta ação.';
    } else if (formattedMessage.includes('authentication')) {
      formattedMessage = 'Sessão expirada. Faça login novamente.';
    }
  }
  
  // Capitaliza a primeira letra se necessário
  if (formattedMessage && formattedMessage.length > 0) {
    formattedMessage = formattedMessage.charAt(0).toUpperCase() + formattedMessage.slice(1);
  }
  
  banner.value = { show: true, type, message: formattedMessage };
}
function closeBanner() {
  banner.value.show = false;
}

// Adicionar computed property para filtrar ações do menu de contexto
const filteredContextMenuActions = computed(() => {
    if (!contextMenu.value.actions) return [];
    return contextMenu.value.actions.filter(action => !action.submenu);
});

const openImportModal = async () => {
    // Verifica se a agenda tem serviços vinculados antes de abrir o modal
    if (!isValidScheduleId()) {
        showBanner('error', 'Selecione uma agenda válida para importar dados.');
        return;
    }

    try {
        // Busca os dados da agenda atual para verificar se tem serviços vinculados
        const response = await apiClient.get(`/api/v1/schedules/${selectedAgendaId.value}/`);
        const scheduleData = response.data;
        
        // Armazena os serviços da agenda atual
        currentScheduleServices.value = scheduleData.services || [];
        
        if (!scheduleData.services || scheduleData.services.length === 0) {
            showBanner('warning', 'Esta agenda não possui serviços vinculados. A importação do SISREG pode falhar se os serviços não estiverem cadastrados e vinculados à agenda. Recomendamos vincular os serviços necessários antes de importar.');
        }
        
        isImportModalOpen.value = true;
        selectedFile.value = null;
        filePreview.value = '';
        if (fileInput.value) {
            fileInput.value.value = '';
        }
    } catch (error) {
        console.error('Erro ao verificar dados da agenda:', error);
        showBanner('error', 'Erro ao verificar dados da agenda. Tente novamente.');
    }
};

const closeImportModal = () => {
    isImportModalOpen.value = false;
    selectedFile.value = null;
    filePreview.value = '';
    importProgress.value = 0;
    importStatus.value = '';
    currentScheduleServices.value = [];
    if (fileInput.value) {
        fileInput.value.value = '';
    }
};

// --- ESTADO PARA IMPORTAÇÃO SISREG ---
const selectedFile = ref(null)
const filePreview = ref('')
const isImporting = ref(false)
const fileInput = ref(null)
const importProgress = ref(0)
const importStatus = ref('')
const currentScheduleServices = ref([])

const handleImportSisreg = async () => {
    if (!selectedFile.value || !isValidScheduleId()) {
        showBanner('error','Selecione um arquivo e uma agenda válida.');
        return;
    }

    isImporting.value = true;
    importProgress.value = 0;
    importStatus.value = 'Iniciando importação...';
    
    try {
        // Simula progresso inicial com frases motivacionais
        await simulateProgress(15, 'Só mais um momento... Lendo o arquivo SISREG');
        
        // Lê o conteúdo do arquivo
        const fileContent = await readFileContent(selectedFile.value);
        await simulateProgress(35, 'Arquivo lido com sucesso! Analisando estrutura dos dados...');
        
        // Validação de serviços - verificar se os serviços do arquivo correspondem à agenda
        await simulateProgress(40, 'Verificando compatibilidade dos serviços...');
        
       
        const lines = fileContent.split('\n');
        const fileServices = new Set();
        const scheduleServices = new Set();
        
        // Extrair serviços do arquivo SISREG (coluna 6 - descrição do serviço)
        for (const line of lines) {
            if (line.trim() && !line.startsWith('data')) {
                const lineData = line.split(';');
                if (lineData.length > 5 && lineData[5].trim()) {
                    fileServices.add(lineData[5].trim().toUpperCase());
                }
            }
        }
        
        // Buscar serviços da agenda selecionada
        try {
            const scheduleResponse = await apiClient.get(`/api/v1/schedules/${selectedAgendaId.value}/`);
            const scheduleData = scheduleResponse.data;
            
            if (scheduleData.services && scheduleData.services.length > 0) {
                scheduleData.services.forEach(service => {
                    if (service.descricao) {
                        scheduleServices.add(service.descricao.trim().toUpperCase());
                    }
                });
            }
            
            // Validação estrita de serviços - bloquear importação de serviços incompatíveis
            if (fileServices.size > 0 && scheduleServices.size > 0) {
                const fileServicesArray = Array.from(fileServices);
                const scheduleServicesArray = Array.from(scheduleServices);
                
                console.log('=== DEBUG: Validando serviços ===');
                console.log('Serviços no arquivo SISREG:', fileServicesArray);
                console.log('Serviços na agenda:', scheduleServicesArray);
                
                // Verificar se há incompatibilidade crítica entre ultrassom e mamografia
                const hasUltrasoundService = fileServicesArray.some(service => {
                    const upperService = service.toUpperCase();
                    const result = upperService.includes('ULTRASSONOGRAFIA') ||
                           upperService.includes('ULTRASSOM') ||
                           upperService.includes('ECOGRAFIA') ||
                           upperService.includes('ULTRASOUND');
                    console.log('Serviço arquivo:', service, 'é ultrassom?', result);
                    return result;
                });
                
                const hasMammographyService = fileServicesArray.some(service => {
                    const upperService = service.toUpperCase();
                    const result = upperService.includes('MAMOGRAFIA') ||
                           upperService.includes('MAMOGRAMA');
                    console.log('Serviço arquivo:', service, 'é mamografia?', result);
                    return result;
                });
                
                const scheduleHasMammography = scheduleServicesArray.some(service => {
                    const upperService = service.toUpperCase();
                    const result = upperService.includes('MAMOGRAFIA') ||
                           upperService.includes('MAMOGRAMA');
                    console.log('Serviço agenda:', service, 'é mamografia?', result);
                    return result;
                });
                
                const scheduleHasUltrasound = scheduleServicesArray.some(service => {
                    const upperService = service.toUpperCase();
                    const result = upperService.includes('ULTRASSONOGRAFIA') ||
                           upperService.includes('ULTRASSOM') ||
                           upperService.includes('ECOGRAFIA') ||
                           upperService.includes('ULTRASOUND');
                    console.log('Serviço agenda:', service, 'é ultrassom?', result);
                    return result;
                });
                
                console.log('Tem ultrassom no arquivo:', hasUltrasoundService);
                console.log('Tem mamografia no arquivo:', hasMammographyService);
                console.log('Tem mamografia na agenda:', scheduleHasMammography);
                console.log('Tem ultrassom na agenda:', scheduleHasUltrasound);
                
                // Criar mensagem específica para o caso de ultrassom vs mamografia
                let incompatibleMessage = '';
                if (hasUltrasoundService && scheduleHasMammography) {
                    incompatibleMessage = 'ULTRASSONOGRAFIA não pode ser agendada em agenda de MAMOGRAFIA';
                } else if (hasMammographyService && scheduleHasUltrasound) {
                    incompatibleMessage = 'MAMOGRAFIA não pode ser agendada em agenda de ULTRASSONOGRAFIA';
                }
                
                // Bloquear importação se houver incompatibilidade crítica
                if ((hasUltrasoundService && scheduleHasMammography) || 
                    (hasMammographyService && scheduleHasUltrasound)) {
                    
                    const fileServicesList = fileServicesArray.join(', ');
                    const scheduleServicesList = scheduleServicesArray.join(', ');
                    
                    console.log('BLOQUEANDO importação: incompatibilidade crítica detectada');
                    
                    showBanner('error', 
                        `SERVIÇOS INCOMPATÍVEIS DETECTADOS!\n\n` +
                        `O arquivo SISREG não pode ser importado nesta agenda.\n\n` +
                        `Serviço detectado: ${fileServicesList}\n` +
                        `Agenda configurada para: ${scheduleServicesList}\n\n` +
                        `ERRO: ${incompatibleMessage}\n\n` +
                        `Por favor, selecione uma agenda apropriada para este tipo de serviço.`
                    );
                    isImporting.value = false;
                    return;
                }
                
                // Verificar compatibilidade direta - exigir correspondência exata ou muito próxima
                let hasExactMatch = false;
                for (const fileService of fileServicesArray) {
                    const fileServiceUpper = fileService.toUpperCase();
                    
                    for (const scheduleService of scheduleServicesArray) {
                        const scheduleServiceUpper = scheduleService.toUpperCase();
                        
                        // Verificar correspondência exata ou muito próxima
                        if (fileServiceUpper === scheduleServiceUpper ||
                            fileServiceUpper.includes(scheduleServiceUpper) ||
                            scheduleServiceUpper.includes(fileServiceUpper)) {
                            hasExactMatch = true;
                            break;
                        }
                    }
                    if (hasExactMatch) break;
                }
                
                // Se não houver correspondência exata, bloquear importação
                if (!hasExactMatch) {
                    const fileServicesList = fileServicesArray.join(', ');
                    const scheduleServicesList = scheduleServicesArray.join(', ');
                    
                    showBanner('error', 
                        `SERVIÇOS NÃO COMPATÍVEIS!\n\n` +
                        `Nenhum serviço do arquivo corresponde aos serviços da agenda.\n\n` +
                        `Serviços no arquivo: ${fileServicesList}\n` +
                        `Serviços na agenda: ${scheduleServicesList}\n\n` +
                        `Por favor, selecione uma agenda apropriada.`
                    );
                    isImporting.value = false;
                    return;
                }
                
                console.log('Validação de serviços concluída - importação permitida');
            }
        } catch (error) {
            console.error('Erro ao verificar serviços da agenda:', error);
            // Continuar mesmo se não conseguir verificar os serviços
        }
        
        // Usa o selectedAgendaId que já tem validação adequada
        const scheduleId = selectedAgendaId.value;
        
        console.log('Import: usando scheduleId:', scheduleId, 'tipo:', typeof scheduleId);
        
        if (!isValidScheduleId(scheduleId)) {
            showBanner('error','ID da agenda inválido para importação.');
            return;
        }

        await simulateProgress(55, 'Preparando dados para importação... Detectando campos do arquivo');

        // Verificação adicional antes de enviar para o backend
        console.log('=== Verificação final antes do envio ao backend ===');
        console.log('Schedule ID:', scheduleId);
        console.log('File content length:', fileContent.length);
        
        // Criar array de serviços do arquivo para envio ao backend
        const fileServicesList = [];
        
        lines.forEach((line) => {
            if (line.trim() && !line.startsWith('data')) {
                const lineData = line.split(';');
                const serviceName = lineData[5] || ''; // Índice 5 para serviço (coluna 6 no SISREG)
                if (serviceName.trim()) {
                    fileServicesList.push(serviceName.trim().toUpperCase());
                }
            }
        });
        
        console.log('Serviços encontrados para validação backend:', fileServicesList);

        // Validação estrita antes de enviar ao backend
        if (fileServicesList.length > 0) {
            // Buscar serviços da agenda selecionada
            try {
                const scheduleResponse = await apiClient.get(`/api/v1/schedules/${scheduleId}/`);
                const scheduleData = scheduleResponse.data;
                const scheduleServices = new Set();
                
                if (scheduleData.services && scheduleData.services.length > 0) {
                    scheduleData.services.forEach(service => {
                        if (service.descricao) {
                            scheduleServices.add(service.descricao.trim().toUpperCase());
                        }
                    });
                }
                
                const scheduleServicesArray = Array.from(scheduleServices);
                
                // Verificar incompatibilidade crítica
                const hasUltrasoundService = fileServicesList.some(service => 
                    service.includes('ULTRASSONOGRAFIA') || service.includes('ULTRASSOM') ||
                    service.includes('ECOGRAFIA') || service.includes('ULTRASOUND')
                );
                
                const hasMammographyService = fileServicesList.some(service => 
                    service.includes('MAMOGRAFIA') || service.includes('MAMOGRAMA')
                );
                
                const scheduleHasMammography = scheduleServicesArray.some(service => 
                    service.includes('MAMOGRAFIA') || service.includes('MAMOGRAMA')
                );
                
                const scheduleHasUltrasound = scheduleServicesArray.some(service => 
                    service.includes('ULTRASSONOGRAFIA') || service.includes('ULTRASSOM') ||
                    service.includes('ECOGRAFIA') || service.includes('ULTRASOUND')
                );
                
                // Bloquear importação se houver incompatibilidade
                if ((hasUltrasoundService && scheduleHasMammography) || 
                    (hasMammographyService && scheduleHasUltrasound)) {
                    
                    let incompatibleMessage = '';
                    if (hasUltrasoundService && scheduleHasMammography) {
                        incompatibleMessage = 'ULTRASSONOGRAFIA não pode ser agendada em agenda de MAMOGRAFIA';
                    } else if (hasMammographyService && scheduleHasUltrasound) {
                        incompatibleMessage = 'MAMOGRAFIA não pode ser agendada em agenda de ULTRASSONOGRAFIA';
                    }
                    
                    const fileServicesStr = fileServicesList.join(', ');
                    const scheduleServicesStr = scheduleServicesArray.join(', ');
                    
                    showBanner('error', 
                        `SERVIÇOS INCOMPATÍVEIS DETECTADOS!\n\n` +
                        `O arquivo SISREG não pode ser importado nesta agenda.\n\n` +
                        `Serviços no arquivo: ${fileServicesStr}\n` +
                        `Serviços na agenda: ${scheduleServicesStr}\n\n` +
                        `ERRO: ${incompatibleMessage}\n\n` +
                        `Por favor, selecione uma agenda apropriada para este tipo de serviço.`
                    );
                    isImporting.value = false;
                    return;
                }
                
            } catch (error) {
                console.error('Erro na validação prévia:', error);
            }
        }

        console.log('=== ENVIANDO PARA O BACKEND ===');
        console.log('Schedule ID:', scheduleId);
        console.log('Tamanho do arquivo:', fileContent.length);
        console.log('Serviços para validação:', fileServicesList);
        
        // Verificação final antes de enviar
        if (!scheduleId) {
            showBanner('error', 'Agenda não selecionada. Por favor, selecione uma agenda.');
            isImporting.value = false;
            return;
        }
        
        if (!fileContent || fileContent.trim() === '') {
            showBanner('error', 'Arquivo vazio ou inválido. Por favor, selecione um arquivo válido.');
            isImporting.value = false;
            return;
        }
        
        console.log('=== DADOS PRONTOS PARA ENVIO ===');
        console.log('Endpoint:', `/api/v1/schedules/${scheduleId}/import-sisreg/`);
        console.log('Payload:', {
            schedule_id: scheduleId,
            file_content_length: fileContent.length,
            validate_services: true,
            file_services_count: fileServicesList.length
        });
        
        // Envia para o backend com validação de serviços
        const response = await apiClient.post(`/api/v1/schedules/${scheduleId}/import-sisreg/`, {
            schedule_id: scheduleId,
            file_content: fileContent,
            validate_services: true,
            file_services: fileServicesList
        });
        
        await simulateProgress(85, 'Processando dados... Criando agendamentos no sistema');
        
        const importData = response.data;
        
        // Processar conflitos de horário criando agendamentos para todos os pacientes
        if (importData.errors && importData.errors.length > 0) {
            const timeConflicts = importData.errors.filter(error => 
                error.includes('Horário já ocupado') || error.includes('time conflict')
            );
            
            if (timeConflicts.length > 0) {
                await simulateProgress(90, 'Processando conflitos... Criando agendamentos para todos os pacientes...');
                
                // Processar cada conflito e criar agendamento com paciente
                const processedConflicts = await processTimeConflicts(timeConflicts, scheduleId, fileContent);
                
                // Atualizar o resultado com os novos agendamentos criados
                importData.appointments_created = processedConflicts.appointments_created;
                importData.errors = processedConflicts.remaining_errors;
                
                if (processedConflicts.appointments_created > 0) {
                    showBanner('success', 
                        `Importação concluída! ${importData.imported_count || 0} agendamentos importados, ` +
                        `${processedConflicts.appointments_created} agendamentos criados para conflitos.`
                    );
                }
            }
        }
        
        importResult.value = importData;
        closeImportModal();
        
        await simulateProgress(100, 'Importação concluída! Verificando resultados...');
        
        // Mensagem de sucesso mais detalhada
        let successMessage = `Importação concluída! ${importData.imported_count || 0} agendamentos importados`;
        
        if (importData.appointments_created && importData.appointments_created > 0) {
            if (importData.appointments_created > 0) {
                successMessage += `, ${importData.appointments_created} criados para conflitos (horários especiais)`;
            }
            
            // Adicionar detalhes sobre tipos de agendamentos criados
            if (importData.appointments && importData.appointments.length > 0) {
                const specialSlots = importData.appointments.filter(a => a.type === 'special_slot').length;
                
                if (specialSlots > 0) {
                    successMessage += ` (${specialSlots} em horários especiais)`;
                }
            }
        }
        
        if (importData.success) {
            showBanner('success', successMessage);
        }
    } catch (err) {
        console.error('=== ERRO CRÍTICO NA IMPORTAÇÃO ===');
        console.error('Tipo do erro:', err.constructor.name);
        console.error('Mensagem:', err.message);
        console.error('Stack:', err.stack);
        console.error('Response:', err.response);
        console.error('Status:', err.response?.status);
        console.error('Data:', err.response?.data);
        
        // Verificar se é erro de serviço incompatível
        const errorData = err.response?.data;
        let errorMessage = errorData?.detail || errorData?.message || err.message || 'Erro desconhecido';
        
        console.error('Mensagem de erro final:', errorMessage);
        
        // Se for erro de validação de serviços, exibir mensagem específica
        if (errorData?.error_type === 'service_incompatible' || 
            errorMessage.toLowerCase().includes('serviço') || 
            errorMessage.toLowerCase().includes('incompatível') ||
            errorMessage.toLowerCase().includes('ultrassonografia') ||
            errorMessage.toLowerCase().includes('mamografia')) {
            
            const fileServices = errorData?.file_services || [];
            const scheduleServices = errorData?.schedule_services || [];
            
            errorMessage = `SERVIÇOS INCOMPATÍVEIS DETECTADOS!\n\n` +
                           `Serviços no arquivo: ${fileServices.join(', ')}\n` +
                           `Serviços na agenda: ${scheduleServices.join(', ')}\n\n` +
                           `Este arquivo não pode ser importado nesta agenda.\n` +
                           `Por favor, selecione uma agenda apropriada para o tipo de serviço.`;
        } else {
            // Para erros genéricos, tentar extruir mais informações
            const status = err.response?.status;
            const detail = errorData?.detail;
            
            if (status === 400) {
                errorMessage = `Erro de validação: ${detail || 'Dados inválidos no arquivo'}`;
            } else if (status === 404) {
                errorMessage = `Agenda não encontrada. ID: ${selectedAgendaId.value}`;
            } else if (status === 500) {
                errorMessage = `Erro no servidor. Tente novamente mais tarde. Detalhes: ${detail || 'Erro interno'}`;
            } else if (err.message?.includes('Network')) {
                errorMessage = `Erro de conexão. Verifique sua internet e tente novamente.`;
            } else {
                errorMessage = `Erro ao importar: ${errorMessage}`;
            }
        }
        
        console.error('Mensagem exibida ao usuário:', errorMessage);
        showBanner('error', errorMessage);
    } finally {
        isImporting.value = false;
        importProgress.value = 0;
        importStatus.value = '';
    }
};

// Função para processar conflitos de horário criando agendamentos diretamente
const processTimeConflicts = async (conflicts, scheduleId, fileContent) => {
    try {
        const lines = fileContent.split('\n');
        const appointmentsCreated = [];
        const remainingErrors = [];
        
        // Verificar serviços da agenda antes de processar conflitos
        let scheduleServices = new Set();
        try {
            const scheduleResponse = await apiClient.get(`/api/v1/schedules/${scheduleId}/`);
            if (scheduleResponse.data && scheduleResponse.data.services) {
                scheduleResponse.data.services.forEach(service => {
                    if (service.descricao) {
                        scheduleServices.add(service.descricao.trim().toUpperCase());
                    }
                });
            }
        } catch (error) {
            console.error('Erro ao buscar serviços da agenda:', error);
        }
        
        const scheduleServicesArray = Array.from(scheduleServices);
        
        // Processar cada conflito
        for (const conflict of conflicts) {
            const match = conflict.match(/Linha (\d+):.*?(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}):\d{2}/);
            if (match) {
                const lineNumber = parseInt(match[1]) - 1; // Ajustar para índice 0
                const conflictDate = match[2];
                const conflictTime = match[3];
                
                if (lines[lineNumber]) {
                    const lineData = lines[lineNumber].split(';');
                    
                    // Extrair serviço da linha SISREG
                    const serviceName = lineData[5] || '';
                    const serviceNameUpper = serviceName.toUpperCase();
                    
                    // Validar compatibilidade do serviço
                    const hasUltrasoundService = serviceNameUpper.includes('ULTRASSONOGRAFIA') ||
                                               serviceNameUpper.includes('ULTRASSOM') ||
                                               serviceNameUpper.includes('ECOGRAFIA') ||
                                               serviceNameUpper.includes('ULTRASOUND');
                    
                    const hasMammographyService = serviceNameUpper.includes('MAMOGRAFIA') ||
                                                serviceNameUpper.includes('MAMOGRAMA');
                    
                    const scheduleHasMammography = scheduleServicesArray.some(s => 
                        s.includes('MAMOGRAFIA') || s.includes('MAMOGRAMA'));
                    
                    const scheduleHasUltrasound = scheduleServicesArray.some(s => 
                        s.includes('ULTRASSONOGRAFIA') || s.includes('ULTRASSOM') ||
                        s.includes('ECOGRAFIA') || s.includes('ULTRASOUND'));
                    
                    // Verificar incompatibilidade crítica
                    if ((hasUltrasoundService && scheduleHasMammography) || 
                        (hasMammographyService && scheduleHasUltrasound)) {
                        
                        console.log(`BLOQUEANDO conflito: ${serviceName} incompatível com agenda`);
                        remainingErrors.push(`Linha ${lineNumber + 1}: Serviço "${serviceName}" incompatível com esta agenda`);
                        continue; // Pular para próximo conflito
                    }
                    
                    // Extrair dados do paciente da linha SISREG
                    const patientData = {
                        name: lineData[7] || 'Paciente SISREG',
                        cpf: lineData[6] || '',
                        phone: lineData[12] || '',
                        email: lineData[13] || '',
                        birth_date: lineData[9] || conflictDate,
                        health_insurance: 'SUS',
                        health_insurance_number: lineData[8] || ''
                    };
                    
                    // Primeiro tentar criar horário especial para garantir que o paciente seja agendado
                    try {
                        const specialSlotData = {
                            date: conflictDate,
                            time: conflictTime,
                            reason: `Horário especial para ${patientData.name} - SISREG Linha ${lineNumber + 1}`,
                            is_available: false, // Marcar como ocupado
                            notes: `Paciente: ${patientData.name} - Serviço: ${serviceName} - Importado SISREG`,
                            patient_name: patientData.name,
                            patient_cpf: patientData.cpf,
                            patient_phone: patientData.phone,
                            health_insurance: patientData.health_insurance,
                            procedencia: 'SISREG',
                            motivo_marcacao: `Importação SISREG - ${serviceName}`
                        };
                        
                        const slotResponse = await apiClient.post(`/api/v1/schedules/${scheduleId}/special-slots/`, specialSlotData);
                        appointmentsCreated.push({
                            line: lineNumber + 1,
                            date: conflictDate,
                            time: conflictTime,
                            patient_name: patientData.name,
                            service_name: serviceName,
                            special_slot_id: slotResponse.data.id,
                            type: 'special_slot'
                        });
                        
                        console.log(`Horário especial criado para ${patientData.name} (${serviceName}) às ${conflictTime}`);
                        
                    } catch (slotError) {
                        console.error('Erro ao criar horário especial:', slotError);
                        remainingErrors.push(conflict);
                    }
                }
            } else {
                remainingErrors.push(conflict);
            }
        }
        
        return {
            appointments_created: appointmentsCreated.length,
            remaining_errors: remainingErrors,
            appointments: appointmentsCreated
        };
    } catch (error) {
        console.error('Erro ao processar conflitos:', error);
        return {
            appointments_created: 0,
            remaining_errors: conflicts,
            appointments: []
        };
    }
};

// Função para simular progresso com animação suave
const simulateProgress = async (targetProgress, status) => {
    const currentProgress = importProgress.value;
    const increment = (targetProgress - currentProgress) / 25; // Mais passos para animação mais suave
    
    for (let i = 0; i < 25; i++) {
        importProgress.value = Math.min(currentProgress + (increment * (i + 1)), targetProgress);
        importStatus.value = status;
        await new Promise(resolve => setTimeout(resolve, 40)); // Intervalo menor para animação mais fluida
    }
    
    importProgress.value = targetProgress;
    importStatus.value = status;
};



// Função para formatar tamanho de arquivo
const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const readFileContent = (file) => {
    return new Promise((resolve, reject) => {
        const fileReader = new FileReader();
        fileReader.onload = (e) => resolve(e.target.result);
        fileReader.onerror = (e) => reject(e);
        fileReader.readAsText(file);
    });
};

const closeImportResult = () => {
    importResult.value = null;
};

const refreshAfterImport = () => {
    fetchAppointments();
    closeImportResult();
};

const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) {
        selectedFile.value = null;
        filePreview.value = '';
        return;
    }
    
    // Verifica se é um arquivo TXT
    if (!file.name.toLowerCase().endsWith('.txt')) {
        showBanner('error','Por favor, selecione apenas arquivos TXT.');
        event.target.value = '';
        return;
    }
    
    selectedFile.value = file;
    filePreview.value = '';
    
    // Lê as primeiras linhas para preview
    const reader = new FileReader();
    reader.onload = (e) => {
        const content = e.target.result;
        const lines = content.split('\n').slice(0, 3);
        filePreview.value = lines.join('\n');
    };
    reader.readAsText(file);
};

const isDragActive = ref(false)

const triggerFileInput = () => {
    if (fileInput.value) fileInput.value.click();
}

const onDragOver = () => {
    isDragActive.value = true;
}
const onDragLeave = () => {
    isDragActive.value = false;
}
const onDropFile = (event) => {
    isDragActive.value = false;
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
        handleFileSelect({ target: { files } });
    }
}

// --- ESTADO PARA MODAL DE PESQUISA ---
const isSearchModalOpen = ref(false)
const searchType = ref('accession') // 'accession', 'patient_id', 'patient_name', 'patient_cpf'
const searchQuery = ref('')
const searchResults = ref([])
const isSearching = ref(false)
const searchFilters = ref({
    schedule_id: null,
    date_from: '',
    date_to: ''
})

// --- FUNÇÃO HELPER PARA VALIDAÇÃO ---
const isValidScheduleId = (id = selectedAgendaId.value) => {
    return id !== null && id !== undefined && !isNaN(id) && id > 0;
};

// --- FUNÇÃO PARA VALIDAR ANTES DE CHAMADAS DA API ---
const validateScheduleIdForApi = (operation = 'operação') => {
    if (!isValidScheduleId()) {
        const errorMsg = `ID da agenda inválido para ${operation}. Valor atual: ${selectedAgendaId.value}`;
        console.error(errorMsg);
        showBanner('error',`Erro: ${errorMsg}`);
        return false;
    }
    return true;
};

// --- FUNÇÕES DE PESQUISA ---
const openSearchModal = () => {
    isSearchModalOpen.value = true;
    searchQuery.value = '';
    searchResults.value = [];
    // Define valores padrão para os filtros
    if (!searchFilters.value.date_from) {
        searchFilters.value.date_from = addDaysToDate(getCurrentDate(), -30);
    }
    if (!searchFilters.value.date_to) {
        searchFilters.value.date_to = addDaysToDate(getCurrentDate(), 30);
    }
};

const closeSearchModal = () => {
    isSearchModalOpen.value = false;
    searchQuery.value = '';
    searchResults.value = [];
};

const getSearchFieldLabel = () => {
    const labels = {
        'accession': 'Número da OS',
        'patient_id': 'ID do Paciente',
        'patient_name': 'Nome do Paciente',
        'patient_cpf': 'CPF do Paciente'
    };
    return labels[searchType.value] || 'Campo de Pesquisa';
};

const getSearchPlaceholder = () => {
    const placeholders = {
        'accession': 'Ex: 1000-10',
        'patient_id': 'Ex: 12345',
        'patient_name': 'Ex: João Silva',
        'patient_cpf': 'Ex: 123.456.789-00'
    };
    return placeholders[searchType.value] || 'Digite o termo de pesquisa';
};

const clearSearchFilters = () => {
    searchQuery.value = '';
    searchResults.value = [];
    searchFilters.value = {
        schedule_id: null,
        date_from: '',
        date_to: ''
    };
};

const performSearch = async () => {
    if (!searchQuery.value.trim()) {
        showBanner('error','Digite um termo para pesquisar.');
        return;
    }

    isSearching.value = true;
    try {
        const params = {
            search_type: searchType.value,
            query: searchQuery.value.trim()
        };
        
        // Adiciona filtros de data se especificados
        if (searchFilters.value.date_from) {
            params.date_from = searchFilters.value.date_from;
        }
        if (searchFilters.value.date_to) {
            params.date_to = searchFilters.value.date_to;
        }
        
        // Adiciona filtro de agenda se especificado
        if (searchFilters.value.schedule_id !== null && searchFilters.value.schedule_id !== '' && searchFilters.value.schedule_id !== undefined) {
            const scheduleId = parseInt(searchFilters.value.schedule_id);
            if (!isNaN(scheduleId) && scheduleId > 0) {
                params.schedule_id = scheduleId;
            }
        }
        
        const response = await apiClient.get('/api/v1/appointments/search/', {
            params: params
        });
        
        searchResults.value = response.data.results || [];
        
        if (searchResults.value.length === 0) {
            showBanner('error','Nenhum resultado encontrado para os filtros aplicados.');
        } else {
           // showBanner('success',`${searchResults.value.length} agendamento(s) encontrado(s)!`);
        }
    } catch (err) {
        console.error('Erro na pesquisa:', err);
        const errorMessage = err.response?.data?.detail || err.response?.data?.message || 'Erro desconhecido';
        showBanner('error', errorMessage);
    } finally {
        isSearching.value = false;
    }
};

const selectSearchResult = (appointment) => {
    // Redireciona para a agenda do agendamento encontrado
    const scheduleId = appointment.schedule_id || appointment.schedule;
    selectedAgenda.value = parseInt(scheduleId);
    selectedDate.value = appointment.date;
    
    // Verifica se a conversão foi bem-sucedida
    if (isNaN(selectedAgenda.value)) {
        showBanner('error','Erro ao identificar a agenda do agendamento.');
        return;
    }
    
    // Fecha o modal de pesquisa
    closeSearchModal();
    
    // Fecha o modal de histórico do paciente se estiver aberto
    closePatientHistoryModal();
    
    // Recarrega os agendamentos da agenda selecionada
    fetchAppointments();
    
    showBanner('success',`Redirecionado para agenda: ${appointment.schedule_name} - ${formatDate(appointment.date)}`);
};  

const selectedAgendaId = computed(() => {
    if (selectedAgenda.value === null || selectedAgenda.value === undefined) {
        console.log('selectedAgendaId: selectedAgenda é null/undefined');
        return null;
    }
    
    const id = typeof selectedAgenda.value === 'string'
        ? parseInt(selectedAgenda.value)
        : selectedAgenda.value;
        
    if (isNaN(id)) {
        console.warn('selectedAgendaId: conversão resultou em NaN para:', selectedAgenda.value);
        return null;
    }
    
    return id;
});

const openScanModal = (appointment) => {
    // Define o agendamento no contextMenu para o modal acessar
    contextMenu.value.data = appointment;
    isScanModalOpen.value = true;
};

const closeScanModal = () => {
    isScanModalOpen.value = false;
};

const handleDocumentSaved = () => {
    showBanner('success','Documento escaneado e salvo com sucesso!');
    // Aqui você pode adicionar lógica adicional se necessário
    // Por exemplo, atualizar a lista de documentos do agendamento
};

const openDocumentsViewer = (appointment) => {
    // Define o agendamento no contextMenu para o modal acessar
    contextMenu.value.data = appointment;
    isDocumentsViewerOpen.value = true;
};

const openShareImagesModal = (appointment) => {
    shareImagesAppointment.value = appointment;
    shareImagesData.value = {
        duration_hours: 24,
        description: ''
    };
    shareResult.value = null;
    isShareImagesModalOpen.value = true;
};

const closeShareImagesModal = () => {
    isShareImagesModalOpen.value = false;
    shareImagesAppointment.value = null;
    shareImagesData.value = {
        duration_hours: 24,
        description: ''
    };
    shareResult.value = null;
    qrCodeDataUrl.value = '';
};

// Função closeDeliveryProtocolModal removida conforme solicitado

// Função printDeliveryProtocol removida conforme solicitado

const handleShareImages = async () => {
    if (!shareImagesAppointment.value || !isValidScheduleId()) {
        showBanner('error', 'Dados inválidos para compartilhamento.');
        return;
    }

    try {
        const response = await apiClient.post(
            `/api/v1/schedules/${selectedAgendaId.value}/appointments/${shareImagesAppointment.value.id}/share-images/`,
            shareImagesData.value
        );

        shareResult.value = response.data;
        
        if (response.data.success) {
            showBanner('success', 'Link de compartilhamento gerado com sucesso!');
            // Gera o QR code automaticamente quando o link for criado
            await generateQRCode(response.data.share_url);
        } else {
            showBanner('error', response.data.message);
        }
    } catch (err) {
        console.error('Erro ao gerar link de compartilhamento:', err);
        const errorMessage = err.response?.data?.detail || err.response?.data?.message || 'Erro desconhecido';
        showBanner('error', 'Erro ao gerar link de compartilhamento: ' + errorMessage);
    }
};

const copyShareUrl = async (url) => {
    try {
        await navigator.clipboard.writeText(url);
        showBanner('success', 'Link copiado para a área de transferência!');
    } catch (err) {
        console.error('Erro ao copiar link:', err);
        showBanner('error', 'Erro ao copiar link para a área de transferência.');
    }
};

const generateQRCode = async (url) => {
    try {
        isGeneratingQR.value = true;
        const qrDataUrl = await QRCode.toDataURL(url, {
            width: 200,
            margin: 2,
            color: {
                dark: '#000000',
                light: '#FFFFFF'
            }
        });
        qrCodeDataUrl.value = qrDataUrl;
    } catch (err) {
        console.error('Erro ao gerar QR code:', err);
        showBanner('error', 'Erro ao gerar QR code.');
    } finally {
        isGeneratingQR.value = false;
    }
};

const printShareDeclaration = () => {
    if (!shareResult.value?.share_url || !qrCodeDataUrl.value) {
        showBanner('error', 'Dados de compartilhamento não disponíveis para impressão.');
        return;
    }

    const currentDate = new Date().toLocaleDateString('pt-BR');
    const currentTime = new Date().toLocaleTimeString('pt-BR');
    const expiresAt = new Date(shareResult.value.expires_at).toLocaleString('pt-BR');
    
    const content = `
        <!DOCTYPE html>
        <html>
        <head>
            <title>Declaração de Compartilhamento de Imagens</title>
            <style>
                @media print {
                    body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
                    .header { text-align: center; margin-bottom: 30px; }
                    .logo { max-width: 200px; height: auto; }
                    .content { margin-bottom: 30px; }
                    .qr-section { text-align: center; margin: 30px 0; }
                    .qr-code { max-width: 200px; height: auto; }
                    .footer { margin-top: 50px; text-align: center; font-size: 12px; }
                    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin: 20px 0; }
                    .info-item { margin-bottom: 15px; }
                    .info-label { font-weight: bold; }
                    .declaration-text { 
                        text-align: justify; 
                        line-height: 1.6; 
                        margin: 30px 0;
                        padding: 20px;
                        border: 1px solid #ccc;
                        border-radius: 8px;
                        background-color: #f9f9f9;
                    }
                }
                body { font-family: Arial, sans-serif; padding: 20px; }
                .header { text-align: center; margin-bottom: 30px; }
                .logo { max-width: 200px; height: auto; }
                .content { margin-bottom: 30px; }
                .qr-section { text-align: center; margin: 30px 0; }
                .qr-code { max-width: 200px; height: auto; }
                .footer { margin-top: 50px; text-align: center; font-size: 12px; }
                .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin: 20px 0; }
                .info-item { margin-bottom: 15px; }
                .info-label { font-weight: bold; }
                .declaration-text { 
                    text-align: justify; 
                    line-height: 1.6; 
                    margin: 30px 0;
                    padding: 20px;
                    border: 1px solid #ccc;
                    border-radius: 8px;
                    background-color: #f9f9f9;
                }
            </style>
        </head>
        <body>
            <div class="header">
                <img src="/inovamed_logo.png" alt="Logo" class="logo" onerror="this.style.display='none'">
                <h1>Declaração de Compartilhamento de Imagens</h1>
                <p><strong>Data:</strong> ${currentDate} às ${currentTime}</p>
            </div>
            
            <div class="content">
                <div class="info-grid">
                    <div class="info-item">
                        <span class="info-label">Paciente:</span> ${shareImagesAppointment.value?.patient_name || 'N/A'}
                    </div>
                    <div class="info-item">
                        <span class="info-label">Exame:</span> ${shareImagesAppointment.value?.service_name || 'N/A'}
                    </div>
                    <div class="info-item">
                        <span class="info-label">Data do Exame:</span> ${formatDate(shareImagesAppointment.value?.date) || 'N/A'} às ${shareImagesAppointment.value?.time?.slice(0,5) || 'N/A'}
                    </div>
                    <div class="info-item">
                        <span class="info-label">Número da OS:</span> ${shareImagesAppointment.value?.accession_number || 'N/A'}
                    </div>
                    <div class="info-item">
                        <span class="info-label">Duração do Link:</span> ${shareImagesData.value.duration_hours} horas
                    </div>
                    <div class="info-item">
                        <span class="info-label">Expira em:</span> ${expiresAt}
                    </div>
                </div>
                
                <div class="declaration-text">
                    <p><strong>DECLARAÇÃO:</strong></p>
                    <p>Declaramos que foi gerado um link de compartilhamento de imagens médicas para o paciente acima identificado, com as seguintes características:</p>
                    <ul>
                        <li>O link permite acesso às imagens do exame médico realizado</li>
                        <li>O acesso é temporário e expira automaticamente na data/hora especificada</li>
                        <li>O compartilhamento foi realizado por profissional autorizado</li>
                        <li>O link deve ser utilizado apenas pelo paciente ou responsável legal</li>
                        <li>É proibida a reprodução ou distribuição das imagens sem autorização médica</li>
                    </ul>
                    <p><strong>Observações:</strong> ${shareImagesData.value.description || 'Nenhuma observação adicional.'}</p>
                </div>
                
                <div class="qr-section">
                    <h3>Acesse as imagens escaneando o QR Code:</h3>
                    <img src="${qrCodeDataUrl.value}" alt="QR Code para acesso às imagens" class="qr-code">
                    <p><strong>Link direto:</strong> ${shareResult.value.share_url}</p>
                </div>
            </div>
            
            <div class="footer">
                <p>Este documento foi gerado automaticamente pelo sistema InovaMed</p>
                <p>Para dúvidas, entre em contato com a unidade médica</p>
            </div>
        </body>
        </html>
    `;
    
    const filename = `declaracao-compartilhamento-${shareImagesAppointment.value?.patient_name?.replace(/\s+/g, '-').toLowerCase()}-${currentDate.replace(/\//g, '-')}.pdf`;
    printDocument(content, filename);
    showBanner('success', isMobileDevice() ? 'Declaração de compartilhamento baixada com sucesso!' : 'Declaração de compartilhamento enviada para impressão!');
};

const closeDocumentsViewer = () => {
    isDocumentsViewerOpen.value = false;
};

const handleDocumentDeleted = () => {
    showBanner('success','Documento excluído com sucesso!');
    // Aqui você pode adicionar lógica adicional se necessário
};

const isPastDay = computed(() => {
    if (!selectedDate.value) return false;
    const [year, month, day] = selectedDate.value.split('-').map(Number);
    const today = new Date();
    const todayYear = today.getFullYear();
    const todayMonth = today.getMonth() + 1; // getMonth() é zero-based
    const todayDay = today.getDate();

    if (year < todayYear) return true;
    if (year > todayYear) return false;
    if (month < todayMonth) return true;
    if (month > todayMonth) return false;
    return day < todayDay; // Corrigido: agora o dia atual não é considerado "passado"
});

const openEditModal = async (appointment) => {
    editAppointment.value = appointment;
    
    // Garante que os dados necessários sejam carregados
    const promises = [];
    if (services.value.length === 0) promises.push(fetchServices());
    if (doctors.value.length === 0) promises.push(fetchDoctors());
    if (insurances.value.length === 0) promises.push(fetchInsurances());
    
    if (promises.length > 0) {
        await Promise.all(promises);
    }
    
    // Dados carregados para edição
    
    // Preenche os dados do formulário de edição
    editData.value = {
        patient_name: appointment.patient_name || '',
        patient_phone: appointment.phone || '',
        patient_email: appointment.patient_email || '',
        race: appointment.patient_race || '',
        patient_health_insurance: appointment.patient_health_insurance || '', // Plano de saúde do paciente
        patient_neighborhood: appointment.address_district || '', // Bairro do paciente
        service_id: appointment.service || null,
        time: normalizarHorario(appointment.time) || '',
        notes: appointment.notes || '',
        procedencia: appointment.procedencia || '',
        motivo_marcacao: appointment.motivo_marcacao || '',
        cid10: appointment.cid10 || '',
        requesting_doctor_id: appointment.requesting_doctor || null,
        requesting_doctor_name: appointment.requesting_doctor_name || '', // Usa o nome do médico solicitante
        internal_doctor_id: appointment.internal_doctor || null,
        insurance_id: appointment.insurance || null,
        sisreg_confirmation_key: appointment.sisreg_confirmation_key || ''
    };
    
    // Lógica para determinar se é médico cadastrado ou importado do SISREG
    if (appointment.requesting_doctor && appointment.requesting_doctor.id) {
        editData.value.requesting_doctor_id = appointment.requesting_doctor.id;
        editData.value.requesting_doctor_name = '';
    } else if (appointment.requesting_doctor_name && appointment.requesting_doctor_name !== 'Médico não informado') {
        editData.value.requesting_doctor_id = null;
        editData.value.requesting_doctor_name = appointment.requesting_doctor_name;
    } else {
        editData.value.requesting_doctor_id = null;
        editData.value.requesting_doctor_name = '';
    }
    
    // Busca os horários disponíveis para a data do agendamento
    await fetchAvailableTimes(appointment.date);
    
    // Se o horário do agendamento não estiver na lista de disponíveis, adiciona
    if (editData.value.time && !availableTimes.value.includes(editData.value.time)) {
        availableTimes.value.push(editData.value.time);
        // Ordena a lista de horários
        availableTimes.value.sort();
    }
    
    isEditModalOpen.value = true;
};

const closeEditModal = () => {
    isEditModalOpen.value = false;
    editAppointment.value = null;
    editData.value = {
        patient_name: '',
        patient_phone: '',
        patient_email: '',
        patient_health_insurance: '',
        patient_neighborhood: '',
        service_id: null,
        time: '',
        notes: '',
        procedencia: '',
        motivo_marcacao: '',
        cid10: '',
        requesting_doctor_id: null,
        requesting_doctor_name: '',
        internal_doctor_id: null,
        insurance_id: null,
        race: ''
    };
};

const toggleDoctorMode = () => {
    if (!editData.value.requesting_doctor_id && editData.value.requesting_doctor_name && editData.value.requesting_doctor_name !== 'Médico não informado') {
        // Está no modo texto (SISREG), muda para select (médicos cadastrados)
        editData.value.requesting_doctor_name = '';
        editData.value.requesting_doctor_id = null;
    } else {
        // Está no modo select, muda para texto
        const selectedDoctor = doctors.value.find(d => d.id === editData.value.requesting_doctor_id);
        if (selectedDoctor) {
            editData.value.requesting_doctor_name = selectedDoctor.name;
            editData.value.requesting_doctor_id = null;
        } else {
            // Se não encontrou o médico, limpa ambos os campos
            editData.value.requesting_doctor_name = '';
            editData.value.requesting_doctor_id = null;
        }
    }
};

const handleSaveEdit = async () => {
    if (!editAppointment.value || !isValidScheduleId()) {
        showBanner('error', 'Dados inválidos para edição.');
        return;
    }

    try {
        // Prepara os dados para enviar ao backend
        const payload = {
            service_id: editData.value.service_id,
            patient_id: editAppointment.value.patient, // Usa o ID do paciente original
            insurance_id: editData.value.insurance_id,
            requesting_doctor_id: editData.value.requesting_doctor_id,
            requesting_doctor_name: editData.value.requesting_doctor_name, // Inclui o nome do médico solicitante
            internal_doctor_id: editData.value.internal_doctor_id,
            date: editAppointment.value.date,
            time: editData.value.time,
            procedencia: editData.value.procedencia,
            motivo_marcacao: editData.value.motivo_marcacao,
            cid10: editData.value.cid10,
            is_inpatient: editAppointment.value.is_inpatient || false,
            is_urgent: editAppointment.value.is_urgent || false,
            send_preparation_email: editAppointment.value.send_preparation_email || false,
            send_preparation_sms: editData.value.send_preparation_sms || false,
            notes: editData.value.notes,
            status: editAppointment.value.status,
            sisreg_confirmation_key: editData.value.sisreg_confirmation_key,
            // Dados do paciente para atualização
            patient_name: editData.value.patient_name,
            patient_phone: editData.value.patient_phone,
            patient_email: editData.value.patient_email,
            patient_health_insurance: editData.value.patient_health_insurance,
            patient_neighborhood: editData.value.patient_neighborhood,
            race: editData.value.race
        };

        // Chama a API para atualizar o agendamento
        const response = await apiClient.put(
            `/api/v1/schedules/${selectedAgendaId.value}/appointments/${editAppointment.value.id}/`,
            payload
        );

        // Atualiza o agendamento na lista local
        const index = appointments.value.findIndex(a => a.id === editAppointment.value.id);
        if (index !== -1) {
            appointments.value[index] = response.data;
        }

        // Fecha o modal
        closeEditModal();
        
        // Mostra mensagem de sucesso
        const patientUpdated = editData.value.patient_name || editData.value.patient_phone || editData.value.patient_email || editData.value.patient_health_insurance || editData.value.race;
        const message = patientUpdated 
            ? 'Agendamento e dados do paciente atualizados com sucesso!' 
            : 'Agendamento atualizado com sucesso!';
        showBanner('success', message);
        
        // Recarrega os agendamentos para garantir sincronização
        await fetchAppointments();
        
    } catch (err) {
        console.error('Erro ao atualizar agendamento:', err);
        const errorMessage = err.response?.data?.detail || err.response?.data?.message || 'Erro desconhecido';
        showBanner('error', errorMessage);
    }
};

// --- FUNÇÕES DE GERENCIAMENTO DE INATIVIDADE ---
const resetInactivityTimer = () => {
    // Limpa o timer de inatividade existente
    if (inactivityTimer.value) {
        clearTimeout(inactivityTimer.value);
    }
    
    // Se o usuário estava inativo, volta ao estado ativo
    if (isUserInactive.value) {
        isUserInactive.value = false;
        showInactivityBanner.value = false;
        stopAutoRefresh();
    }
    
    // Não inicia o timer de inatividade se o modal de importação estiver aberto
    if (isImportModalOpen.value) {
        return;
    }
    
    // Inicia um novo timer de inatividade
    inactivityTimer.value = setTimeout(() => {
        isUserInactive.value = true;
        startInactivityCountdown();
    }, INACTIVITY_TIMEOUT);
};

const startInactivityCountdown = () => {
    showInactivityBanner.value = true;
    autoRefreshCountdown.value = COUNTDOWN_DURATION;
    
    countdownTimer.value = setInterval(() => {
        autoRefreshCountdown.value--;
        
        if (autoRefreshCountdown.value <= 0) {
            clearInterval(countdownTimer.value);
            showInactivityBanner.value = false;
            startAutoRefresh();
        }
    }, 1000);
};

const startAutoRefresh = () => {
    // Não inicia o auto-refresh se o modal de importação estiver aberto
    if (isImportModalOpen.value) {
        console.log('startAutoRefresh: Modal de importação aberto, auto-refresh cancelado');
        return;
    }
    
    isAutoRefreshing.value = true;
    
    // Primeira atualização imediata
    fetchAppointments();
    
    // Inicia a contagem regressiva da próxima atualização
    startNextRefreshCountdown();
    
    // Configura o intervalo de atualização
    autoRefreshInterval.value = setInterval(() => {
        if (isUserInactive.value && !isImportModalOpen.value) {
            fetchAppointments();
            // Reinicia a contagem regressiva após cada atualização
            startNextRefreshCountdown();
        }
    }, AUTO_REFRESH_INTERVAL);
};

const startNextRefreshCountdown = () => {
    nextRefreshCountdown.value = 15; // 15 segundos
    
    if (nextRefreshTimer.value) {
        clearInterval(nextRefreshTimer.value);
    }
    
    nextRefreshTimer.value = setInterval(() => {
        nextRefreshCountdown.value--;
        
        if (nextRefreshCountdown.value <= 0) {
            clearInterval(nextRefreshTimer.value);
            nextRefreshCountdown.value = 15;
        }
    }, 1000);
};

const stopAutoRefresh = () => {
    isAutoRefreshing.value = false;
    
    if (autoRefreshInterval.value) {
        clearInterval(autoRefreshInterval.value);
        autoRefreshInterval.value = null;
    }
    
    if (countdownTimer.value) {
        clearInterval(countdownTimer.value);
        countdownTimer.value = null;
    }
    
    if (nextRefreshTimer.value) {
        clearInterval(nextRefreshTimer.value);
        nextRefreshTimer.value = null;
    }
    
    nextRefreshCountdown.value = 15;
};

const handleUserActivity = () => {
    // Se o modal de importação estiver aberto, apenas para a contagem regressiva mas não inicia novo timer
    if (isImportModalOpen.value) {
        if (isUserInactive.value) {
            isUserInactive.value = false;
            showInactivityBanner.value = false;
            stopAutoRefresh();
        }
        return;
    }
    
    resetInactivityTimer();
};

// --- EVENT LISTENERS PARA DETECTAR ATIVIDADE DO USUÁRIO ---
const setupActivityListeners = () => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
        document.addEventListener(event, handleUserActivity, { passive: true });
    });
};

const cleanupActivityListeners = () => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
        document.removeEventListener(event, handleUserActivity);
    });
    
    // Limpa todos os timers
    if (inactivityTimer.value) {
        clearTimeout(inactivityTimer.value);
    }
    if (countdownTimer.value) {
        clearInterval(countdownTimer.value);
    }
    if (autoRefreshInterval.value) {
        clearInterval(autoRefreshInterval.value);
    }
    if (nextRefreshTimer.value) {
        clearInterval(nextRefreshTimer.value);
    }
};

</script>

<style>
@import 'tailwindcss/base';
@import 'tailwindcss/components';
@import 'tailwindcss/utilities';

.clinic-btn {
  background-color: var(--clinic-btn-bg) !important;
  color: #fff !important;
  transition: background 0.2s;
}
.clinic-btn:hover {
  background-color: var(--clinic-btn-bg-hover) !important;
}

/* Animações personalizadas para o carregamento */
@keyframes fade-in {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.animate-fade-in {
  animation: fade-in 0.3s ease-out;
}

/* Animação de pulso suave para o container de carregamento */
@keyframes gentle-pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}

.animate-pulse {
  animation: gentle-pulse 2s ease-in-out infinite;
}

/* Gradiente animado para a barra de progresso */
@keyframes gradient-shift {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.bg-gradient-to-r {
  background-size: 200% 200%;
  animation: gradient-shift 3s ease infinite;
}

@keyframes blink-border {
    0%, 50% {
        border-color: #ef4444; /* red-500 */
        box-shadow: 0 0 0 1px #ef4444;
    }
    51%, 100% {
        border-color: #dc2626; /* red-600 */
        box-shadow: 0 0 0 1px #dc2626;
    }
}

.blink-border {
    animation: blink-border 0.5s ease-in-out infinite;
    border-color: #ef4444 !important;
}

/* Dark mode support */
.dark .blink-border {
    border-color: #f87171 !important; /* red-400 for dark mode */
}

.dark .blink-border {
    animation: blink-border-dark 0.5s ease-in-out infinite;
}

@keyframes blink-border-dark {
    0%, 50% {
        border-color: #f87171;
        box-shadow: 0 0 0 1px #f87171;
    }
    51%, 100% {
        border-color: #ef4444;
        box-shadow: 0 0 0 1px #ef4444;
    }
}
</style>